import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  LinearProgress,
  Collapse,
  Table,
TableHead,
TableCell,
TableRow,
TableBody,
TableContainer,
TableFooter,
Tooltip,
Menu,
MenuItem,
ListItemIcon,
ListItemText,
Dialog,
DialogTitle,
DialogContent,
DialogContentText,
DialogActions,
Autocomplete,
Checkbox,
Drawer,
Avatar,
List,
ListItem,
ListItemAvatar,
Badge,
} from '@mui/material';

import {
  Save as SaveIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
  Preview as PreviewIcon,
  ViewKanban as ViewKanbanIcon,
  AddReactionOutlined as AddReactionIcon,
  FactCheck as ReviewsReceivedIcon,
  People as AuthorsIcon,
  FormatQuote as ReferencesIcon,
  Event as DeadlineIcon,
  Folder as FolderIcon,
  FolderSpecial as FolderSpecialIcon,
  Task as TaskIcon,
  Add as AddIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Groups as GroupsIcon,
  Campaign as CampaignIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  CheckCircle as CheckCircleIcon,
  Replay as ReplayIcon,
  RateReview as RateReviewIcon,
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
  AutoAwesome as AutoAwesomeIcon,
  Upload as UploadIcon,
  DriveFolderUpload as ImportIcon,
  ListAlt as TemplateIcon,
  AccountTree as AccountTreeIcon,
  Reorder as ReorderIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Fullscreen as FullscreenIcon,
  ArrowBack as ArrowBackIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  BookmarkAdd as BookmarkAddIcon,
  EditNote as EditNoteIcon,
} from '@mui/icons-material';

import {
  fetchProposal,
  saveSectionContent,
  lockSection,
  unlockSection,
  updateSectionStatus,
  updateSection,
  saveProposalVersion,
  fetchProposalVersions,
  switchProposalVersion,
  fetchProposalStatistics,
  deleteSection,
  deleteWorkPackage,
  deleteTask,
  fetchAIStatus,
  createSection,
  fetchMemberResources,
  updateProposal,
  restoreContentVersion,
} from 'shared/utils/proposalApi';
import SaveConflictDialog from './SaveConflictDialog';
import { fetchUserById } from 'shared/utils/userApi';
import { useUser } from 'shared/helpers/axios';
import { axiosService } from 'shared/helpers/axios';
import { useUserStatus, getStatusMeta } from 'shared/contexts/UserStatusContext';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import ProjectManagementPanel from 'components/collab/consortium/projectmgmt/ProjectManagementPanel';
import AuthorsPanel from 'components/collab/consortium/publication/AuthorsPanel';
import ReferencesPanel from 'components/collab/consortium/publication/ReferencesPanel';
import { publicationTypeLabel } from 'components/collab/consortium/publication/publicationConstants';
import {
  applyCitations, applyInTextMap, buildInTextMap, citationMarker, citedReferencesMarkdown, formatAll, referencesForMarker,
  DEFAULT_CITATION_STYLE, getStyle, referencesFromDois,
} from 'components/collab/consortium/publication/citations';
import ImportPublicationDialog from 'components/collab/consortium/publication/ImportPublicationDialog';
import FormatReferencesDialog from 'components/collab/consortium/publication/FormatReferencesDialog';

import { useParams, useNavigate, useLocation,
        Link as RouterLink  } from 'react-router-dom';
import SectionDialog from './SectionDialog';
import WorkPackageDialog from './WorkPackageDialog';
import ProposalTemplateDialog from './ProposalTemplateDialog';
import AIActivationDialog from './AIActivationDialog';
import GraphicalAbstractPanel from '../publication/GraphicalAbstractPanel';
import AISectionWriter from './AISectionWriter';
import TaskDialog from './TaskDialog';
import BudgetDialog from './BudgetDialog';
import PersonMonthDialog from './PersonMonthDialog';
import ContentVersionsDialog from './ContentVersionsDialog';
import EditProposalDialog from './EditProposalDialog';
import ProposalSettingsDialog from './ProposalSettingsDialog';
import ReviewsDialog from './ReviewsDialog';
import ProposalTodoPanel from './ProposalTodoPanel';
import WpPreviewDialog from './WpPreviewDialog';
import WpNotesDialog from './WpNotesDialog';
import DisseminationTab from './DisseminationTab';
import EventSuggestionCard from './EventSuggestionCard';
import BackButton from 'components/common/buttons/BackButton';
import SlateEditor from 'components/social/profile/reflections/SlateEditor';
import { renderMarkdownToReact, markdownToDocxParagraphs, markdownToPlainText } from 'shared/utils/richTextMarkdown';
import LineNumberGutter from 'components/collab/consortium/publication/LineNumberGutter';
import { exportManuscript, exportRis } from 'components/collab/consortium/publication/manuscriptExport';

import { Document, Packer, Paragraph, TextRun,
        Table as DocxTable,
        TableRow as DocxTableRow,
        TableCell as DocxTableCell,
        VerticalAlign,
        ShadingType,
        WidthType, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { useTheme, alpha } from '@mui/material/styles';
import PresenceAvatars, { usePresence } from '../publication/PresenceAvatars';

// Team chat: initials on the brand's avatar colour (as the members above it), not MUI's default grey
const CHAT_AVATAR_SX = { bgcolor: 'primary.avatar', color: '#fff', fontWeight: 600 };
// The reactions offered in the team chat (the server accepts these only: ProposalMessage.REACTIONS)
const CHAT_REACTIONS = ['👍', '❤️', '😂', '🎉', '😮', '🙏', '✅', '👀'];

// Still being written: no reviews yet (the backend refuses them too: proposal/proposal/viewsets.py ReviewRoundViewSet)
const UNSUBMITTED_STATUSES = ['preparing', 'drafting', 'internal_review'];

// Tables in the Implementation section are numbered relative to the "Work
// Plan" subheading — e.g. if Work Plan is section 2.1, tables become 2.1a,
// 2.1b, ... — instead of a hardcoded "3.1" prefix, so numbering stays correct
// after headings are restructured. Letters are assigned only to tables that
// actually render, so a hidden table never leaves a gap in the sequence.
// Shared by the Preview tab and the Word (.docx) export so both stay in sync.
function computeWorkPlanTableData(selectedProposal) {
  const findWorkPlanSection = (sections) => {
    for (const s of (sections || [])) {
      if (/work\s*plan/i.test(s.title || '')) return s;
      const found = findWorkPlanSection(s.subsections);
      if (found) return found;
    }
    return null;
  };
  const workPlanSection = findWorkPlanSection(selectedProposal.sections);
  const tablePrefix = workPlanSection?.section_key || '3.1';

  const hasWPs = selectedProposal.work_packages && selectedProposal.work_packages.length > 0;
  const hasMembers = selectedProposal.consortium_members && selectedProposal.consortium_members.length > 0;
  const allDeliverables = hasWPs
    ? selectedProposal.work_packages.flatMap(wp =>
        (wp.deliverables || []).map((deliv, i) => ({
          ...deliv, wp_number: wp.number,
          displayCode: `D${wp.number}.${i + 1}`,
        })))
    : [];
  const allMilestones = hasWPs
    ? selectedProposal.work_packages.flatMap(wp =>
        (wp.milestones || []).map((ms, i) => ({
          ...ms, wp_number: wp.number,
          displayCode: `MS${wp.number}.${i + 1}`,
        })))
    : [];
  const allRisks = hasWPs
    ? selectedProposal.work_packages.flatMap(wp =>
        (wp.risks || []).map((risk, i) => ({
          ...risk, wp_number: wp.number,
          displayCode: `R${wp.number}.${i + 1}`,
        })))
    : [];
  const hasEquipmentCosts = selectedProposal.tasks && selectedProposal.tasks.some(
    t => t.budget_items?.some(bi => parseFloat(bi.equipment || 0) > 0)
  );

  const TABLE_ORDER = [
    { key: 'wpList', label: 'List of Work Packages', visible: hasWPs },
    { key: 'wpDesc', label: 'Work Package Descriptions', visible: hasWPs },
    { key: 'deliverables', label: 'List of Deliverables', visible: allDeliverables.length > 0 },
    { key: 'milestones', label: 'List of Milestones', visible: allMilestones.length > 0 },
    { key: 'risks', label: 'Critical Risks for Implementation', visible: allRisks.length > 0 },
    { key: 'staffEffort', label: 'Summary of Staff Effort', visible: hasWPs && hasMembers },
    { key: 'equipment', label: 'Purchase Costs (Equipment)', visible: hasEquipmentCosts },
  ];
  const tableLabels = {};
  let letterIndex = 0;
  TABLE_ORDER.forEach(({ key, label, visible }) => {
    if (!visible) return;
    tableLabels[key] = `Table ${tablePrefix}${String.fromCharCode(97 + letterIndex)}: ${label}`;
    letterIndex += 1;
  });

  return { tablePrefix, tableLabels, hasWPs, hasMembers, allDeliverables, allMilestones, allRisks, hasEquipmentCosts };
}

// ── Warning computation ────────────────────────────────────────────────────────
const WARN_SEVERITY = {
  error:   { label: 'Missing',     color: 'error',   docxColor: 'C00000' },
  warning: { label: 'Incomplete',  color: 'warning',  docxColor: 'C55A00' },
  info:    { label: 'Suggested',   color: 'info',     docxColor: '1F4E79' },
};

function computeWarnings(proposal) {
  const items = [];
  const add = (category, severity, message) => items.push({ category, severity, message });

  // Work packages
  (proposal.work_packages || []).forEach(wp => {
    const wpId = `WP${wp.number}`;
    if (!wp.description) add('Work Packages', 'warning', `${wpId} "${wp.title}": description is missing`);
    if (!wp.tasks || wp.tasks.length === 0) add('Work Packages', 'error', `${wpId} "${wp.title}": has no tasks`);
    if (!wp.objectives || wp.objectives.length === 0) add('Work Packages', 'warning', `${wpId} "${wp.title}": no objectives defined`);
    if (!wp.start_month) add('Work Packages', 'info', `${wpId} "${wp.title}": start month not set`);
    if (!wp.end_month)   add('Work Packages', 'info', `${wpId} "${wp.title}": end month not set`);

    // Tasks
    (wp.tasks || []).forEach(task => {
      const tid = `Task ${wp.number}.${task.number}`;
      const tq = `"${task.title || 'Untitled'}"`;
      if (!task.description)    add('Tasks', 'error',   `${tid} ${tq}: description is missing`);
      if (!task.start_month)    add('Tasks', 'warning',  `${tid} ${tq}: start month not set`);
      if (!task.duration_months)add('Tasks', 'warning',  `${tid} ${tq}: duration not set`);
    });

    // Deliverables (per WP)
    (wp.deliverables || []).forEach((d, i) => {
      const did = `D${wp.number}.${i + 1}`;
      const dq = `"${d.title || 'Untitled'}"`;
      if (!d.title)           add('Deliverables', 'error',   `${did}: title is missing`);
      if (!d.delivery_month)  add('Deliverables', 'warning',  `${did} ${dq}: delivery month not set`);
      if (!d.dissemination_level) add('Deliverables', 'info', `${did} ${dq}: dissemination level not set`);
    });

    // Milestones (per WP)
    (wp.milestones || []).forEach((ms, i) => {
      const mid = `MS${wp.number}.${i + 1}`;
      const mq = `"${ms.description || 'Untitled'}"`;
      if (!ms.description)         add('Milestones', 'error',   `${mid}: description is missing`);
      if (!ms.due_month)           add('Milestones', 'warning',  `${mid} ${mq}: due month not set`);
      if (!ms.verification_means)  add('Milestones', 'info',     `${mid} ${mq}: verification means not specified`);
    });

    // Risks (per WP)
    (wp.risks || []).forEach((risk, i) => {
      const rid = `R${wp.number}.${i + 1}`;
      if (!risk.description)    add('Risks', 'error',   `${rid}: description is missing`);
      if (!risk.mitigation_plan)add('Risks', 'warning',  `${rid}: mitigation plan is missing`);
      if (!risk.likelihood || !risk.impact) add('Risks', 'warning', `${rid}: likelihood / impact not set`);
    });
  });

  // Sections
  const checkSection = (s) => {
    if (!s.current_content?.body)
      add('Sections', 'warning', `Section ${s.section_key} "${s.title}": no content written`);
    (s.subsections || []).forEach(checkSection);
  };
  (proposal.sections || [])
    .filter(s => s.section_key !== 'abstract' && s.section_key !== 'appendix')
    .forEach(checkSection);

  // Group
  const grouped = {};
  items.forEach(item => {
    if (!grouped[item.category]) grouped[item.category] = [];
    grouped[item.category].push(item);
  });
  return { items, grouped };
}

// Compact word-limit box: a gap between the number and the arrows so they never overlap
const WORD_LIMIT_SX = {
  '& .MuiInputBase-root': { height: 24, fontSize: '0.8rem' },
  '& input': { py: '2px', pl: '6px', pr: '4px' },
  '& input::-webkit-inner-spin-button': { ml: '8px' },   // Chrome, Edge, Safari
};

// Writer tab value -> key used by the tab-changes API (Preview has no badge)
const TAB_KEYS = { 7: 'authors', 0: 'abstract', 1: 'sections', 2: 'work_packages', 3: 'budget', 4: 'dissemination', 8: 'bibliography', 5: 'preview' };
const TAB_VALUES = Object.fromEntries(Object.entries(TAB_KEYS).map(([v, k]) => [k, Number(v)]));
const AUTOSAVE_MS = 60000;
// Text typed since the last autosave survives a page refresh (this tab only)
const draftKey = (kind, id) => `otable-draft:${kind}:${id}`;
const readDraft = (key) => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } };
const writeDraft = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full or blocked */ } };
const clearDraft = (key) => { try { sessionStorage.removeItem(key); } catch { /* blocked */ } };

const ProposalWriter = () => {

  const theme = useTheme();
  const navigate = useNavigate();
  const { user: currentUser }  = useUser();
  const { status: myStatus } = useUserStatus();
  const { showSnackbar } = useSnackbar();
  // const [proposals, setProposals] = useState([]);
  const { consortiumId, proposalId } = useParams();
  const [consortium, setConsortium] = useState(null);
  const [selectedProposal, setSelectedProposal] = useState(null);
  // ?tab=sections&section=<id>&full=1 keeps the writer where it was across a page refresh
  const initialUrlState = useRef(new URLSearchParams(window.location.search)).current;
  const [activeTab, setActiveTab] = useState(() => TAB_VALUES[initialUrlState.get('tab')] ?? 0);
  // Boards (project management) replace the tab area while open
  const [boardsOpen, setBoardsOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);     // reviews received and our responses
  const previewRef = useRef(null);
  const [downloadMenu, setDownloadMenu] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editBaseVersion, setEditBaseVersion] = useState(null);   // version the open edit started from
  const [saveConflict, setSaveConflict] = useState(null);         // simultaneous save that could not be merged
  const [sectionComments, setSectionComments] = useState([]);
  const [abstractComments, setAbstractComments] = useState([]);
  const [sectionEditorFullScreen, setSectionEditorFullScreen] = useState(false);
  const [previewMenu, setPreviewMenu] = useState(null);   // Preview: right-click a part to edit it ({ x, y, section | part })
  // Full screen: fold away the word-count / word-limit / AI strip above the editor (remembered per browser)
  const [fsToolsHidden, setFsToolsHidden] = useState(() => {
    try { return localStorage.getItem('writer.fsToolsHidden') === '1'; } catch { return false; }
  });
  const toggleFsTools = () => setFsToolsHidden((v) => {
    try { localStorage.setItem('writer.fsToolsHidden', v ? '0' : '1'); } catch { /* storage unavailable */ }
    return !v;
  });
  // Bumped whenever content is replaced from outside the editor itself (e.g.
  // AI-generated text) so the keyed SlateEditor below remounts and picks up
  // the new value instead of continuing to show its own in-progress state.
  const [sectionEditorResetKey, setSectionEditorResetKey] = useState(0);
  const [abstractEditorResetKey, setAbstractEditorResetKey] = useState(0);
  const [content, setContent] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [consortiumUsers, setConsortiumUsers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [error, setError] = useState(null);

  // Dialog states
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingDialogSection, setEditingDialogSection] = useState(null);
  const [workPackageDialogOpen, setWorkPackageDialogOpen] = useState(false);
  const [editingWorkPackage, setEditingWorkPackage] = useState(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedWorkPackageId, setSelectedWorkPackageId] = useState(null);
  const [wpPreviewOpen, setWpPreviewOpen] = useState(false);
  const [wpPreviewTarget, setWpPreviewTarget] = useState(null);
  const [wpNotesOpen, setWpNotesOpen] = useState(false);
  const [wpNotesTarget, setWpNotesTarget] = useState(null);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [editingPersonMonth, setEditingPersonMonth] = useState(null);
  const [budgetTaskId, setBudgetTaskId] = useState(null);
  const [budgetTask, setBudgetTask] = useState(null);
  const [personMonthDialogOpen, setPersonMonthDialogOpen] = useState(false);
  const [personMonthTask, setPersonMonthTask] = useState(null);
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [versionsSectionId, setVersionsSectionId] = useState(null);
  const [versionsSectionTitle, setVersionsSectionTitle] = useState('');
  const [editProposalDialogOpen, setEditProposalDialogOpen] = useState(false);
  const [proposalSettingsOpen, setProposalSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState(null);   // the tab the settings dialog opens on (null: Details)
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  const [importDocxOpen, setImportDocxOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // A team-chat notification links here with ?panel=chat: open the chat straight away
  const routeLocation = useLocation();
  useEffect(() => {
    if (new URLSearchParams(routeLocation.search).get('panel') === 'chat') setChatOpen(true);
  }, [routeLocation.search]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatSending, setChatSending] = useState(false);
  const [hasChatInput, setHasChatInput] = useState(false);
  const chatInputRef = useRef(null);
  const chatBottomRef = useRef(null);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingMsgBody, setEditingMsgBody] = useState('');
  const [abstractText, setAbstractText] = useState('');
  const [keywords, setKeywords] = useState('');
  const [advisoryBoard, setAdvisoryBoard] = useState([]);
  const [abDialogOpen, setAbDialogOpen] = useState(false);
  const [memberResources, setMemberResources] = useState(null);
  const [loadingMemberResources, setLoadingMemberResources] = useState(false);
  const [memberResourcesSelected, setMemberResourcesSelected] = useState({
    publications: new Set(), patents: new Set(), projects: new Set(), networks: new Set(),
  });
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateDesc, setSaveTemplateDesc] = useState('');
  const [saveTemplateSaving, setSaveTemplateSaving] = useState(false);
  const [restructureDialogOpen, setRestructureDialogOpen] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(() => localStorage.getItem('otable_proposal_ai_enabled') === 'true');
  const [aiActivationDialogOpen, setAiActivationDialogOpen] = useState(false);
  const [abstractSection, setAbstractSection] = useState(null);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiReviewResult, setAiReviewResult] = useState(null);
  const [aiReviewError, setAiReviewError] = useState(null);
  const [aiReviewDialogOpen, setAiReviewDialogOpen] = useState(false);
  const [aiReviewHistory, setAiReviewHistory] = useState([]);
  const [aiReviewHistoryMode, setAiReviewHistoryMode] = useState(false);
  const [selectedHistoryReview, setSelectedHistoryReview] = useState(null);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [checkedWarnings, setCheckedWarnings] = useState(new Set());
  const [wordLimitInput, setWordLimitInput] = useState('');
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [versionInput, setVersionInput] = useState('');
  const [versionSaving, setVersionSaving] = useState(false);
  const [versionsList, setVersionsList] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [switchingVersion, setSwitchingVersion] = useState(null);
  const [abstractWordLimitInput, setAbstractWordLimitInput] = useState('');

// You can add a loading state
const [downloading] = useState(false);



useEffect(() => {
    // Load consortium details first
    const loadConsortiumDetails = async () => {
      if (!consortiumId) return;
      
      try {
        const response = await axiosService.get(`/consortium/consortiums/${consortiumId}/`);
        setConsortium(response.data);
      } catch (error) {
        console.error('Error loading consortium details:', error);
        setError('Failed to load consortium details. Please try again later.');
      }
    };
    loadConsortiumDetails();
  }, [consortiumId]);

  useEffect(() => {
    localStorage.setItem('otable_proposal_ai_enabled', aiEnabled ? 'true' : 'false');
  }, [aiEnabled]);

  useEffect(() => {
    if (proposalId) {
      loadProposalDetails(proposalId);
      loadStatistics(proposalId);
      fetchAIStatus(proposalId).then(data => { if (data?.enabled) setAiEnabled(true); });
      loadAIReviewHistory(proposalId);
      setAbstractSection(null); // reset when proposal changes
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  // Find or create the abstract ProposalSection (used by AISectionWriter and version history)
  useEffect(() => {
    if (activeTab !== 0 || !selectedProposal?.id || abstractSection) return;
    const existing = selectedProposal.sections?.find(s => s.section_key === 'abstract');
    if (existing) {
      setAbstractSection(existing);
      return;
    }
    createSection({
      proposal: selectedProposal.id,
      section_key: 'abstract',
      title: 'Abstract',
      position: 0,
      word_limit: 250,
    }).then(result => {
      if (result.success) setAbstractSection(result.data);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProposal?.id]);


  useEffect(() => {
    if (consortium?.memberships) {
      setConsortiumUsers(
        consortium.memberships
          .filter(m => m.member)
          .map(m => ({ ...m.member, org_short_name: m.organization?.short_name || m.organization?.name || '' }))
      );
    }
  }, [consortium]);

  // Keep consortiumUsers in sync with fresh data from the proposal (overrides stale consortium cache)
  useEffect(() => {
    if (selectedProposal?.consortium_users?.length > 0) {
      setConsortiumUsers(
        selectedProposal.consortium_users.map(u => ({
          ...u,
          org_short_name: u.org_short_name || u.org_name || '',
          organization: u.org_id ? { id: u.org_id, name: u.org_name, short_name: u.org_short_name } : null,
        }))
      );
    }
  }, [selectedProposal?.consortium_users]);

  // Fetch member resources when Dissemination (4) or Preview (5) tab becomes active
  useEffect(() => {
    if ((activeTab !== 4 && activeTab !== 5) || !selectedProposal?.id || memberResources) return;
    setLoadingMemberResources(true);
    fetchMemberResources(selectedProposal.id)
      .then(data => setMemberResources(data))
      .finally(() => setLoadingMemberResources(false));
  }, [activeTab, selectedProposal?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first 5 per user per type when memberResources loads (runs regardless of active tab)
  useEffect(() => {
    if (!memberResources) return;
    const autoSelected = {};
    for (const key of ['publications', 'patents', 'projects', 'networks']) {
      const perUser = {};
      const ids = new Set();
      for (const item of (memberResources[key] || [])) {
        const uid = item._member_user_id || '__unknown__';
        if (!perUser[uid]) perUser[uid] = 0;
        if (perUser[uid] < 5) { ids.add(String(item.id)); perUser[uid]++; }
      }
      autoSelected[key] = ids;
    }
    setMemberResourcesSelected(autoSelected);
  }, [memberResources]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update abstractText whenever selectedProposal.abstract changes. Also bump
  // the reset key so the keyed SlateEditor below remounts and actually picks
  // up the fetched value — it mounts once selectedProposal.id is set, which
  // can happen a render before this effect updates abstractText, and a plain
  // prop change alone wouldn't make an already-mounted SlateEditor re-sync.
  useEffect(() => {
    if (selectedProposal?.abstract !== undefined) {
      const server = selectedProposal.abstract || '';
      const draft = readDraft(draftKey('abstract', selectedProposal.id));
      abstractSavedRef.current = server;
      setAbstractText(draft && draft.base === server ? draft.body : server);   // unsaved text from before a refresh
      setAbstractEditorResetKey(k => k + 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProposal?.abstract]);

  // Abstract autosave (same rules as the sections: silent, merged with co-authors' saves)
  const abstractSavedRef = useRef('');
  const abstractBaseRef = useRef(null);
  useEffect(() => {
    abstractBaseRef.current = abstractSection?.current_content?.version ?? 0;
  }, [abstractSection?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const abstractLive = useRef({});
  abstractLive.current = { abstractText, abstractSection, proposalId: selectedProposal?.id };
  useEffect(() => {
    if (!selectedProposal?.id) return undefined;
    const t = setTimeout(() => {
      if (abstractText !== abstractSavedRef.current) {
        writeDraft(draftKey('abstract', selectedProposal.id), { base: abstractSavedRef.current, body: abstractText });
      }
    }, 800);
    return () => clearTimeout(t);
  }, [abstractText, selectedProposal?.id]);
  useEffect(() => {
    if (!selectedProposal?.id || !abstractSection?.id) return undefined;
    const timer = setInterval(async () => {
      const { abstractText: body, abstractSection: sec, proposalId } = abstractLive.current;
      if (!sec || body === abstractSavedRef.current) return;
      const result = await saveSectionContent(sec.id, { body, language: 'en', base_version: abstractBaseRef.current, auto: true });
      if (!result.success || result.data?.autosave_skipped) return;
      const saved = result.data;
      if (saved.merged && abstractLive.current.abstractText !== body) return;   // typed meanwhile: next minute
      try {
        await axiosService.patch(`/proposal/proposals/${proposalId}/`, { abstract: saved.body });
      } catch { return; }
      abstractBaseRef.current = saved.version;
      abstractSavedRef.current = saved.body;
      clearDraft(draftKey('abstract', proposalId));
      if (saved.merged) { setAbstractText(saved.body); setAbstractEditorResetKey((k) => k + 1); }
    }, AUTOSAVE_MS);
    return () => clearInterval(timer);
  }, [selectedProposal?.id, abstractSection?.id]);

  useEffect(() => {
    setKeywords(selectedProposal?.keywords || '');
  }, [selectedProposal?.keywords]);

  // Sync word limit inputs when the active section changes
  useEffect(() => {
    setWordLimitInput(editingSection?.word_limit != null ? String(editingSection.word_limit) : '');
  }, [editingSection?.id]);

  useEffect(() => {
    setAbstractWordLimitInput(abstractSection?.word_limit != null ? String(abstractSection.word_limit) : '');
  }, [abstractSection?.id]);

  useEffect(() => {
    if (abstractSection?.id) loadSectionComments(abstractSection.id, setAbstractComments);
    else setAbstractComments([]);
  }, [abstractSection?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAIReviewHistory = async (proposalId) => {
    try {
      const res = await axiosService.get(`/proposal/proposals/${proposalId}/ai_review_history/`);
      setAiReviewHistory(res.data || []);
    } catch {
      setAiReviewHistory([]);
    }
  };

  const handleStartAIReview = () => {
    if (!selectedProposal?.id) return;
    setAiReviewHistoryMode(false);
    setAiReviewResult(null);
    setAiReviewError(null);
    setAiReviewDialogOpen(true);
  };

  const handleShowPreviousReviews = () => {
    if (!selectedProposal?.id) return;
    setAiReviewResult(null);
    setAiReviewError(null);
    setAiReviewHistoryMode(true);
    setAiReviewDialogOpen(true);
  };

  const handleConfirmAIReview = async () => {
    setAiReviewLoading(true);
    setAiReviewError(null);
    setAiReviewResult(null);
    try {
      await axiosService.post(`/proposal/proposals/${selectedProposal.id}/ai_activate/`);
      const response = await axiosService.post(
        `/proposal/proposals/${selectedProposal.id}/ai_review/`
      );
      setAiReviewResult(response.data.review);
      setAiReviewHistoryMode(false);
      loadAIReviewHistory(selectedProposal.id);
    } catch (err) {
      const msg = err.response?.data?.error || 'AI Review failed. Please try again.';
      setAiReviewError(msg);
    } finally {
      setAiReviewLoading(false);
    }
  };

  const handleWordLimitBlur = async (sectionId, rawValue, onUpdated) => {
    const trimmed = String(rawValue).trim();
    const parsed = trimmed === '' ? null : parseInt(trimmed, 10);
    if (trimmed !== '' && (isNaN(parsed) || parsed < 1)) return;
    const result = await updateSection(sectionId, { word_limit: parsed });
    if (result.success) onUpdated(result.data.word_limit);
  };

  const isValidSemver = (v) => /^\d+\.\d+\.\d+$/.test(String(v).trim());

  const loadProposalVersions = async (proposalId) => {
    setVersionsLoading(true);
    const data = await fetchProposalVersions(proposalId);
    setVersionsList(data);
    setVersionsLoading(false);
  };

  const handleOpenVersionDialog = () => {
    setVersionInput(String(selectedProposal.current_version || '1.0.0'));
    setVersionDialogOpen(true);
    loadProposalVersions(selectedProposal.id);
  };

  const handleVersionSave = async () => {
    const trimmed = String(versionInput).trim();
    if (!isValidSemver(trimmed)) return;
    setVersionSaving(true);
    const result = await saveProposalVersion(selectedProposal.id, trimmed);
    setVersionSaving(false);
    if (result.success) {
      await loadProposalDetails(selectedProposal.id);
      await loadProposalVersions(selectedProposal.id);
    } else {
      setError(result.error?.error || 'Failed to save proposal version.');
    }
  };

  const handleSwitchVersion = async (version) => {
    if (version === selectedProposal.current_version) return;
    setSwitchingVersion(version);
    const result = await switchProposalVersion(selectedProposal.id, version);
    setSwitchingVersion(null);
    if (result.success) {
      setVersionInput(version);
      await loadProposalDetails(selectedProposal.id);
      await loadProposalVersions(selectedProposal.id);
    } else {
      setError(result.error?.error || 'Failed to switch proposal version.');
    }
  };


    // console.log("000 consortium", selectedProposal?.consortium);



  const isCoordinator = (consortium?.memberships || []).some((m) => {
    const memId = String(m?.member?.id ?? m?.member?.pk ?? '');
    return memId && memId === String(currentUser?.id) && (
      m?.role === 'coordinator' || (Array.isArray(m?.roles) && m.roles.includes('coordinator'))
    );
  });

  // Publications reuse this writer without work packages, budget or dissemination
  const isPublication = selectedProposal?.document_type === 'publication';
  const PUBLICATION_HIDDEN_TABS = [2, 3, 4];
  useEffect(() => {
    if (isPublication && PUBLICATION_HIDDEN_TABS.includes(activeTab)) setActiveTab(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublication, activeTab]);

  // A publication opens on its first tab, Authors
  const openedOnAuthorsRef = useRef(null);
  useEffect(() => {
    if (isPublication && selectedProposal?.id && openedOnAuthorsRef.current !== selectedProposal.id) {
      const restoring = openedOnAuthorsRef.current === null && initialUrlState.get('tab');
      openedOnAuthorsRef.current = selectedProposal.id;
      if (!restoring) setActiveTab(7);
    }
  }, [isPublication, selectedProposal?.id]);

  const savePublicationFields = async (data) => {
    const result = await updateProposal(selectedProposal.id, data);
    if (result.success) {
      await reloadProposalSilently(selectedProposal.id);
      showSnackbar('Saved', 'success');
    } else {
      showSnackbar('Could not save changes', 'error');
    }
  };

  // Writes text into the References section (created if missing)
  const writeReferencesSection = async (markdown) => {
    const existing = (selectedProposal.sections || []).find(
      (s) => s.section_key === 'references' || s.title.trim().toLowerCase() === 'references'
    );
    let sectionId = existing?.id;
    if (!sectionId) {
      const created = await createSection({ proposal: selectedProposal.id, title: 'References' });
      if (!created.success) { showSnackbar('Could not create the References section', 'error'); return false; }
      sectionId = created.data.id;
    }
    const result = await saveSectionContent(sectionId, { body: markdown, language: selectedProposal.language || 'en' });
    if (!result.success) showSnackbar('Could not update the References section', 'error');
    return result.success;
  };

  const citationStyle = selectedProposal?.citation_style || DEFAULT_CITATION_STYLE;
  const [formatDialogOpen, setFormatDialogOpen] = useState(false);

  // The References section lists exactly the cited references, in the publication's style
  const rebuildReferencesSection = async (refs = selectedProposal.references || [], announce = true, style = citationStyle) => {
    const ok = await writeReferencesSection(await citedReferencesMarkdown(refs, style));
    if (ok) {
      await reloadProposalSilently(selectedProposal.id);
      if (announce) showSnackbar('References section updated', 'success');
    }
  };

  const citedIds = (refs) => refs.filter((r) => r.cited).map((r) => r.id).join(',');

  // After a Word import: complete references that carry a DOI, write the References section in the
  // publication's style, reload. Works on fresh data, since the import replaced sections on the server.
  const finishDocxImport = async () => {
    const id = selectedProposal.id;
    const fresh = await fetchProposal(id);
    if (!fresh) return;
    let refs = fresh.document_type === 'publication' ? (fresh.references || []) : [];   // proposals: no bibliography
    const lookup = refs.filter((r) => !r.csl && r.doi);
    if (lookup.length) {
      try {
        const found = await referencesFromDois(lookup.map((r) => r.doi));
        const byDoi = Object.fromEntries(found.map((f) => [(f.csl.DOI || f.doi || '').toLowerCase(), f.csl]));
        refs = refs.map((r) => (!r.csl && r.doi && byDoi[r.doi.toLowerCase()] ? { ...r, csl: byDoi[r.doi.toLowerCase()] } : r));
        await updateProposal(id, { references: refs });
      } catch { /* offline or unknown DOIs: the references keep their imported text */ }
    }
    if (refs.some((r) => r.cited)) {
      const markdown = await citedReferencesMarkdown(refs, fresh.citation_style || DEFAULT_CITATION_STYLE);
      const flat = [];
      const walk = (list) => (list || []).forEach((sec) => { flat.push(sec); walk(sec.subsections); });
      walk(fresh.sections);
      let section = flat.find((sec) => sec.section_key === 'references' || sec.title.trim().toLowerCase() === 'references');
      if (!section) {
        const created = await createSection({ proposal: id, title: 'References' });
        section = created.success ? created.data : null;
      }
      if (section) await saveSectionContent(section.id, { body: markdown, language: fresh.language || 'en' });
    }
    try {                                     // the imported figures, so placeholders show their images
      const figs = await axiosService.get(`/proposal/proposals/${id}/figures/`);
      setFigureData(figs.data);
    } catch { /* figures appear on the next load */ }
    await reloadProposalSilently(id);
    showSnackbar('Publication imported', 'success');
  };

  // Bibliography edits; deleting a cited reference changes the References section
  const saveReferences = async (refs) => {
    const before = citedIds(selectedProposal.references || []);
    await savePublicationFields({ references: refs });
    if (citedIds(refs) !== before) await rebuildReferencesSection(refs, false);
  };

  const applyCitationStyle = async (style, title = '') => {
    const result = await updateProposal(selectedProposal.id, { citation_style: style, citation_style_title: title });
    if (!result.success) { showSnackbar('Could not change the citation style', 'error'); return; }
    setSelectedProposal((prev) => ({ ...prev, citation_style: style, citation_style_title: title }));
    setFormatDialogOpen(false);
    if ((selectedProposal.references || []).some((r) => r.cited)) {
      await rebuildReferencesSection(selectedProposal.references, false, style);
    }
    showSnackbar(`References formatted in ${getStyle(style, title).label} style`, 'success');
  };

  // Journal behind the citation style: author guidelines and recommended section template
  const [journalInfo, setJournalInfo] = useState(null);
  useEffect(() => {
    const style = selectedProposal?.citation_style;
    if (!isPublication || !style || style === 'vancouver') { setJournalInfo(null); return undefined; }
    let cancelled = false;
    axiosService.get(`/proposal/journal-styles/${encodeURIComponent(style)}/`)
      .then((res) => { if (!cancelled) setJournalInfo(res.data); })
      .catch(() => { if (!cancelled) setJournalInfo(null); });
    return () => { cancelled = true; };
  }, [isPublication, selectedProposal?.citation_style]);

  const isReferencesSection = (section) => section?.title?.trim().toLowerCase() === 'references';

  // Canonical [n] markers rendered in the chosen style for Preview and Word export
  const [inTextMap, setInTextMap] = useState(null);
  useEffect(() => {
    if (!isPublication) { setInTextMap(null); return undefined; }
    const bodies = [];
    const walk = (list) => (list || []).forEach((sec) => {
      if (!isReferencesSection(sec)) bodies.push(sec.current_content?.body || '');
      walk(sec.subsections);
    });
    walk(selectedProposal?.sections);
    let cancelled = false;
    buildInTextMap(bodies, selectedProposal?.references || [], citationStyle)
      .then((map) => { if (!cancelled) setInTextMap(map); })
      .catch(() => { if (!cancelled) setInTextMap(null); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublication, selectedProposal?.sections, selectedProposal?.references, citationStyle]);

  // Same rendering for the text currently being edited (debounced), so citations
  // show in the chosen style inside the editor as soon as they are inserted
  const [editorCiteMap, setEditorCiteMap] = useState(null);
  useEffect(() => {
    if (!isPublication || !editingSection) { setEditorCiteMap(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      buildInTextMap([content], selectedProposal?.references || [], citationStyle)
        .then((map) => { if (!cancelled) setEditorCiteMap(map); })
        .catch(() => {});
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublication, editingSection?.id, content, selectedProposal?.references, citationStyle]);

  const styledBody = (section) => {
    const body = section?.current_content?.body || '';
    return isPublication && !isReferencesSection(section) ? applyInTextMap(body, inTextMap) : body;
  };
  // Figure placeholders are numbered across the whole preview, in render order
  const previewFigures = { n: 0 };

  // Uploaded figure images (compressed on the server; shared by proposals and publications)
  const [figureData, setFigureData] = useState({ figures: [], used_bytes: 0, limit_bytes: 0 });
  useEffect(() => {
    const id = selectedProposal?.id;
    if (!id) return undefined;
    let cancelled = false;
    axiosService.get(`/proposal/proposals/${id}/figures/`)
      .then((res) => { if (!cancelled) setFigureData(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedProposal?.id]);
  const figuresMap = useMemo(
    () => Object.fromEntries((figureData.figures || []).map((f) => [f.id, f])),
    [figureData],
  );
  // after sections are deleted: their figures may be gone (freed on the server), refresh the list and storage
  const refreshFigures = useCallback(async () => {
    if (!selectedProposal?.id) return;
    try {
      const res = await axiosService.get(`/proposal/proposals/${selectedProposal.id}/figures/`);
      setFigureData(res.data);
    } catch { /* shown on the next load */ }
  }, [selectedProposal?.id]);
  const uploadFigure = useCallback(async (file) => {
    const form = new FormData();
    form.append('image', file);
    const res = await axiosService.post(`/proposal/proposals/${selectedProposal.id}/figures/`, form, { timeout: 120000 });
    setFigureData(res.data);
    return res.data.figure;
  }, [selectedProposal?.id]);
  const editorFigures = useMemo(
    () => ({ items: figuresMap, onUpload: uploadFigure, usage: figureData }),
    [figuresMap, uploadFigure, figureData],
  );
  // PNG/JPEG copies of the figures placed in the text, for the Word export (Word cannot hold WebP)
  const loadFigureImages = async (bodies) => {
    const ids = new Set();
    bodies.forEach((body) => {
      for (const m of (body || '').matchAll(/<!--figure:([^>]*)-->/g)) {
        try {
          const id = JSON.parse(decodeURIComponent(m[1])).figure_id;
          if (id && figuresMap[id]) ids.add(id);
        } catch { /* malformed marker: leave it as a placeholder */ }
      }
    });
    const images = {};
    await Promise.all([...ids].map(async (id) => {
      try {
        const res = await axiosService.get(`/proposal/proposals/${selectedProposal.id}/figures/${id}/`, { responseType: 'arraybuffer' });
        const type = (res.headers['content-type'] || '').includes('png') ? 'png' : 'jpg';
        images[id] = { data: res.data, type, width: figuresMap[id].width, height: figuresMap[id].height };
      } catch { /* that figure is exported as a placeholder */ }
    }));
    return images;
  };
  const allBodies = () => {
    const out = [selectedProposal?.abstract || ''];
    const walk = (list) => (list || []).forEach((sec) => { out.push(sec.current_content?.body || ''); walk(sec.subsections); });
    walk(selectedProposal?.sections);
    return out;
  };
  // Co-authors who have this document open (avatars at the top right of the section editor)
  const presenceOthers = usePresence(selectedProposal?.id, editingSection?.id);

  // "N new" badges on the tabs: changes by others since you last opened each tab. A tab is marked
  // seen when you open it and when you leave it (so your own edits there don't count), and the
  // open tab is kept seen while you're on it.
  const [tabChanges, setTabChanges] = useState({});
  const activeTabKey = TAB_KEYS[activeTab];
  const activeTabKeyRef = useRef(activeTabKey);
  const proposalIdForTabs = selectedProposal?.id;
  const markTabsSeen = useCallback((tabs) => {
    if (!proposalIdForTabs) return;
    const seen = tabs.filter(Boolean);
    const req = seen.length
      ? axiosService.post(`/proposal/proposals/${proposalIdForTabs}/tab-changes/`, { seen })
      : axiosService.get(`/proposal/proposals/${proposalIdForTabs}/tab-changes/`);
    req.then((res) => setTabChanges(res.data || {})).catch(() => {});
  }, [proposalIdForTabs]);
  useEffect(() => {
    const left = activeTabKeyRef.current;
    activeTabKeyRef.current = activeTabKey;
    markTabsSeen([left !== activeTabKey ? left : null, activeTabKey]);
  }, [activeTabKey, markTabsSeen]);
  useEffect(() => {
    if (!proposalIdForTabs) return undefined;
    const timer = setInterval(() => markTabsSeen([activeTabKeyRef.current]), 60000);
    return () => {
      clearInterval(timer);
      markTabsSeen([activeTabKeyRef.current]);      // leaving the proposal
    };
  }, [proposalIdForTabs, markTabsSeen]);
  const tabLabel = (text, value) => {
    const key = TAB_KEYS[value];
    const n = key && key !== activeTabKey ? tabChanges[key] || 0 : 0;
    if (!n) return text;
    return (
      <Tooltip describeChild title={`${n} new ${n === 1 ? 'change' : 'changes'} by others since you last opened this tab`}>
        <Badge
          badgeContent={n}
          color="error"
          max={99}
          sx={{ '& .MuiBadge-badge': { top: -2, right: -12, fontSize: '0.7rem', height: 18, minWidth: 18, fontWeight: 700 } }}
        >
          {text}
        </Badge>
      </Tooltip>
    );
  };

  // Editor features shared by the Sections and Abstract editors
  const trackChangesProp = currentUser ? {
    author: {
      id: currentUser.id,
      name: currentUser.name?.trim() || [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') || currentUser.email,
    },
  } : undefined;
  const toEditorComment = (c) => ({
    id: c.id,
    commentId: c.comment_id,
    body: c.body,
    authorId: c.author_id ? String(c.author_id) : null,
    authorName: c.author_name,
    authorAvatar: c.author_avatar || null,
    created: c.created,
    resolved: c.resolved ?? false,
    replies: c.replies || [],
  });
  const loadSectionComments = (sectionId, setList) => {
    setList([]);
    axiosService.get(`/proposal/section-comments/?section=${sectionId}`)
      .then(res => setList((res.data?.results || res.data || []).map(toEditorComment)))
      .catch(() => {});
  };
  // comments / onAddComment / onResolveComment / onReplyComment props for a section's editor
  const commentProps = (sectionId, list, setList) => ({
    comments: list,
    onAddComment: async (commentId, body) => {
      try {
        const res = await axiosService.post('/proposal/section-comments/', { section: sectionId, comment_id: commentId, body });
        setList(prev => [...prev, toEditorComment(res.data)]);
      } catch (e) {
        console.error('Failed to save comment', e);
      }
    },
    onResolveComment: async (commentId) => {
      const c = list.find(x => x.commentId === commentId);
      if (!c) return;
      try {
        const res = await axiosService.patch(`/proposal/section-comments/${c.id}/resolve/`);
        setList(prev => prev.map(x => (x.commentId === commentId ? { ...x, resolved: res.data.resolved } : x)));
      } catch (e) {
        console.error('Failed to resolve comment', e);
      }
    },
    onReplyComment: async (commentId, body) => {
      const c = list.find(x => x.commentId === commentId);
      if (!c) return;
      try {
        const res = await axiosService.post(`/proposal/section-comments/${c.id}/reply/`, { body });
        setList(prev => prev.map(x => (x.commentId === commentId ? { ...x, replies: [...(x.replies || []), res.data] } : x)));
      } catch (e) {
        console.error('Failed to add reply', e);
      }
    },
    // Authors can change or remove their own comment and replies
    currentUserId: currentUser?.id ? String(currentUser.id) : null,
    onEditComment: async (commentId, body) => {
      const c = list.find(x => x.commentId === commentId);
      if (!c) return false;
      try {
        const res = await axiosService.patch(`/proposal/section-comments/${c.id}/`, { body });
        setList(prev => prev.map(x => (x.commentId === commentId ? { ...x, body: res.data.body } : x)));
        return true;
      } catch (e) {
        showSnackbar(e.response?.data?.error || e.response?.data?.detail || 'Could not edit the comment', 'error');
        return false;
      }
    },
    onDeleteComment: async (commentId) => {
      const c = list.find(x => x.commentId === commentId);
      if (!c) return false;
      try {
        await axiosService.delete(`/proposal/section-comments/${c.id}/`);
        setList(prev => prev.filter(x => x.commentId !== commentId));
        return true;
      } catch (e) {
        showSnackbar(e.response?.data?.error || e.response?.data?.detail || 'Could not delete the comment', 'error');
        return false;
      }
    },
    onEditReply: async (commentId, index, body) => {
      const c = list.find(x => x.commentId === commentId);
      if (!c) return false;
      try {
        const res = body === null
          ? await axiosService.delete(`/proposal/section-comments/${c.id}/replies/${index}/`)
          : await axiosService.patch(`/proposal/section-comments/${c.id}/replies/${index}/`, { body });
        setList(prev => prev.map(x => (x.commentId === commentId ? { ...x, replies: res.data.replies } : x)));
        return true;
      } catch (e) {
        showSnackbar(e.response?.data?.error || 'Could not change the reply', 'error');
        return false;
      }
    },
  });

  // Called by the section editor's "Insert reference" button; returns the in-text marker
  const handleCite = async (ids) => {
    const { references, numbers, changed } = applyCitations(selectedProposal.references || [], ids);
    if (changed) {
      const result = await updateProposal(selectedProposal.id, { references });
      if (!result.success) { showSnackbar('Could not save the citation', 'error'); return null; }
      setSelectedProposal((prev) => ({ ...prev, references }));
      await rebuildReferencesSection(references, false);
      markTabsSeen(['bibliography']);
    }
    return citationMarker(numbers);
  };

  // Readable labels for the citation picker
  const [citationLabels, setCitationLabels] = useState({});
  useEffect(() => {
    const refs = selectedProposal?.references || [];
    if (!isPublication || refs.length === 0) { setCitationLabels({}); return undefined; }
    let cancelled = false;
    formatAll(refs, 'apa')
      .then((texts) => { if (!cancelled) setCitationLabels(Object.fromEntries(refs.map((r, i) => [r.id, texts[i]]))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isPublication, selectedProposal?.references]);

  // The full references in the publication's own style, for the hover on in-text citations
  const [fullReferenceTexts, setFullReferenceTexts] = useState({});
  useEffect(() => {
    const refs = selectedProposal?.references || [];
    if (!isPublication || refs.length === 0) { setFullReferenceTexts({}); return undefined; }
    let cancelled = false;
    formatAll(refs, citationStyle)
      .then((texts) => { if (!cancelled) setFullReferenceTexts(Object.fromEntries(refs.map((r, i) => [r.id, texts[i]]))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isPublication, selectedProposal?.references, citationStyle]);
  const citationTitle = useCallback(
    (marker) => referencesForMarker(marker, selectedProposal?.references || [], fullReferenceTexts),
    [selectedProposal?.references, fullReferenceTexts],
  );
  // Preview: citations in the chosen style, full references on hover (not in the References section itself)
  const previewCitations = (section) => (isPublication && !isReferencesSection(section)
    ? { display: inTextMap, title: citationTitle } : null);
  const previewBody = (section) => (previewCitations(section) ? section?.current_content?.body || '' : styledBody(section));

  const citationItems = (selectedProposal?.references || []).map((r, i) => ({
    id: r.id,
    label: citationLabels[r.id] || r.text || r.csl?.title || 'Untitled reference',
    number: r.cited ? i + 1 : null,
  }));
  // in-text citations for the Sections and Abstract editors (publications only)
  const citationsProp = isPublication
    ? { items: citationItems, onCite: handleCite, onFormat: () => setFormatDialogOpen(true), display: editorCiteMap, title: citationTitle }
    : undefined;

  // Project leader for this specific proposal (scoped to collaboration via consortium_users)
  const isProjectLeader = (selectedProposal?.consortium_users || []).some(
    u => String(u.id) === String(currentUser?.id) && Array.isArray(u.roles) && u.roles.includes('project_leader')
  );
  // Managing sections (add, add subsection, edit settings, delete): the coordinator and the document's project
  // leader (a publication's corresponding author); the server applies the same rule to deleting
  // (ProposalSectionViewSet.perform_destroy)
  const canManageSections = isCoordinator || isProjectLeader;

  // Auto-create Appendix section (hidden, like Abstract) — placed after isCoordinator is defined
  useEffect(() => {
    // Publications have no appendix yet (it will hold uploaded supplementary material)
    if (!selectedProposal?.id || !isCoordinator || isPublication) return;
    const hasAppendix = selectedProposal.sections?.some(s => s.section_key === 'appendix');
    if (!hasAppendix) {
      createSection({
        proposal: selectedProposal.id,
        section_key: 'appendix',
        title: 'Appendix',
        position: 9999,
      }).then(result => {
        if (result.success) loadProposalDetails(selectedProposal.id);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProposal?.id, isCoordinator]);

  const fetchChatMessages = useCallback(async () => {
    if (!selectedProposal?.id) return;
    try {
      const res = await axiosService.get(`/proposal/proposals/${selectedProposal.id}/messages/`);
      setChatMessages(res.data);
    } catch (_) {}
  }, [selectedProposal?.id]);

  useEffect(() => {
    if (!chatOpen) return;
    fetchChatMessages();
    const interval = setInterval(fetchChatMessages, 8000);
    return () => clearInterval(interval);
  }, [chatOpen, fetchChatMessages]);

  useEffect(() => {
    if (chatOpen && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatOpen]);

  const handleChatSend = async () => {
    const el = chatInputRef.current;
    if (!el) return;
    const body = el.value.trim();
    if (!body || chatSending) return;
    el.value = '';
    setHasChatInput(false);
    setChatSending(true);
    try {
      
      const res = await axiosService.post(`/proposal/proposals/${selectedProposal.id}/messages/`, { body });
      setChatMessages(prev => [...prev, res.data]);
    } catch (_) {
      el.value = '';
      // setHasChatInput(true);
    } finally {
      setChatSending(false);
    }
  };

  const handleMsgEditStart = (msg) => {
    setEditingMsgId(msg.id);
    setEditingMsgBody(msg.body);
  };

  const handleMsgEditSave = async () => {
    const body = editingMsgBody.trim();
    if (!body) return;
    try {
      const res = await axiosService.patch(
        `/proposal/proposals/${selectedProposal.id}/messages/${editingMsgId}/`,
        { body }
      );
      setChatMessages(prev => prev.map(m => m.id === editingMsgId ? res.data : m));
    } catch (_) {}
    setEditingMsgId(null);
    setEditingMsgBody('');
  };

  // Emoji reactions on team chat messages: a click adds yours, another takes it back
  const [reactAnchor, setReactAnchor] = useState(null);          // { el, msg } while the emoji menu is open
  const handleMsgReact = async (msg, emoji) => {
    setReactAnchor(null);
    try {
      const res = await axiosService.post(`/proposal/proposals/${selectedProposal.id}/messages/${msg.id}/react/`, { emoji });
      setChatMessages(prev => prev.map(m => (m.id === msg.id ? res.data : m)));
    } catch (_) {}
  };

  const handleMsgDelete = async (msg) => {
    try {
      await axiosService.delete(`/proposal/proposals/${selectedProposal.id}/messages/${msg.id}/`);
      setChatMessages(prev => prev.filter(m => m.id !== msg.id));
    } catch (_) {}
  };

  // When false (default), any member may edit freely — no locking, no approval workflow
  const lockingEnabled = consortium?.funding_application?.section_locking_enabled ?? false;

  const handleSectionStatusUpdate = async (sectionId, newStatus) => {
    const result = await updateSectionStatus(sectionId, newStatus);
    if (result.success) {
      await loadProposalDetails(selectedProposal.id);
    } else {
      setError(result.error || 'Failed to update section status.');
    }
  };

// const handleDownloadWord = () => {
//    setDownloading(true);
//   try {
//     // Create document sections
//     const sections = [];

//     // 1. Title Page
//     sections.push(
//       new Paragraph({
//         children: [
//           new TextRun({
//             text: selectedProposal.title,
//             bold: true,
//             size: 36,
//           }),
//         ],
//         alignment: AlignmentType.CENTER,
//         spacing: { after: 400 },
//       }),
//       new Paragraph({
//         children: [
//           new TextRun({
//             text: `(${selectedProposal.acronym})`,
//             size: 28,
//           }),
//         ],
//         alignment: AlignmentType.CENTER,
//         spacing: { after: 300 },
//       }),
//       new Paragraph({
//         children: [
//           new TextRun({
//             text: `Duration: ${selectedProposal.duration_months} months`,
//             size: 22,
//           }),
//         ],
//         spacing: { after: 200 },
//       }),
//       new Paragraph({
//         children: [
//           new TextRun({
//             text: `Total Budget: €${selectedProposal.total_budget ? parseFloat(selectedProposal.total_budget).toLocaleString() : 'N/A'}`,
//             size: 22,
//           }),
//         ],
//         spacing: { after: 200 },
//       }),
//       new Paragraph({
//         children: [
//           new TextRun({
//             text: `Status: ${selectedProposal.status.replace('_', ' ')}`,
//             size: 22,
//           }),
//         ],
//         spacing: { after: 400 },
//       })
//     );

//     // 2. Abstract
//     if (selectedProposal.abstract) {
//       sections.push(
//         new Paragraph({
//           text: "ABSTRACT",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         }),
//         new Paragraph({
//           text: selectedProposal.abstract,
//           spacing: { after: 400 },
//         })
//       );
//     }

//     // 3. Consortium Members Table
//     if (selectedProposal.consortium_members?.length > 0) {
//       sections.push(
//         new Paragraph({
//           text: "Consortium Members",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         })
//       );

//       const memberRows = [
//         new DocTableRow({
//           children: [
//             new DocTableCell({
//               children: [new Paragraph("Number")],
//               width: { size: 10, type: WidthType.PERCENTAGE },
//             }),
//             new DocTableCell({
//               children: [new Paragraph("Organization")],
//               width: { size: 30, type: WidthType.PERCENTAGE },
//             }),
//             new DocTableCell({
//               children: [new Paragraph("Short Name")],
//               width: { size: 15, type: WidthType.PERCENTAGE },
//             }),
//             new DocTableCell({
//               children: [new Paragraph("Country")],
//               width: { size: 15, type: WidthType.PERCENTAGE },
//             }),
//             new DocTableCell({
//               children: [new Paragraph("Type")],
//               width: { size: 15, type: WidthType.PERCENTAGE },
//             }),
//             new DocTableCell({
//               children: [new Paragraph("Person-Months")],
//               width: { size: 15, type: WidthType.PERCENTAGE },
//             }),
//           ],
//           tableHeader: true,
//         }),
//         ...selectedProposal.consortium_members
//           // .sort((a, b) => (a.number || '').localeCompare(b.number || ''))
//           .map(member =>
//             new DocTableRow({
//               children: [
//                 new DocTableCell({
//                   children: [new Paragraph(member.number || '—')],
//                 }),
//                 new DocTableCell({
//                   children: [new Paragraph(member.name || '—')],
//                 }),
//                 new DocTableCell({
//                   children: [new Paragraph(member.short_name || '—')],
//                 }),
//                 new DocTableCell({
//                   children: [new Paragraph(member.country || '—')],
//                 }),
//                 new DocTableCell({
//                   children: [new Paragraph(member.org_type || '—')],
//                 }),
//                 new DocTableCell({
//                   children: [new Paragraph((member.total_person_months || 0).toString())],
//                 }),
//               ],
//             })
//           ),
//       ];

//       sections.push(
//         new DocTable({
//           rows: memberRows,
//           width: { size: 100, type: WidthType.PERCENTAGE },
//         })
//       );
//     }

//     // 4. Sections Content
//     if (selectedProposal.sections?.length > 0) {
//       sections.push(
//         new Paragraph({
//           text: "Proposal Sections",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         })
//       );

//       selectedProposal.sections.forEach(section => {
//         if (section.current_content?.body) {
//           sections.push(
//             new Paragraph({
//               text: `${section.section_key}. ${section.title.toUpperCase()}`,
//               heading: HeadingLevel.HEADING_2,
//               spacing: { before: 300, after: 100 },
//             }),
//             new Paragraph({
//               text: section.current_content.body,
//               spacing: { after: 200 },
//             })
//           );
//         }
//       });
//     }

//     // 5. Work Packages
//     if (selectedProposal.work_packages?.length > 0) {
//       sections.push(
//         new Paragraph({
//           text: "Table 3.1a: List of Work Packages",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         })
//       );

//       const wpRows = [
//         new DocTableRow({
//           children: [
//             new DocTableCell({ children: [new Paragraph("WP No")] }),
//             new DocTableCell({ children: [new Paragraph("Title")] }),
//             new DocTableCell({ children: [new Paragraph("Lead")] }),
//             new DocTableCell({ children: [new Paragraph("Person-Months")] }),
//             new DocTableCell({ children: [new Paragraph("Start Month")] }),
//             new DocTableCell({ children: [new Paragraph("End Month")] }),
//           ],
//           tableHeader: true,
//         }),
//         ...selectedProposal.work_packages.map(wp =>
//           new DocTableRow({
//             children: [
//               new DocTableCell({ children: [new Paragraph(wp.number || '—')] }),
//               new DocTableCell({ children: [new Paragraph(wp.title || '—')] }),
//               new DocTableCell({
//                 children: [new Paragraph(wp.wp_leader_org_name || '—')],
//               }),
//               new DocTableCell({
//                 children: [new Paragraph((wp.total_person_months || 0).toString())],
//               }),
//               new DocTableCell({ children: [new Paragraph((wp.start_month || '—').toString())] }),
//               new DocTableCell({ children: [new Paragraph((wp.end_month || '—').toString())] }),
//             ],
//           })
//         ),
//       ];

//       sections.push(
//         new DocTable({
//           rows: wpRows,
//           width: { size: 100, type: WidthType.PERCENTAGE },
//         })
//       );
//     }

//     // 6. Staff Effort Summary
//     if (selectedProposal.work_packages?.length > 0 && selectedProposal.consortium_members?.length > 0) {
//       sections.push(
//         new Paragraph({
//           text: "Table 3.1f: Summary of staff effort",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         })
//       );

//       // Create WP headers
//       const wpHeaders = selectedProposal.work_packages.map(wp =>
//         new DocTableCell({
//           children: [new Paragraph(`WP${wp.number}`)],
//           width: { size: 8, type: WidthType.PERCENTAGE },
//         })
//       );

//       const effortRows = [
//         new DocTableRow({
//           children: [
//             new DocTableCell({
//               children: [new Paragraph("Participant")],
//               width: { size: 20, type: WidthType.PERCENTAGE },
//             }),
//             ...wpHeaders,
//             new DocTableCell({
//               children: [new Paragraph("Total")],
//               width: { size: 10, type: WidthType.PERCENTAGE },
//             }),
//           ],
//           tableHeader: true,
//         }),
//         ...selectedProposal.consortium_members.map(member => {
//           const wpCells = selectedProposal.work_packages.map(wp =>
//             new DocTableCell({
//               children: [
//                 new Paragraph((member.effort_by_wp?.[wp.number] || 0).toString()),
//               ],
//             })
//           );

//           const total = selectedProposal.work_packages.reduce(
//             (sum, wp) => sum + (member.effort_by_wp?.[wp.number] || 0),
//             0
//           );

//           return new DocTableRow({
//             children: [
//               new DocTableCell({
//                 children: [new Paragraph(`${member.number} / ${member.name}`)],
//               }),
//               ...wpCells,
//               new DocTableCell({ children: [new Paragraph(total.toString())] }),
//             ],
//           });
//         }),
//       ];

//       sections.push(
//         new DocTable({
//           rows: effortRows,
//           width: { size: 100, type: WidthType.PERCENTAGE },
//         })
//       );
//     }

//     // 7. Deliverables
//     const allDeliverables = selectedProposal.work_packages?.flatMap(
//       wp => wp.deliverables ? wp.deliverables.map(d => ({ ...d, wp_number: wp.number })) : []
//     ) || [];

//     if (allDeliverables.length > 0) {
//       sections.push(
//         new Paragraph({
//           text: "Table 3.1c: List of Deliverables",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         })
//       );

//       const deliverableRows = [
//         new DocTableRow({
//           children: [
//             new DocTableCell({ children: [new Paragraph("Number")] }),
//             new DocTableCell({ children: [new Paragraph("Name")] }),
//             new DocTableCell({ children: [new Paragraph("Description")] }),
//             new DocTableCell({ children: [new Paragraph("WP No")] }),
//             new DocTableCell({ children: [new Paragraph("Lead")] }),
//             new DocTableCell({ children: [new Paragraph("Type")] }),
//             new DocTableCell({ children: [new Paragraph("Delivery Month")] }),
//           ],
//           tableHeader: true,
//         }),
//         ...allDeliverables.map(deliv =>
//           new DocTableRow({
//             children: [
//               new DocTableCell({ children: [new Paragraph(deliv.code || '—')] }),
//               new DocTableCell({ children: [new Paragraph(deliv.title || '—')] }),
//               new DocTableCell({ children: [new Paragraph(deliv.description || '—')] }),
//               new DocTableCell({ children: [new Paragraph((deliv.wp_number || '—').toString())] }),
//               new DocTableCell({
//                 children: [new Paragraph(deliv.assignee?.organization?.name || '—')],
//               }),
//               new DocTableCell({ children: [new Paragraph(deliv.type || '—')] }),
//               new DocTableCell({
//                 children: [new Paragraph((deliv.delivery_month || '—').toString())],
//               }),
//             ],
//           })
//         ),
//       ];

//       sections.push(
//         new DocTable({
//           rows: deliverableRows,
//           width: { size: 100, type: WidthType.PERCENTAGE },
//         })
//       );
//     }

//     // 8. Milestones
//     const allMilestones = selectedProposal.work_packages?.flatMap(
//       wp => wp.milestones ? wp.milestones.map(m => ({ ...m, wp_number: wp.number })) : []
//     ) || [];

//     if (allMilestones.length > 0) {
//       sections.push(
//         new Paragraph({
//           text: "Table 3.1d: List of Milestones",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         })
//       );

//       const milestoneRows = [
//         new DocTableRow({
//           children: [
//             new DocTableCell({ children: [new Paragraph("Number")] }),
//             new DocTableCell({ children: [new Paragraph("Name")] }),
//             new DocTableCell({ children: [new Paragraph("WP No")] }),
//             new DocTableCell({ children: [new Paragraph("Due Month")] }),
//             new DocTableCell({ children: [new Paragraph("Verification")] }),
//           ],
//           tableHeader: true,
//         }),
//         ...allMilestones.map(ms =>
//           new DocTableRow({
//             children: [
//               new DocTableCell({ children: [new Paragraph(ms.code || '—')] }),
//               new DocTableCell({ children: [new Paragraph(ms.description || '—')] }),
//               new DocTableCell({
//                 children: [new Paragraph((ms.wp_number || '—').toString())],
//               }),
//               new DocTableCell({ children: [new Paragraph((ms.due_month || '—').toString())] }),
//               new DocTableCell({
//                 children: [new Paragraph(ms.verification_means || '—')],
//               }),
//             ],
//           })
//         ),
//       ];

//       sections.push(
//         new DocTable({
//           rows: milestoneRows,
//           width: { size: 100, type: WidthType.PERCENTAGE },
//         })
//       );
//     }

//     // 9. Budget Summary
//     if (selectedProposal.tasks?.length > 0) {
//       sections.push(
//         new Paragraph({
//           text: "Budget Summary",
//           heading: HeadingLevel.HEADING_1,
//           spacing: { before: 400, after: 200 },
//         })
//       );

//       const budgetItems = selectedProposal.tasks.flatMap(t => t.budget_items || []);
//       const totals = {
//         consumables: budgetItems.reduce((s, i) => s + parseFloat(i.consumables || 0), 0),
//         equipment: budgetItems.reduce((s, i) => s + parseFloat(i.equipment || 0), 0),
//         personnel: budgetItems.reduce((s, i) => s + parseFloat(i.personnel || 0), 0),
//         travel: budgetItems.reduce((s, i) => s + parseFloat(i.travel || 0), 0),
//         subcontracting: budgetItems.reduce((s, i) => s + parseFloat(i.subcontracting || 0), 0),
//         overheads: budgetItems.reduce((s, i) => s + parseFloat(i.overheads || 0), 0),
//       };
//       const grandTotal = Object.values(totals).reduce((s, v) => s + v, 0);

//       sections.push(
//         new Paragraph({
//           text: `Consumables: €${totals.consumables.toLocaleString()}`,
//           spacing: { after: 50 },
//         }),
//         new Paragraph({
//           text: `Equipment: €${totals.equipment.toLocaleString()}`,
//           spacing: { after: 50 },
//         }),
//         new Paragraph({
//           text: `Personnel: €${totals.personnel.toLocaleString()}`,
//           spacing: { after: 50 },
//         }),
//         new Paragraph({
//           text: `Travel: €${totals.travel.toLocaleString()}`,
//           spacing: { after: 50 },
//         }),
//         new Paragraph({
//           text: `Subcontracting: €${totals.subcontracting.toLocaleString()}`,
//           spacing: { after: 50 },
//         }),
//         new Paragraph({
//           text: `Overheads: €${totals.overheads.toLocaleString()}`,
//           spacing: { after: 50 },
//         }),
//         new Paragraph({
//           text: `Total Budget: €${grandTotal.toLocaleString()}`,
//           bold: true,
//           spacing: { before: 100, after: 200 },
//         })
//       );
//     }

//     // Create the document
//     const doc = new Document({
//       sections: [
//         {
//           properties: {},
//           children: sections,
//         },
//       ],
//     });

//     // Generate and download
//     Packer.toBlob(doc).then(blob => {
//       saveAs(blob, `${selectedProposal.acronym}_Proposal_${new Date().toISOString().split('T')[0]}.docx`);
//     });

//   } catch (error) {
//     console.error('Error generating Word document:', error);
//     alert('Error generating Word document. Please try again.');
//   }

//   finally {
//     setDownloading(false);
//   }
// };



const handleDownloadWord = async () => {
  try {
    const children = [];
    const docFigures = { n: 0 }; // figure placeholders numbered across the document
    const docImages = await loadFigureImages(allBodies());
    const { tableLabels, allDeliverables, allMilestones, allRisks } = computeWorkPlanTableData(selectedProposal);

    // 1. TITLE PAGE
    children.push(
      new Paragraph({
        children: [new TextRun({ text: selectedProposal.title || 'Proposal', bold: true, size: 52, color: '1F3864' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
      new Paragraph({
        text: `(${selectedProposal.acronym || ''})`,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      })
    );

    // 2. ABSTRACT
    if (selectedProposal.abstract) {
      children.push(
        new Paragraph({ text: 'Abstract', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } }),
        ...markdownToDocxParagraphs(selectedProposal.abstract, { figureCounter: docFigures, figureImages: docImages })
      );
    }

    // 3. CONSORTIUM MEMBERS
    if (selectedProposal.consortium_members && selectedProposal.consortium_members.length > 0) {
      children.push(
        new Paragraph({ text: 'Consortium Members', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } })
      );
      const memberHeaderRow = new DocxTableRow({
        children: ['Number', 'Organization', 'Short Name', 'Country', 'Total Person-Months', 'Total Budget (€)']
          .map(h => new DocxTableCell({ children: [new Paragraph(h)] })),
      });
      const memberRows = selectedProposal.consortium_members
        .slice()
        .sort((a, b) => (a.number || 0) - (b.number || 0))
        .map(member => new DocxTableRow({
          children: [
            new DocxTableCell({ children: [new Paragraph(String(member.number ?? '—'))] }),
            new DocxTableCell({ children: [new Paragraph(member.name || '—')] }),
            new DocxTableCell({ children: [new Paragraph(member.short_name || '—')] }),
            new DocxTableCell({ children: [new Paragraph(member.country || '—')] }),
            new DocxTableCell({ children: [new Paragraph(String(member.total_person_months ?? 0))] }),
            new DocxTableCell({ children: [new Paragraph(`€${parseFloat(member.total_budget || 0).toLocaleString()}`)] }),
          ],
        }));
      children.push(new DocxTable({ rows: [memberHeaderRow, ...memberRows], width: { size: 100, type: WidthType.PERCENTAGE } }));
    }

    // 4. SECTIONS — the written proposal narrative (Excellence/Impact/
    // Implementation etc.), recursively including subsections, matching what
    // the Preview tab shows.
    const sectionHeadingLevels = [HeadingLevel.HEADING_1, HeadingLevel.HEADING_2, HeadingLevel.HEADING_3, HeadingLevel.HEADING_4];
    const addSectionContent = (section, depth) => {
      children.push(
        new Paragraph({
          text: `${section.section_key}. ${section.title}`,
          heading: sectionHeadingLevels[Math.min(depth, sectionHeadingLevels.length - 1)],
          spacing: { before: 300, after: 150 },
        })
      );
      if (section.current_content?.body) {
        children.push(...markdownToDocxParagraphs(styledBody(section), { figureCounter: docFigures, figureImages: docImages }));
      } else {
        children.push(new Paragraph({ text: 'No content available for this section.', spacing: { after: 100 } }));
      }
      (section.subsections || []).forEach(sub => addSectionContent(sub, depth + 1));
    };
    (selectedProposal.sections || [])
      .filter(s => s.section_key !== 'abstract' && s.section_key !== 'appendix')
      .forEach(section => addSectionContent(section, 0));

    // 5. WORK PACKAGES TABLE
    if (selectedProposal.work_packages && selectedProposal.work_packages.length > 0) {
      children.push(
        new Paragraph({
          text: tableLabels.wpList || "List of Work Packages",
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 },
        })
      );

      // Create table rows
      const tableRows = [
        new DocxTableRow({
          children: [
            new DocxTableCell({ children: [new Paragraph("Work Package No")] }),
            new DocxTableCell({ children: [new Paragraph("Work Package Title")] }),
            new DocxTableCell({ children: [new Paragraph("Lead Participant")] }),
            new DocxTableCell({ children: [new Paragraph("Person-Months")] }),
            new DocxTableCell({ children: [new Paragraph("Start Month")] }),
            new DocxTableCell({ children: [new Paragraph("End Month")] }),
          ],
        }),
        ...selectedProposal.work_packages.map((wp, index) => {
          return new DocxTableRow({
            children: [
              new DocxTableCell({ children: [new Paragraph(wp.number.toString() || `WP${index + 1}`)] }),
              new DocxTableCell({ children: [new Paragraph(wp.title || '—')] }),
              new DocxTableCell({ 
                children: [new Paragraph(
                  wp.wp_leader_org_name ? 
                  `${wp.wp_leader_org_name} (${wp.wp_leader_org_short_name || ''})` 
                  : '—'
                )] 
              }),
              new DocxTableCell({ children: [new Paragraph((wp.total_person_months || 0).toString())] }),
              new DocxTableCell({ children: [new Paragraph((wp.start_month || '—').toString())] }),
              new DocxTableCell({ children: [new Paragraph((wp.end_month || '—').toString())] }),
            ],
          });
        })
      ];

      // Create the table
      const workPackageTable = new DocxTable({
        rows: tableRows,
        width: {
          size: 100,
          type: WidthType.PERCENTAGE,
        },
      });

      children.push(workPackageTable);
    }

    // 6. WORK PACKAGE DESCRIPTIONS
    if (selectedProposal.work_packages && selectedProposal.work_packages.length > 0) {
      children.push(
        new Paragraph({
          text: tableLabels.wpDesc || "Work Package Descriptions",
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 },
        })
      );

      selectedProposal.work_packages.forEach((wp, wpIndex) => {
        // WP Header
        children.push(
          new Paragraph({
            text: `WP${wp.number?.toString() || wpIndex + 1}: ${wp.title || ''}`,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 100 },
          })
        );

        // Create description table
        const descRows = [
          new DocxTableRow({
            children: [
              new DocxTableCell({ 
                children: [new Paragraph("Work package number")],
                width: { size: 30, type: WidthType.PERCENTAGE }
              }),
              new DocxTableCell({ 
                children: [new Paragraph(String(wp.number) || `WP${wpIndex + 1}`)],
                width: { size: 70, type: WidthType.PERCENTAGE }
              }),
            ],
          }),
          new DocxTableRow({
            children: [
              new DocxTableCell({ children: [new Paragraph("Work package title")] }),
              new DocxTableCell({ children: [new Paragraph(wp.title || '—')] }),
            ],
          }),
        ];

        // Add objectives if they exist
        if (wp.objectives && wp.objectives.length > 0) {
          descRows.push(
            new DocxTableRow({
              children: [
                new DocxTableCell({ 
                  children: [new Paragraph("Objectives")],
                  verticalAlign: VerticalAlign.TOP
                }),
                new DocxTableCell({
                  children: wp.objectives.map((obj, idx) =>
                    new Paragraph({
                      children: [
                        new TextRun({ text: `O${wp.number}.${idx + 1} - `, bold: true }),
                        new TextRun({ text: obj.description || '' }),
                      ],
                      spacing: { after: 60 },
                    })
                  ),
                  verticalAlign: VerticalAlign.TOP
                }),
              ],
            })
          );
        }

        // Add description of work
        descRows.push(
          new DocxTableRow({
            children: [
              new DocxTableCell({ 
                children: [new Paragraph("Description of work")],
                verticalAlign: VerticalAlign.TOP
              }),
              new DocxTableCell({ 
                children: [
                  new Paragraph(wp.description || '—'),
                  ...(wp.tasks && wp.tasks.length > 0 ?
                    wp.tasks.map((task) => {
                      const mStart = task.start_month;
                      const mEnd = mStart && task.duration_months
                        ? mStart + task.duration_months - 1
                        : null;
                      const months = mStart
                        ? ` (M${mStart}${mEnd ? ` - M${mEnd}` : ''})`
                        : '';
                      return new Paragraph({
                        children: [
                          new TextRun({ text: `Task ${wp.number}.${task.number} - ${task.title}${months}: `, bold: true }),
                          new TextRun({ text: task.description || '' }),
                        ],
                        spacing: { after: 80 },
                      });
                    }) : []
                  )
                ],
                verticalAlign: VerticalAlign.TOP
              }),
            ],
          })
        );

        children.push(
          new DocxTable({
            rows: descRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          }),
          new Paragraph({ text: '' }) // Spacer
        );
      });
    }

    // 7. LIST OF DELIVERABLES
    if (allDeliverables.length > 0) {
      children.push(
        new Paragraph({ text: tableLabels.deliverables || 'List of Deliverables', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } })
      );
      const delivHeaderRow = new DocxTableRow({
        children: ['Number', 'Deliverable name', 'WP No', 'Type', 'Dissemination level', 'Delivery month']
          .map(h => new DocxTableCell({ children: [new Paragraph(h)] })),
      });
      const delivRows = allDeliverables.map(deliv => new DocxTableRow({
        children: [
          new DocxTableCell({ children: [new Paragraph(String(deliv.displayCode ?? deliv.code ?? '—'))] }),
          new DocxTableCell({ children: [new Paragraph(deliv.title || '—')] }),
          new DocxTableCell({ children: [new Paragraph(String(deliv.wp_number ?? '—'))] }),
          new DocxTableCell({ children: [new Paragraph(deliv.type || '—')] }),
          new DocxTableCell({ children: [new Paragraph(deliv.dissemination_level || '—')] }),
          new DocxTableCell({ children: [new Paragraph(deliv.delivery_month ? `M${deliv.delivery_month}` : '—')] }),
        ],
      }));
      children.push(new DocxTable({ rows: [delivHeaderRow, ...delivRows], width: { size: 100, type: WidthType.PERCENTAGE } }));
    }

    // 8. LIST OF MILESTONES
    if (allMilestones.length > 0) {
      children.push(
        new Paragraph({ text: tableLabels.milestones || 'List of Milestones', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } })
      );
      const msHeaderRow = new DocxTableRow({
        children: ['Milestone number', 'Milestone name', 'Related WP(s)', 'Due month', 'Means of verification']
          .map(h => new DocxTableCell({ children: [new Paragraph(h)] })),
      });
      const msRows = allMilestones.map(ms => new DocxTableRow({
        children: [
          new DocxTableCell({ children: [new Paragraph(String(ms.displayCode ?? ms.code ?? '—'))] }),
          new DocxTableCell({ children: [new Paragraph(ms.description || '—')] }),
          new DocxTableCell({ children: [new Paragraph(String(ms.wp_number ?? '—'))] }),
          new DocxTableCell({ children: [new Paragraph(ms.due_month ? `M${ms.due_month}` : '—')] }),
          new DocxTableCell({ children: [new Paragraph(ms.verification_means || '—')] }),
        ],
      }));
      children.push(new DocxTable({ rows: [msHeaderRow, ...msRows], width: { size: 100, type: WidthType.PERCENTAGE } }));
    }

    // 9. CRITICAL RISKS
    if (allRisks.length > 0) {
      children.push(
        new Paragraph({ text: tableLabels.risks || 'Critical Risks for Implementation', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } })
      );
      const riskHeaderRow = new DocxTableRow({
        children: ['Number', 'Description of risk', 'WP(s) involved', 'Mitigation measures']
          .map(h => new DocxTableCell({ children: [new Paragraph(h)] })),
      });
      const riskRows = allRisks.map(risk => new DocxTableRow({
        children: [
          new DocxTableCell({ children: [new Paragraph(String(risk.displayCode ?? '—'))] }),
          new DocxTableCell({ children: [new Paragraph(`${risk.description || '—'} (Likelihood: ${risk.likelihood || 'n/a'}, Severity: ${risk.impact || 'n/a'})`)] }),
          new DocxTableCell({ children: [new Paragraph(String(risk.wp_number ?? '—'))] }),
          new DocxTableCell({ children: [new Paragraph(risk.mitigation_plan || '—')] }),
        ],
      }));
      children.push(new DocxTable({ rows: [riskHeaderRow, ...riskRows], width: { size: 100, type: WidthType.PERCENTAGE } }));
    }

    // 10. PROJECT TIMELINE (GANTT — bar chart via cell shading)
    {
      const ganttTasks = (selectedProposal.tasks || [])
        .filter(t => t.start_month != null && t.duration_months != null)
        .map(t => ({ ...t, end_month: t.start_month + t.duration_months - 1 }));

      if (ganttTasks.length > 0) {
        const totalMonths = selectedProposal.duration_months ||
          Math.max(...ganttTasks.map(t => t.end_month), 12);

        const DOCX_WP_PALETTE = [
          { dark: '1565C0', light: 'BBDEFB' },
          { dark: '2E7D32', light: 'C8E6C9' },
          { dark: 'C84B00', light: 'FFE0B2' },
          { dark: '6A1B9A', light: 'E1BEE7' },
          { dark: 'B71C1C', light: 'FFCDD2' },
          { dark: '00695C', light: 'B2DFDB' },
          { dark: '283593', light: 'C5CAE9' },
          { dark: '4E342E', light: 'D7CCC8' },
        ];

        const wps = (selectedProposal.work_packages || [])
          .slice().sort((a, b) => (a.number || 0) - (b.number || 0));
        const tasksByWP = {};
        ganttTasks.forEach(t => {
          const key = t.work_package || '__none__';
          if (!tasksByWP[key]) tasksByWP[key] = [];
          tasksByWP[key].push(t);
        });

        const colWidth = Math.floor(75 / totalMonths);

        children.push(
          new Paragraph({ text: 'Project Timeline', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } })
        );

        // Month header row — numbers only
        const headerCells = [
          new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: 'Task', bold: true, size: 16 })] })],
            width: { size: 25, type: WidthType.PERCENTAGE },
            shading: { fill: 'F5F5F5', color: 'F5F5F5', type: ShadingType.SOLID },
          }),
        ];
        for (let m = 1; m <= totalMonths; m++) {
          headerCells.push(new DocxTableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(m), size: 12 })], alignment: AlignmentType.CENTER })],
            width: { size: colWidth, type: WidthType.PERCENTAGE },
            shading: { fill: 'F5F5F5', color: 'F5F5F5', type: ShadingType.SOLID },
          }));
        }
        const rows = [new DocxTableRow({ children: headerCells })];

        wps.forEach((wp, wpIdx) => {
          const wpTasks = (tasksByWP[wp.id] || []).slice().sort((a, b) => a.start_month - b.start_month);
          if (wpTasks.length === 0) return;
          const palette = DOCX_WP_PALETTE[wpIdx % DOCX_WP_PALETTE.length];
          const wpStart = Math.min(...wpTasks.map(t => t.start_month));
          const wpEnd   = Math.max(...wpTasks.map(t => t.end_month));

          // WP header row — dark shading across active months
          const wpCells = [
            new DocxTableCell({
              children: [new Paragraph({ children: [new TextRun({ text: `WP${wp.number}: ${wp.title || ''}`, bold: true, size: 16, color: palette.dark })] })],
              width: { size: 25, type: WidthType.PERCENTAGE },
            }),
          ];
          for (let m = 1; m <= totalMonths; m++) {
            const active = m >= wpStart && m <= wpEnd;
            wpCells.push(new DocxTableCell({
              children: [new Paragraph('')],
              width: { size: colWidth, type: WidthType.PERCENTAGE },
              shading: { fill: active ? palette.dark : 'FFFFFF', color: active ? palette.dark : 'FFFFFF', type: ShadingType.SOLID },
            }));
          }
          rows.push(new DocxTableRow({ children: wpCells }));

          // Task rows — light shading
          wpTasks.forEach(task => {
            const taskCells = [
              new DocxTableCell({
                children: [new Paragraph({ children: [new TextRun({ text: `  ${task.full_title || task.title || '—'}`, size: 15 })] })],
                width: { size: 25, type: WidthType.PERCENTAGE },
              }),
            ];
            for (let m = 1; m <= totalMonths; m++) {
              const active = m >= task.start_month && m <= task.end_month;
              taskCells.push(new DocxTableCell({
                children: [new Paragraph('')],
                width: { size: colWidth, type: WidthType.PERCENTAGE },
                shading: { fill: active ? palette.light : 'FFFFFF', color: active ? palette.light : 'FFFFFF', type: ShadingType.SOLID },
              }));
            }
            rows.push(new DocxTableRow({ children: taskCells }));
          });
        });

        children.push(new DocxTable({ rows, width: { size: 100, type: WidthType.PERCENTAGE } }));
      }
    }

    // 11. STAFF EFFORT SUMMARY
    if (selectedProposal.work_packages && selectedProposal.work_packages.length > 0 &&
        selectedProposal.consortium_members && selectedProposal.consortium_members.length > 0) {

      children.push(
        new Paragraph({
          text: tableLabels.staffEffort || "Summary of Staff Effort",
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 400, after: 200 },
        })
      );

      // Create table header
      const headerCells = [
        new DocxTableCell({ 
          children: [new Paragraph("Participant")],
          width: { size: 20, type: WidthType.PERCENTAGE }
        })
      ];

      // Add WP headers
      selectedProposal.work_packages.forEach(wp => {
        headerCells.push(
          new DocxTableCell({ 
            children: [new Paragraph(`WP${wp.number.toString()}`)],
            width: { size: 8, type: WidthType.PERCENTAGE }
          })
        );
      });

      headerCells.push(
        new DocxTableCell({ 
          children: [new Paragraph("Total")],
          width: { size: 10, type: WidthType.PERCENTAGE }
        })
      );

      const headerRow = new DocxTableRow({
        children: headerCells,
      });

      // Create data rows
      const dataRows = selectedProposal.consortium_members.map(member => {
        const cells = [
          new DocxTableCell({ 
            children: [new Paragraph(`${member.number || ''} / ${member.name || ''}`)]
          })
        ];

        // Add effort per WP
        let total = 0;
        selectedProposal.work_packages.forEach(wp => {
          const effort = member.effort_by_wp?.[wp.number] || 0;
          cells.push(
            new DocxTableCell({ 
              children: [new Paragraph(effort.toString())]
            })
          );
          total += effort;
        });

        // Add total
        cells.push(
          new DocxTableCell({ 
            children: [new Paragraph(total.toString())]
          })
        );

        return new DocxTableRow({ children: cells });
      });

      children.push(
        new DocxTable({
          rows: [headerRow, ...dataRows],
          width: { size: 100, type: WidthType.PERCENTAGE },
        })
      );
    }

    // 12. PURCHASE COSTS (EQUIPMENT)
    if (selectedProposal.tasks && selectedProposal.tasks.some(t => t.budget_items?.some(bi => parseFloat(bi.equipment || 0) > 0))) {
      const equipmentByOrg = {};
      selectedProposal.tasks.forEach(task => {
        task.budget_items?.forEach(item => {
          if (parseFloat(item.equipment || 0) > 0) {
            const orgName = item.organization_name || 'Unknown';
            if (!equipmentByOrg[orgName]) equipmentByOrg[orgName] = { cost: 0, justifications: [] };
            equipmentByOrg[orgName].cost += parseFloat(item.equipment);
            if (item.equipment_justification) equipmentByOrg[orgName].justifications.push(item.equipment_justification);
          }
        });
      });

      children.push(
        new Paragraph({ text: tableLabels.equipment || 'Purchase Costs (Equipment)', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } })
      );
      const equipHeaderRow = new DocxTableRow({
        children: ['Participant', 'Cost (€)', 'Justification'].map(h => new DocxTableCell({ children: [new Paragraph(h)] })),
      });
      const equipRows = Object.entries(equipmentByOrg).map(([orgName, data]) => new DocxTableRow({
        children: [
          new DocxTableCell({ children: [new Paragraph(orgName)] }),
          new DocxTableCell({ children: [new Paragraph(`€${data.cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`)] }),
          new DocxTableCell({ children: [new Paragraph(data.justifications.length > 0 ? data.justifications.join('; ') : '—')] }),
        ],
      }));
      children.push(new DocxTable({ rows: [equipHeaderRow, ...equipRows], width: { size: 100, type: WidthType.PERCENTAGE } }));
    }

    // 13. APPENDIX — matches Preview tab: subsections + selected member resources
    {
      const appendixSection = (selectedProposal.sections || []).find(s => s.section_key === 'appendix');
      const appendixSubs = (appendixSection?.subsections || [])
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

      const appendixResTypes = [
        { key: 'publications', label: 'Publications', getTitle: r => r.title || '—', getSub: r => [r.journal, r.year].filter(Boolean).join(', ') },
        { key: 'patents',      label: 'Patents',      getTitle: r => r.title || '—', getSub: r => [r.patent_number, r.status].filter(Boolean).join(', ') },
        { key: 'projects',     label: 'Projects',     getTitle: r => r.title || '—', getSub: r => [r.role, r.funding_agency].filter(Boolean).join(', ') },
        { key: 'networks',     label: 'Networks',     getTitle: r => r.name  || '—', getSub: r => [r.domain, r.role].filter(Boolean).join(', ') },
      ];
      const hasSelectedResources = appendixResTypes.some(({ key }) =>
        (memberResources?.[key] || []).some(r => memberResourcesSelected[key]?.has(String(r.id)))
      );

      if (!isPublication && (appendixSubs.length > 0 || hasSelectedResources)) {
        children.push(
          new Paragraph({ text: 'Appendix', heading: HeadingLevel.HEADING_1, spacing: { before: 400, after: 200 } })
        );
        let appendixCounter = 0;

        appendixSubs.forEach((sub) => {
          appendixCounter++;
          children.push(
            new Paragraph({ text: `A.${appendixCounter}. ${sub.title}`, heading: HeadingLevel.HEADING_2, spacing: { before: 250, after: 100 } })
          );
          if (sub.current_content?.body) {
            children.push(...markdownToDocxParagraphs(styledBody(sub), { figureCounter: docFigures, figureImages: docImages }));
          }
          (sub.subsections || []).forEach((s2, i2) => {
            children.push(
              new Paragraph({ text: `A.${appendixCounter}.${i2 + 1}. ${s2.title}`, heading: HeadingLevel.HEADING_3, spacing: { before: 150, after: 75 } })
            );
            if (s2.current_content?.body) {
              children.push(...markdownToDocxParagraphs(styledBody(s2), { figureCounter: docFigures, figureImages: docImages }));
            }
          });
        });

        appendixResTypes.forEach(({ key, label, getTitle, getSub }) => {
          const items = (memberResources?.[key] || []).filter(r => memberResourcesSelected[key]?.has(String(r.id)));
          if (!items.length) return;
          appendixCounter++;
          children.push(
            new Paragraph({ text: `A.${appendixCounter}. ${label}`, heading: HeadingLevel.HEADING_2, spacing: { before: 250, after: 100 } })
          );
          items.forEach(r => {
            children.push(
              new Paragraph({ children: [new TextRun({ text: getTitle(r), bold: true, size: 20 })], spacing: { before: 80 } }),
            );
            const sub = getSub(r);
            if (sub) {
              children.push(new Paragraph({ children: [new TextRun({ text: sub, italics: true, size: 18, color: '555555' })], spacing: { after: 60 } }));
            }
          });
        });
      }
    }

    // 14. CREATE AND DOWNLOAD DOCUMENT
    const doc = new Document({
      sections: [{
        properties: {},
        children: children,
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${selectedProposal.acronym || 'proposal'}_export.docx`);

  } catch (error) {
    console.error('Error generating Word document:', error);
    alert('Failed to generate document. Please try again.');
  }
};

const handleDownloadWarnings = async () => {
  try {
    const { items, grouped } = computeWarnings(selectedProposal);
    const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const errorCount   = items.filter(i => i.severity === 'error').length;
    const warningCount = items.filter(i => i.severity === 'warning').length;
    const infoCount    = items.filter(i => i.severity === 'info').length;

    const children = [];

    // Title
    children.push(
      new Paragraph({
        children: [new TextRun({ text: 'Proposal Completeness Checklist', bold: true, size: 36, color: '1F4E79' })],
        spacing: { after: 120 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: `${selectedProposal.title || selectedProposal.acronym || 'Proposal'}`, size: 24, color: '444444' }),
        ],
        spacing: { after: 60 },
      }),
      new Paragraph({
        children: [new TextRun({ text: `Generated: ${dateStr}`, size: 20, italics: true, color: '888888' })],
        spacing: { after: 240 },
      }),
    );

    // Summary row
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Summary:  ', bold: true, size: 22 }),
          new TextRun({ text: `${errorCount} Missing   `, bold: true, color: 'C00000', size: 22 }),
          new TextRun({ text: `${warningCount} Incomplete   `, bold: true, color: 'C55A00', size: 22 }),
          new TextRun({ text: `${infoCount} Suggested`, bold: true, color: '1F4E79', size: 22 }),
        ],
        spacing: { after: 60 },
      }),
      new Paragraph({
        children: [new TextRun({ text: `${items.length} total items to review`, size: 20, color: '666666' })],
        spacing: { after: 300 },
      }),
    );

    // Instructions line
    children.push(
      new Paragraph({
        children: [new TextRun({ text: '☐ = To do   ☑ = Done  —  Check off items as you complete them.', size: 18, italics: true, color: '666666' })],
        spacing: { after: 280 },
      }),
    );

    const SEVERITY_LABELS = { error: '[MISSING]', warning: '[INCOMPLETE]', info: '[SUGGESTED]' };
    const SEVERITY_COLORS  = { error: 'C00000',   warning: 'C55A00',       info: '1F4E79' };

    // One section per category
    Object.entries(grouped).forEach(([category, catItems]) => {
      const catErrors   = catItems.filter(i => i.severity === 'error').length;
      const catWarnings = catItems.filter(i => i.severity === 'warning').length;
      const catInfo     = catItems.filter(i => i.severity === 'info').length;
      const badges = [
        catErrors   ? `${catErrors} missing`     : null,
        catWarnings ? `${catWarnings} incomplete` : null,
        catInfo     ? `${catInfo} suggested`      : null,
      ].filter(Boolean).join('  ·  ');

      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: category, bold: true, size: 26, color: '1F4E79' }),
            new TextRun({ text: `   ${badges}`, size: 18, color: '888888' }),
          ],
          spacing: { before: 280, after: 100 },
          border: { bottom: { style: 'single', size: 6, color: 'C7D8E8' } },
        }),
      );

      catItems.forEach(item => {
        const sev = SEVERITY_LABELS[item.severity] || '';
        const col = SEVERITY_COLORS[item.severity] || '444444';
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '☐  ', size: 22, color: '444444' }),
              new TextRun({ text: `${sev}  `, bold: true, size: 20, color: col }),
              new TextRun({ text: item.message, size: 20, color: '222222' }),
            ],
            spacing: { after: 80 },
            indent: { left: 360 },
          }),
        );
      });
    });

    if (items.length === 0) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: '✓ No issues found — the proposal appears complete!', bold: true, color: '276221', size: 24 })],
          spacing: { after: 200 },
        }),
      );
    }

    const doc = new Document({ sections: [{ properties: {}, children }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${selectedProposal.acronym || 'proposal'}_checklist.docx`);
  } catch (err) {
    console.error('Error generating checklist:', err);
    alert('Failed to generate checklist document.');
  }
};



  // const loadProposals = async () => {
  //   setIsLoading(true);
  //   const data = await fetchProposals();
  //   setProposals(data);
  //   if (data.length > 0) {
  //     setSelectedProposal(data[0]);
  //   }
  //   setIsLoading(false);
  // };

  const loadProposalDetails = async (id) => {
    setIsLoading(true);
    const data = await fetchProposal(id);
    if (data) {
      setSelectedProposal(data);
    }
    setIsLoading(false);
  };

  const reloadProposalSilently = async (id) => {
    const data = await fetchProposal(id);
    if (data) setSelectedProposal(data);
  };

  const loadStatistics = async (id) => {
    const stats = await fetchProposalStatistics(id);
    if (stats) {
      setStatistics(stats);
    }
  };

  const handleSaveContent = async () => {
    if (!editingSection) return;
    
    setIsSaving(true);
    setError(null);
    
    const result = await saveSectionContent(editingSection.id, {
      body: content,
      language: 'en',
      assignees: assignees.map(a => a.id),
      // The version this edit started from: lets the server merge simultaneous saves
      base_version: editBaseVersion,
    });
    
    if (result.success) {
      const saved = result.data || {};
      if (saved.conflict) {
        // Both versions were kept; let the writer decide which one becomes current
        setSaveConflict({ ...saved, section: editingSection });
      } else if (saved.merged) {
        const who = saved.merged_with?.length ? saved.merged_with.join(', ') : 'another save';
        showSnackbar(`Saved and merged with simultaneous changes by ${who}.`, 'success');
      }
      clearDraft(draftKey('section', editingSection.id));
      await loadProposalDetails(selectedProposal.id);
      await loadStatistics(selectedProposal.id);
      setEditingSection(null);
      setContent('');
      setAssignees([]);
    } else {
      setError(result.error);
    }
    
    setIsSaving(false);
  };

  const startEditing = async (section) => {
    if (lockingEnabled) {
      const lockResult = await lockSection(section.id);
      if (!lockResult.success) {
        if (lockResult.conflict) {
          setError(`This section is currently being edited by ${lockResult.lockedByName}. Please try again later.`);
        } else {
          setError('Could not acquire edit lock. Please try again.');
        }
        return;
      }
    }

    const baseVersion = section.current_content?.version ?? 0;   // 0 = the section was empty
    const draft = readDraft(draftKey('section', section.id));
    const body = draft && draft.base === baseVersion ? draft.body : (section.current_content?.body || '');
    setEditingSection(section);
    setContent(body);
    setEditBaseVersion(baseVersion);
    autosavedRef.current = section.current_content?.body || '';
    setAutosaveStatus(null);
    loadSectionComments(section.id, setSectionComments);

    const assigneeNames = section.current_content?.assignee_names || [];
    if (assigneeNames.length > 0 && consortiumUsers.length > 0) {
      const assigneeObjects = assigneeNames
        .map(assigneeName => consortiumUsers.find(u => u && u.id === assigneeName.id))
        .filter(u => u);
      setAssignees(assigneeObjects);
    } else {
      setAssignees([]);
    }

    setError(null);
  };

  const cancelEditing = async () => {
    if (editingSection) clearDraft(draftKey('section', editingSection.id));
    if (editingSection && lockingEnabled) {
      await unlockSection(editingSection.id);
    }
    setEditingSection(null);
    setContent('');
    setAssignees([]);
    setError(null);
  };

  // ── Autosave: once a minute while text changed, so co-authors see it. Autosaves never notify
  // anyone (the server only notifies on a deliberate Save) and update one version in place.
  const autosavedRef = useRef('');            // section body as last stored on the server
  const [autosaveStatus, setAutosaveStatus] = useState(null);   // {at} | {paused: true}
  const autosaveLive = useRef({});
  autosaveLive.current = { editingSection, content, editBaseVersion, isSaving };
  useEffect(() => {
    if (!editingSection?.id) return undefined;
    const timer = setInterval(async () => {
      const { editingSection: sec, content: body, editBaseVersion: base, isSaving: busy } = autosaveLive.current;
      if (!sec || busy || body === autosavedRef.current) return;
      const result = await saveSectionContent(sec.id, { body, language: 'en', base_version: base, auto: true });
      if (!result.success || autosaveLive.current.editingSection?.id !== sec.id) return;
      const saved = result.data || {};
      if (saved.autosave_skipped) { setAutosaveStatus({ paused: true }); return; }
      if (saved.merged && autosaveLive.current.content !== body) return;   // typed meanwhile: merge again next minute
      autosavedRef.current = saved.body ?? body;
      setEditBaseVersion(saved.version);
      writeDraft(draftKey('section', sec.id), { base: saved.version, body: saved.body ?? body });
      if (saved.merged) {                       // a co-author's simultaneous changes were merged in
        setContent(saved.body);
        setSectionEditorResetKey((k) => k + 1);
      }
      setAutosaveStatus({ at: new Date() });
    }, AUTOSAVE_MS);
    return () => clearInterval(timer);
  }, [editingSection?.id]);

  // Keep the typed text locally until it's stored, so a refresh doesn't lose it
  useEffect(() => {
    if (!editingSection?.id) return undefined;
    const t = setTimeout(() => writeDraft(draftKey('section', editingSection.id), { base: editBaseVersion, body: content }), 800);
    return () => clearTimeout(t);
  }, [content, editingSection?.id, editBaseVersion]);

  // Mirror the open tab / section / full screen in the URL (replace, so Back still leaves the writer)
  useEffect(() => {
    if (!selectedProposal?.id) return;
    const params = new URLSearchParams(window.location.search);
    params.set('tab', TAB_KEYS[activeTab] || 'abstract');
    if (editingSection?.id && activeTab === 1) params.set('section', editingSection.id); else params.delete('section');
    if (editingSection?.id && activeTab === 1 && sectionEditorFullScreen) params.set('full', '1'); else params.delete('full');
    const search = `?${params.toString()}`;
    if (search !== window.location.search) navigate({ search }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProposal?.id, activeTab, editingSection?.id, sectionEditorFullScreen]);

  // After a refresh, reopen the section that was being edited
  const restoredSectionRef = useRef(false);
  useEffect(() => {
    if (restoredSectionRef.current || !selectedProposal?.sections) return;
    restoredSectionRef.current = true;
    const id = initialUrlState.get('section');
    if (!id) return;
    const find = (list) => {
      for (const sec of list || []) {
        if (String(sec.id) === id) return sec;
        const hit = find(sec.subsections);
        if (hit) return hit;
      }
      return null;
    };
    const sec = find(selectedProposal.sections);
    if (sec) {
      startEditing(sec);
      if (initialUrlState.get('full') === '1') setSectionEditorFullScreen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProposal?.sections]);

  // Dialog handlers
  const [subsectionParentId, setSubsectionParentId] = useState(null);

  const handleAddSection = () => {
    setEditingDialogSection(null);
    setSubsectionParentId(null);
    setSectionDialogOpen(true);
  };

  const handleAddSubsection = (parentSection) => {
    setEditingDialogSection(null);
    setSubsectionParentId(parentSection.id);
    setSectionDialogOpen(true);
  };

  const handleEditSection = (section) => {
    setEditingDialogSection(section);
    setSubsectionParentId(null);
    setSectionDialogOpen(true);
  };

  const handleSectionDialogSuccess = async () => {
    const freshData = await fetchProposal(selectedProposal.id);
    if (freshData) {
      setSelectedProposal(freshData);
      const filteredSections = (freshData.sections || []).filter(
        s => s.section_key !== 'abstract' && s.section_key !== 'appendix'
      );
      const tree = cloneSectionTree(filteredSections);
      const updates = computeTreeUpdates(tree);
      if (updates.length > 0) {
        try {
          await axiosService.post('/proposal/sections/reorder/', { updates });
          const reorderedData = await fetchProposal(selectedProposal.id);
          if (reorderedData) setSelectedProposal(reorderedData);
        } catch (_) {}
      }
    }
    loadStatistics(selectedProposal.id);
  };

  const handleAddWorkPackage = () => {
    setEditingWorkPackage(null);
    setWorkPackageDialogOpen(true);
  };

  const handleEditWorkPackage = (wp) => {
    setEditingWorkPackage(wp);
    setWorkPackageDialogOpen(true);
  };

  const handleWorkPackageDialogSuccess = () => {
    loadProposalDetails(selectedProposal.id);
    loadStatistics(selectedProposal.id);
  };

  const [importingExcel, setImportingExcel] = useState(false);

  const handleDownloadExcelTemplate = async () => {
    try {
      const response = await axiosService.get(
        `/proposal/proposals/${selectedProposal.id}/excel_template/`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `${selectedProposal.acronym}_work_plan_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template.');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportingExcel(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axiosService.post(
        `/proposal/proposals/${selectedProposal.id}/import_excel/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { created, updated, errors: importErrors } = res.data;
      const summary = Object.entries(created)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${v} ${k} created`)
        .concat(Object.entries(updated).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k} updated`))
        .join(', ');
      if (importErrors?.length) {
        setError(`Import completed with warnings: ${importErrors.slice(0, 3).join('; ')}${importErrors.length > 3 ? ` (+${importErrors.length - 3} more)` : ''}`);
      }
      if (summary) {
        showSnackbar(`Import successful: ${summary}`);
      }
      await loadProposalDetails(selectedProposal.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed. Check the file format and try again.');
    } finally {
      setImportingExcel(false);
    }
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setTaskDialogOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTaskDialogOpen(true);
  };

  const handleTaskDialogSuccess = () => {
    loadProposalDetails(selectedProposal.id);
    loadStatistics(selectedProposal.id);
  };

  const getTaskAssignees = (task) => {
    if (!task?.assignee_names?.length) return [];
    return task.assignee_names.map(a => consortiumUsers.find(u => u.id === a.id) || a);
  };

  /**
   * Returns true when the current user is allowed to edit assignees on a task:
   *   - site admin / superuser
   *   - consortium coordinator
   *   - WP leader of the task's work package
   */
  const canEditAssignees = (task) => {
    if (!task) return true; // new task — no restriction yet
    if (currentUser?.is_superuser || currentUser?.is_staff) return true;
    // Coordinator check
    const coordinatorId = consortium?.coordinator_member?.member?.id
      ?? consortium?.coordinator_member?.member
      ?? null;
    if (coordinatorId && String(coordinatorId) === String(currentUser?.id)) return true;
    // WP leader check
    const wp = selectedProposal?.work_packages?.find(w => w.id === task.work_package);
    if (wp && String(wp.wp_leader) === String(currentUser?.id)) return true;
    return false;
  };



  const th = {
      fontWeight: 600,
      backgroundColor: '#f5f5f5',
      whiteSpace: 'nowrap',
      fontSize: '0.9rem'
};


const [leadersById, setLeadersById] = useState({});


const allBudgetItems = selectedProposal?.tasks
  ?.flatMap((task) => task.budget_items || [])
  .filter((item) => item.organization_name);

// Group by organization
const byOrganization = allBudgetItems?.reduce((acc, item) => {
const orgName = item.organization_name || "Unknown Organization";
acc[orgName] = acc[orgName] || [];
acc[orgName].push(item);
return acc;
}, {});

const currency = (value) =>
`€${Number(value).toLocaleString("en-US", {
  minimumFractionDigits: 2,
})}`;




// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  selectedProposal?.work_packages?.forEach(wp => {
    const id = wp.wp_leader;
    if (!id || leadersById[id]) return;

    fetchUserById(id).then(user => {
      setLeadersById(prev => ({ ...prev, [id]: user }));
    });
  });
}, [selectedProposal?.work_packages]);

// const updateWPLeader = async (workPackage) => {
//   const leaderId = workPackage.wp_leader;
//   const user = await fetchUserById(leaderId);
//   setWPLeader(user);
// };


  const handleDeleteSection = async (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      const result = await deleteSection(sectionId);
      if (result.success) {
        await loadProposalDetails(selectedProposal.id);
        await loadStatistics(selectedProposal.id);
        refreshFigures();
      } else {
        setError(result.error);
      }
    }
  };

  const handleDeleteWorkPackage = async (workPackageId) => {
    if (window.confirm('Are you sure you want to delete this work package? All associated tasks will also be deleted.')) {
      const result = await deleteWorkPackage(workPackageId);
      if (result.success) {
        await loadProposalDetails(selectedProposal.id);
        await loadStatistics(selectedProposal.id);
      } else {
        setError(result.error);
      }
    }
  };

  const handleDeleteTask = async (e, taskId) => {
    e.stopPropagation();
    if (window.confirm('Delete this task? All associated objectives, deliverables, risks, and budget items will also be deleted.')) {
      const result = await deleteTask(taskId);
      if (result.success) {
        await loadProposalDetails(selectedProposal.id);
      } else {
        setError(result.error);
      }
    }
  };



  const handleSaveAsTemplate = async () => {
    if (!saveTemplateName.trim() || !selectedProposal?.id) return;
    setSaveTemplateSaving(true);
    try {
      await axiosService.post(`/proposal/proposals/${selectedProposal.id}/save_as_template/`, {
        name: saveTemplateName.trim(),
        description: saveTemplateDesc.trim(),
      });
      setSaveTemplateOpen(false);
      setSaveTemplateName('');
      setSaveTemplateDesc('');
      showSnackbar('Template saved successfully.', 'success');
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Failed to save template.', 'error');
    } finally {
      setSaveTemplateSaving(false);
    }
  };

  const handleSaveAbstract = async () => {
    if (!selectedProposal?.id) return;
    setIsSaving(true);
    setError(null);
    try {
      await axiosService.patch(`/proposal/proposals/${selectedProposal.id}/`, { abstract: abstractText, keywords });
      if (abstractSection?.id) {
        const res = await saveSectionContent(abstractSection.id, { body: abstractText, language: 'en', assignees: [] });
        if (res.success && res.data?.version) abstractBaseRef.current = res.data.version;
      }
      abstractSavedRef.current = abstractText;
      clearDraft(draftKey('abstract', selectedProposal.id));
      await loadProposalDetails(selectedProposal.id);
    } catch (err) {
      console.error('Error saving abstract:', err);
      setError('Failed to save abstract. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleShowVersions = (section) => {
    setVersionsSectionId(section.id);
    setVersionsSectionTitle(section.title);
    setVersionsDialogOpen(true);
  };

  // Preview: a right-click on a section (or the abstract, the authors) opens it for editing
  const previewEditable = (target) => ({
    className: 'preview-editable',
    onContextMenu: (e) => { e.preventDefault(); e.stopPropagation(); setPreviewMenu({ x: e.clientX, y: e.clientY, ...target }); },
  });
  const previewEditSx = {
    borderRadius: 1, cursor: 'context-menu', outline: '1px dashed transparent', outlineOffset: 4,
    '&:hover:not(:has(.preview-editable:hover))': { outlineColor: 'primary.light' },
  };
  const editFromPreview = async (section, full = false) => {
    setPreviewMenu(null);
    if (!editingSection || String(editingSection.id) !== String(section.id)) {
      // the open section's draft stays in sessionStorage (restored when it is opened again)
      if (editingSection && lockingEnabled) await unlockSection(editingSection.id);
      await startEditing(section);
    }
    setActiveTab(1);
    setSectionEditorFullScreen(Boolean(full));
  };

  const handleVersionRestore = () => {
    loadProposalDetails(selectedProposal.id);
    loadStatistics(selectedProposal.id);
  };


  // Counts the accepted text: tracked deletions and change markers are ignored
  const getWordCount = (text) => {
    return text ? markdownToPlainText(text).split(/\s+/).filter(word => word.length > 0).length : 0;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      in_progress: 'primary',
      submitted: 'success',
      in_review: 'warning',
      pending: 'default',
      completed: 'success',
      accepted: 'success',
      rejected: 'error',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography color="text.secondary">Loading proposals...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!selectedProposal) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" gutterBottom>No proposals available</Typography>
        <Typography color="text.secondary">Create a new proposal to get started.</Typography>
      </Container>
    );
  }

  // console.log('##### Selected Proposal:', selectedProposal);

  return (
    <Box sx={{  minHeight: '100vh' }}>
      {/* Header */}


<Paper sx={{ borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider', pt: 3 }}>
  <Container maxWidth="xl" sx={{ pb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>

      {/* Left: Back + Title + context chips */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
        <BackButton position="inline" />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="p" sx={{ fontSize: '1.75rem !important', lineHeight: 1.4, fontWeight: 200 }}>
            {selectedProposal.acronym && (
              <Box component="span" sx={{ fontWeight: 500, mr: 1, fontSize: '1.55rem',  }}>
                {selectedProposal.acronym}
              </Box>
            )}
            {selectedProposal.title}
            {/* <Tooltip title="This tool is in active development. Features may change and some functionality may be incomplete.">
              <Chip
                label="BETA"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 18, borderRadius: 1, cursor: 'default', verticalAlign: 'middle', ml: 1 }}
              />
            </Tooltip> */}
          </Typography>
          {/* Consortium + Call chips */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
            {consortium && (
              <Tooltip arrow title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} lineHeight={1.2} gutterBottom>{consortium.name}</Typography>
                  {consortium.summary && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{consortium.summary.substring(0, 150)}{consortium.summary.length > 150 ? '…' : ''}</Typography>}
                  {consortium.member_count !== undefined && <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>Members: {consortium.member_count}</Typography>}
                </Box>
              }>
                <Chip icon={<GroupsIcon />} label={consortium.name} variant="filled"
                  component={RouterLink} to={`/consortium/${consortium.id}/`} clickable
                  sx={{ fontSize: '1rem', height: 22, '& .MuiChip-icon': { fontSize: 13 }, textDecoration: 'none' }} />
              </Tooltip>
            )}
            {isPublication && (
              <Chip label={publicationTypeLabel(selectedProposal.publication_type)} variant="outlined"
                sx={{ fontSize: '1rem', height: 22 }} />
            )}
            {isPublication && selectedProposal.target_journal && (
              <Chip label={selectedProposal.target_journal} variant="outlined" color="primary"
                sx={{ fontSize: '1rem', height: 22 }} />
            )}
            {isPublication && journalInfo?.documentation_url && (
              <Chip label="Author guidelines" variant="outlined" clickable component="a"
                href={journalInfo.documentation_url} target="_blank" rel="noopener noreferrer"
                sx={{ fontSize: '1rem', height: 22, textDecoration: 'none' }} />
            )}
            {isPublication && consortium?.collaboration?.end_date && (
              <Chip icon={<DeadlineIcon />} variant="outlined"
                color={new Date(consortium.collaboration.end_date) < new Date() ? 'error' : 'default'}
                label={`Deadline ${new Date(consortium.collaboration.end_date).toLocaleDateString()}`}
                sx={{ fontSize: '1rem', height: 22, '& .MuiChip-icon': { fontSize: 13 } }} />
            )}
            {selectedProposal.call && (
              <Tooltip arrow title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} lineHeight={1.2} gutterBottom>{consortium?.funding_application?.call_details?.title || selectedProposal.call_title || 'Funding Call'}</Typography>
                  {consortium?.funding_application?.call_details?.code && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Code: {consortium.funding_application.call_details.code}</Typography>}
                  {consortium?.funding_application?.call_details?.type_of_action && <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>Type: {consortium.funding_application.call_details.type_of_action}</Typography>}
                  {consortium?.funding_application?.call_details?.closes_at && <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>Deadline: {new Date(consortium.funding_application.call_details.closes_at).toLocaleDateString()}</Typography>}
                </Box>
              }>
                <Chip icon={<CampaignIcon />} label={consortium?.funding_application?.call_details?.code || selectedProposal.call_title || 'Funding Call'}
                 variant="filled" component={RouterLink} to={`/funding/call/${selectedProposal.call}/`} clickable
                  sx={{ fontSize: '1rem', height: 22, '& .MuiChip-icon': { fontSize: 13 }, textDecoration: 'none' }} />
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>



      {/* Right: stats + chat + three-dots menu */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
        {/* WPs / Words / Budget */}
        <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {statistics && (isPublication ? [
              { label: 'Sections', value: (selectedProposal.sections || []).filter(s => s.section_key !== 'abstract' && s.section_key !== 'appendix').length },
              { label: 'Words', value: `${(statistics.total_words / 1000).toFixed(1)}K` },
              { label: 'Refs', value: (selectedProposal.references || []).length },
            ] : [
              { label: 'WPs', value: statistics.work_packages },
              { label: 'Words', value: `${(statistics.total_words / 1000).toFixed(1)}K` },
              { label: 'Budget', value: selectedProposal.total_budget ? `€${(parseFloat(selectedProposal.total_budget) / 1000).toFixed(0)}K` : '—' },
            ]).map(({ label, value }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.25 }} />}
                <Box sx={{ textAlign: 'center', minWidth: 52 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                    {label}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                    {value}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
          <Divider flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              label={selectedProposal.status.replace(/_/g, ' ')}
              color={getStatusColor(selectedProposal.status)}
              size="small"
              sx={{ fontWeight: 600, textTransform: 'capitalize', fontSize: '1rem', height: 22 }}
            />
            <Tooltip title={isCoordinator ? 'View or edit proposal version' : 'View proposal version'}>
              <Chip
                label={`v${selectedProposal.current_version}`}
                variant="outlined"
                size="small"
                onClick={handleOpenVersionDialog}
                sx={{ fontSize: '1rem', height: 22, cursor: 'pointer' }}
              />
            </Tooltip>
            <Tooltip title={`Private to your team: only its members can read this ${isPublication ? 'publication' : 'proposal'}, and Otable staff cannot open it. The text is stored encrypted. Click to see when it left the team.`}>
              <Chip
                icon={<LockIcon sx={{ fontSize: '13px !important' }} />}
                label="Private"
                size="small"
                color="success"
                variant="outlined"
                onClick={() => { setSettingsTab('privacy'); setProposalSettingsOpen(true); }}
                sx={{ fontSize: '1rem', height: 22, cursor: 'pointer' }}
              />
            </Tooltip>
            {aiEnabled && (
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '13px !important' }} />}
                label="AI on"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '1rem', height: 22 }}
              />
            )}
            <Button
              size="small"
              variant={boardsOpen ? 'contained' : 'outlined'}
              startIcon={<ViewKanbanIcon />}
              onClick={() => setBoardsOpen((open) => !open)}
              sx={{ height: 24, ml: 0.5, textTransform: 'none', fontWeight: 600 }}
            >
              Boards
            </Button>
            {/* reviews come back once it was sent out: not while it is still being written */}
            {!UNSUBMITTED_STATUSES.includes(selectedProposal.status) && (
              <Tooltip title="Reviews received (evaluation reports, reviewers' comments) and the team's responses">
                <Button
                  size="small"
                  color="secondary"
                  variant={reviewsOpen ? 'contained' : 'outlined'}
                  startIcon={<ReviewsReceivedIcon />}
                  onClick={() => setReviewsOpen(true)}
                  sx={{ height: 24, ml: 0.5, textTransform: 'none', fontWeight: 600 }}
                >
                  Reviews
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Chat icon + three-dots menu */}
        <Box sx={{ display: 'flex', alignItems: 'center',  mt: 0 }}>
        <Tooltip title="Team chat">
          <IconButton size="medium" onClick={() => setChatOpen(true)} color={chatOpen ? 'primary' : 'default'}>
            <Badge color="primary" variant="dot" invisible={chatMessages.length === 0}>
              <ChatIcon fontSize="large" />
            </Badge>
          </IconButton>
        </Tooltip>
        <IconButton size="medium" onClick={(e) => setHeaderMenuAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="large" />
        </IconButton>
        <Menu
          anchorEl={headerMenuAnchor}
          open={Boolean(headerMenuAnchor)}
          onClose={() => setHeaderMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { setHeaderMenuAnchor(null); setSettingsTab(null); setProposalSettingsOpen(true); }}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { setHeaderMenuAnchor(null); setAiEnabled(v => !v); }}>
            <ListItemIcon><AutoAwesomeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{aiEnabled ? 'Disable AI' : 'Enable AI'}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { setHeaderMenuAnchor(null); setImportDocxOpen(true); }}>
            <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Import {isPublication ? 'publication' : 'proposal'}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { setHeaderMenuAnchor(null); setAboutDialogOpen(true); }}>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>About {isPublication ? 'Publication' : 'Proposal'} Writer</ListItemText>
          </MenuItem>
        </Menu>
        </Box>
      </Box>
    </Box>
  </Container>
</Paper>


      {/* Team Chat Drawer */}
      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        PaperProps={{ sx: { width: 360, display: 'flex', flexDirection: 'column', top: 64, height: 'calc(100% - 64px)' } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>Team Chat</Typography>
          <IconButton size="small" onClick={() => setChatOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        {/* Member presence row */}
        {consortiumUsers.length > 0 && (() => {
          const now = Date.now();
          const ONLINE_MS = 15 * 60 * 1000;
          const lastMsgTime = {};
          chatMessages.forEach(msg => {
            const t = new Date(msg.created).getTime();
            if (!lastMsgTime[msg.author] || t > lastMsgTime[msg.author]) lastMsgTime[msg.author] = t;
          });
          const sorted = [...consortiumUsers].sort((a, b) => (lastMsgTime[b.id] || 0) - (lastMsgTime[a.id] || 0));
          const MAX_SHOWN = 7;
          const shown = sorted.slice(0, MAX_SHOWN);
          const overflow = sorted.length - MAX_SHOWN;
          return (
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
              {shown.map(u => {
                const isCurrentUser = currentUser && u.id === currentUser.id;
                const sm = isCurrentUser ? getStatusMeta(myStatus) : null;
                const badgeColor = isCurrentUser ? sm.color : '#9e9e9e';
                const tooltipStatus = isCurrentUser ? sm.label : 'Offline';
                const displayName = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || '?';
                const initials = (
                  (u.first_name?.[0] || '') + (u.last_name?.[0] || '')
                ).toUpperCase() || displayName[0]?.toUpperCase() || '?';
                return (
                  <Tooltip key={u.id} title={`${displayName} · ${tooltipStatus}`} placement="bottom" arrow>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      badgeContent=""
                      sx={{
                        '& .MuiBadge-badge': {
                          minWidth: 10, width: 10, height: 10,
                          borderRadius: '50%',
                          bgcolor: badgeColor,
                          border: '1.5px solid',
                          borderColor: 'background.paper',
                          p: 0,
                        },
                      }}
                    >
                      <Avatar
                        src={u.avatar || undefined}
                        sx={{ width: 32, height: 32, fontSize: '1.25rem', 
                          fontWeight: 700, cursor: 'default', 
                          bgcolor: theme.palette.primary.avatar || theme.palette.primary.light }}
                      >
                        {initials}
                      </Avatar>
                    </Badge>
                  </Tooltip>
                );
              })}
              {overflow > 0 && (
                <Tooltip title={`${overflow} more member${overflow > 1 ? 's' : ''}`} placement="bottom" arrow>
                  <Chip
                    label={`+${overflow}`}
                    size="small"
                    sx={{ height: 32, width: 32, borderRadius: '50%', '& .MuiChip-label': { px: 0 }, fontSize: '0.7rem', flexShrink: 0 }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        })()}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1 }}>
          {chatMessages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No messages yet. Start the conversation!
            </Typography>
          )}
          <List disablePadding>
            {chatMessages.map((msg) => {
              const isMe = currentUser && msg.author === currentUser.id;
              const isEditing = editingMsgId === msg.id;
              return (
                <ListItem
                  key={msg.id}
                  disablePadding
                  sx={{
                    mb: 1.5, alignItems: 'center',
                    flexDirection: 'row', gap: 0.5,
                    '&:hover .msg-actions': { opacity: 1 },
                  }}
                >
                  {/* Edit/Delete: leftmost, only for own messages */}
                  {isMe && !isEditing && (
                    <Box
                      className="msg-actions"
                      sx={{ display: 'flex', flexDirection: 'row', gap: 0.25, flexShrink: 0, opacity: 0, transition: 'opacity 0.15s' }}
                    >
                      <Tooltip title="Edit" placement="top" arrow>
                        <IconButton size="small" onClick={() => handleMsgEditStart(msg)} sx={{ width: 22, height: 22 }}>
                          <EditIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" placement="top" arrow>
                        <IconButton size="small" color="error" onClick={() => handleMsgDelete(msg)} sx={{ width: 22, height: 22 }}>
                          <DeleteIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  {/* Spacer: pushes own-message bubble to the right */}
                  {isMe && <Box sx={{ flex: 1 }} />}
                  {/* Avatar: left for others, right for own */}
                  {!isMe && (
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <Avatar src={msg.author_avatar || undefined} sx={{ width: 32, height: 32, fontSize: '0.8rem', ...CHAT_AVATAR_SX }}>
                        {msg.author_name?.[0] || '?'}
                      </Avatar>
                    </ListItemAvatar>
                  )}
                  <Box sx={{ maxWidth: '70%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, textAlign: isMe ? 'right' : 'left' }}>
                      {isMe ? 'You' : msg.author_name}
                    </Typography>
                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth size="small" multiline maxRows={6}
                          value={editingMsgBody}
                          onChange={(e) => setEditingMsgBody(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMsgEditSave(); }
                            if (e.key === 'Escape') { setEditingMsgId(null); setEditingMsgBody(''); }
                          }}
                          autoFocus
                          sx={{ mb: 0.5 }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton size="small" onClick={() => { setEditingMsgId(null); setEditingMsgBody(''); }}>
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                          <IconButton size="small" color="primary" onClick={handleMsgEditSave} disabled={!editingMsgBody.trim()}>
                            <CheckCircleIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{
                        bgcolor: isMe ? 'primary.main' : 'action.hover',
                        color: isMe ? 'primary.contrastText' : 'text.primary',
                        borderRadius: 2, px: 1.5, py: 0.75,
                      }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body}</Typography>
                      </Box>
                    )}
                    {/* reactions, the time, and (on hover) a button to add one */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap',
                      flexDirection: isMe ? 'row-reverse' : 'row' }}>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(msg.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      {(msg.reactions || []).map((r) => (
                        <Tooltip key={r.emoji} arrow title={r.names.filter(Boolean).join(', ')}>
                          <Chip size="small" label={`${r.emoji} ${r.count}`} onClick={() => handleMsgReact(msg, r.emoji)}
                            variant={r.mine ? 'filled' : 'outlined'} color={r.mine ? 'primary' : 'default'}
                            sx={{ height: 22, fontSize: '0.8rem', cursor: 'pointer', '& .MuiChip-label': { px: 0.75 } }} />
                        </Tooltip>
                      ))}
                      {!isEditing && (
                        <Tooltip title="React" arrow>
                          <IconButton size="small" className="msg-actions" aria-label="Add a reaction"
                            onClick={(e) => setReactAnchor({ el: e.currentTarget, msg })}
                            sx={{ width: 22, height: 22, opacity: 0, transition: 'opacity 0.15s' }}>
                            <AddReactionIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  {isMe && (
                    <Avatar src={msg.author_avatar || undefined} sx={{ width: 32, height: 32, fontSize: '0.8rem', flexShrink: 0, ...CHAT_AVATAR_SX }}>
                      {msg.author_name?.[0] || '?'}
                    </Avatar>
                  )}
                </ListItem>
              );
            })}
          </List>
          <Menu anchorEl={reactAnchor?.el} open={Boolean(reactAnchor)} onClose={() => setReactAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ zIndex: 5000 }} MenuListProps={{ sx: { display: 'flex', px: 0.5, py: 0.25 } }}>
            {CHAT_REACTIONS.map((emoji) => (
              <MenuItem key={emoji} onClick={() => handleMsgReact(reactAnchor.msg, emoji)}
                sx={{ fontSize: '1.25rem', px: 0.75, minHeight: 36, borderRadius: 1 }}>
                {emoji}
              </MenuItem>
            ))}
          </Menu>
          <div ref={chatBottomRef} />
        </Box>
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1 }}>
          <TextField
            inputRef={chatInputRef}
            fullWidth
            size="small"
            placeholder="Type a message…"
            onChange={(e) => setHasChatInput(!!e.target.value.trim())}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
            multiline
            maxRows={4}
            disabled={chatSending}
          />
          <IconButton color="primary" onClick={handleChatSend} disabled={!hasChatInput || chatSending}>
            {chatSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Drawer>

      {/* Error Message */}
      {error && (
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </Alert>
        </Container>
      )}

      {/* Navigation Tabs */}
     {/* 1. Abstract; 2. Sections; 3. Work Packages; 4. Budget; 5. Gender; 6. Ethics; 7. Preview */} 
      <Paper sx={{ borderRadius: 0, display: boardsOpen ? 'none' : undefined }}>
        {/* the same width as the header and the content below: the first tab lines up with the Back button */}
        <Container maxWidth="xl">
         <Stack flexDirection="row" justifyContent="space-between" alignItems="center" sx={{ gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ '& .MuiTab-root': { fontSize: '1.4rem', overflow: 'visible' } }}
          >
            {isPublication && <Tab value={7} icon={<AuthorsIcon />} label={tabLabel(`Authors (${(selectedProposal?.authors || []).length})`, 7)} iconPosition="start" />}
            <Tab value={0} icon={<DescriptionIcon />} label={tabLabel('Abstract', 0)} iconPosition="start" />
            <Tab value={1} icon={<FolderIcon />} label={tabLabel('Sections', 1)} iconPosition="start" />
            {!isPublication && <Tab value={2} icon={<FolderSpecialIcon />} label={tabLabel('Work Packages', 2)} iconPosition="start" />}
            {!isPublication && <Tab value={3} icon={<AttachMoneyIcon />} label={tabLabel('Budget', 3)} iconPosition="start" />}
            {/* <Tab icon={<GenderEqualityIcon />} label="Gender Equality" iconPosition="start" /> */}
            {/* <Tab icon={<BalanceIcon />} label="Ethics" iconPosition="start" /> */}
            {!isPublication && <Tab value={4} icon={<CampaignIcon />} label={tabLabel('Dissemination', 4)} iconPosition="start" />}
            {isPublication && <Tab value={8} icon={<ReferencesIcon />} label={tabLabel(`Bibliography (${(selectedProposal?.references || []).length})`, 8)} iconPosition="start" />}
            <Tab value={5} icon={<PreviewIcon />} label="Preview" iconPosition="start" />
          </Tabs>
          </Box>


       { activeTab === 1 ? (
         <Stack direction="row" spacing={2} mr={2} sx={{ flexShrink: 0 }}>
           
          
        
<Box>
           <Tooltip title="Select Template">      
              <IconButton onClick={() => setTemplateDialogOpen(true)} color="primary" >
                <TemplateIcon fontSize="large"/>
              </IconButton>
          </Tooltip>



          <Tooltip title="Edit Order">      
              <IconButton onClick={() => setRestructureDialogOpen(true)} color="primary" >
              <AccountTreeIcon  fontSize="large" />
              </IconButton>
          </Tooltip>
</Box>
           <Button
             size="large"
             variant="contained"
             startIcon={<AddIcon />}
             onClick={handleAddSection}
             sx={{ ml: 1}}
           >
             Add
           </Button>
         </Stack>
         ) : activeTab === 2 ? (
          <Stack direction="row" spacing={2} mr={2} sx={{ flexShrink: 0 }}>
         

         <Box>

            <Tooltip title="Download Template">
            

                <IconButton onClick={handleDownloadExcelTemplate} color="primary">
                    <DownloadIcon  fontSize="large"/>
                </IconButton>


            </Tooltip>


        <Tooltip title="Import Work Plan">
        <IconButton
          component="label"
          disabled={importingExcel}
          color="primary"
          
        >
          {importingExcel ? <CircularProgress size={24} sx={{ mr: 1 }} /> : <ImportIcon fontSize="large"/>}
          <input type="file" hidden accept=".xlsx" onChange={handleImportExcel} />
        </IconButton>
  </Tooltip>
  </Box>

            <Button
              size="large"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddWorkPackage}
            >
              Add 
            </Button>
        
          </Stack>
         ) :
         activeTab === 5 ? (

<Stack direction="row" spacing={2} mr={2} sx={{ flexShrink: 0 }}>


{/* {isCoordinator && (

<Tooltip title="Edit Proposal Details">
  <IconButton size="small" onClick={() => setEditProposalDialogOpen(true)}>
     <EditIcon fontSize="small" color="primary"/>
  </IconButton>
</Tooltip>

   )} */}


<Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', display: { xs: 'none', md: 'block' } }}>
  Right-click a section to edit it
</Typography>

<Tooltip title="Download">
  <IconButton size="small" onClick={(e) => setDownloadMenu(e.currentTarget)}>
        {downloading ? <CircularProgress size={20} color="primary" /> :
                        <DownloadIcon fontSize="large" color="primary"/>}
  </IconButton>
</Tooltip>
<Menu anchorEl={downloadMenu} open={Boolean(downloadMenu)} onClose={() => setDownloadMenu(null)}>
  {!isPublication && (
  <MenuItem onClick={() => { setDownloadMenu(null); handleDownloadWord(); }}>
    <ListItemText primary="Rich Text Document (.docx)" secondary="The proposal as a Word document" />
  </MenuItem>
  )}
  {isPublication && (
  <MenuItem onClick={async () => {
    setDownloadMenu(null);
    try { await exportManuscript({ pub: selectedProposal, mode: 'formatted', styledBody, figureImages: await loadFigureImages(allBodies()) }); }
    catch (err) { console.error(err); showSnackbar('Export failed', 'error'); }
  }}>
    <ListItemText primary="Manuscript (.docx)" secondary="Line-numbered, citations in the journal style" />
  </MenuItem>
  )}
  {isPublication && (
  <MenuItem onClick={async () => {
    setDownloadMenu(null);
    try { await exportManuscript({ pub: selectedProposal, mode: 'endnote', styledBody, figureImages: await loadFigureImages(allBodies()) }); }
    catch (err) { console.error(err); showSnackbar('Export failed', 'error'); }
  }}>
    <ListItemText primary="EndNote version (.docx + .ris)"
      secondary="Import the .ris into EndNote, then run Update Citations and Bibliography in Word" />
  </MenuItem>
  )}
  {isPublication && (
  <MenuItem onClick={async () => {
    setDownloadMenu(null);
    try { await exportRis(selectedProposal); } catch (err) { console.error(err); showSnackbar('Export failed', 'error'); }
  }}>
    <ListItemText primary="References only (.ris)" secondary="For EndNote, Zotero or Mendeley" />
  </MenuItem>
  )}
  <Divider />
  {/* the Otable document format: the whole document, to keep a copy and import it back into Otable —
      backend: proposal/proposal/otable_transfer.py */}
  <MenuItem onClick={async () => {
    setDownloadMenu(null);
    try {
      const res = await axiosService.get(`/proposal/proposals/${selectedProposal.id}/export_document/`, { responseType: 'blob', timeout: 180000 });
      const name = /filename="([^"]+)"/.exec(res.headers?.['content-disposition'] || '')?.[1] || 'document.otable';
      saveAs(res.data, name);
    } catch (err) { console.error(err); showSnackbar('Export failed', 'error'); }
  }}>
    <ListItemText primary="Otable document (.otable)" secondary="The whole document with figures, references and comments — keep a copy and import it back into Otable any time" />
  </MenuItem>
</Menu>


<Box display="flex" flexDirection="row" spacing={1}>

{selectedProposal && (() => {
      const { items } = computeWarnings(selectedProposal);
      const errorCount = items.filter(i => i.severity === 'error').length;
      return (
        <Tooltip title="Check for missing or incomplete fields in this proposal">
          <Button
            variant="text"
            size="large"
            startIcon={<WarningAmberIcon />}
            onClick={() => { setCheckedWarnings(new Set()); setWarningsOpen(true); }}
            color={errorCount > 0 ? 'error' : 'warning'}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Warnings{items.length > 0 ? ` (${items.length})` : ''}
          </Button>
        </Tooltip>
      );
    })()}


{aiReviewHistory.length > 0 && (
          <Tooltip title={`Show ${aiReviewHistory.length} previous review${aiReviewHistory.length > 1 ? 's' : ''}`}>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<HistoryIcon />}
              onClick={handleShowPreviousReviews}
              sx={{ whiteSpace: 'nowrap' }}
            >
               Reviews ({aiReviewHistory.length})
            </Button>
          </Tooltip>
        )}

{aiEnabled && (
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Tooltip title="Review this proposal against the funding call criteria using AI">
          <span>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<RateReviewIcon />}
              onClick={handleStartAIReview}
              sx={{ whiteSpace: 'nowrap', color: '#fff' }}
            >
              AI Review
            </Button>
          </span>
        </Tooltip>

      </Stack>
    )}

</Box>

 </Stack>


         ) : null

}
        </Stack>
        </Container>
      </Paper>

      {/* Main Content */}
      {boardsOpen && selectedProposal && (
        <Container maxWidth="xl" sx={{ py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <ViewKanbanIcon color="primary" />
            <Typography variant="h5" sx={{ flex: 1 }}>Boards</Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setBoardsOpen(false)}>
              Back to {isPublication ? 'publication' : 'proposal'}
            </Button>
          </Stack>
          <ProjectManagementPanel
            proposalId={selectedProposal.id}
            members={consortiumUsers}
            sections={(selectedProposal.sections || []).map((s) => ({ id: s.id, title: s.title }))}
            canManage={isCoordinator || isProjectLeader}
            currentUserId={currentUser?.id}
          />
        </Container>
      )}

      <Container maxWidth="xl" sx={{ py: 4, display: boardsOpen ? 'none' : undefined }}>
        {/* Abstract Tab */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3, mr: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ ml: 1 }}>ABSTRACT</Typography>
            <Divider sx={{ my: 1 }} />

   

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                Word count: {getWordCount(abstractText)}
                {abstractSection?.word_limit ? ` / ${abstractSection.word_limit}` : ''}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                {aiEnabled && abstractSection && (
                  <>
                    <Typography variant="caption" color="text.secondary">Word limit:</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={abstractWordLimitInput}
                      onChange={e => setAbstractWordLimitInput(e.target.value)}
                      onBlur={() => handleWordLimitBlur(
                        abstractSection.id,
                        abstractWordLimitInput,
                        (newLimit) => setAbstractSection(prev => ({ ...prev, word_limit: newLimit }))
                      )}
                      placeholder="none"
                      slotProps={{ htmlInput: { min: 1, style: { width: 80, textAlign: 'right' } } }}
                      sx={{ ...WORD_LIMIT_SX, mr: 1 }}
                    />
                  </>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  disabled={!abstractSection}
                  onClick={() => handleShowVersions(abstractSection)}
                >
                  View History
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={isSaving}
                  onClick={handleSaveAbstract}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>

            {aiEnabled && abstractSection && (
              <AISectionWriter
                section={abstractSection}
                proposal={selectedProposal}
                onUseContent={async (text) => {
                  setAbstractText(text);
                  setAbstractEditorResetKey(k => k + 1);
                  try {
                    await axiosService.patch(`/proposal/proposals/${selectedProposal.id}/`, { abstract: text });
                    await loadProposalDetails(selectedProposal.id);
                  } catch (e) {
                    console.error('Auto-save abstract failed:', e);
                  }
                }}
              />

            )}

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ mb: 1.5 }}>
              <SlateEditor
                key={`${selectedProposal.id}-${abstractEditorResetKey}`}
                richText
                value={abstractText}
                onChange={setAbstractText}
                height={320}
                placeholder="Enter proposal abstract..."
                allowFigures
                figures={editorFigures}
                trackChanges={trackChangesProp}
                citations={citationsProp}
                {...(abstractSection?.id ? commentProps(abstractSection.id, abstractComments, setAbstractComments) : {})}
              />

            {isPublication && (
              <GraphicalAbstractPanel proposal={selectedProposal} aiEnabled={aiEnabled}
                figureData={figureData} setFigureData={setFigureData} />
            )}
            </Box>

            {/* <Divider sx={{ my: 1.5 }} /> */}
            <Box sx={{ px:1, mt:3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ ml: 1}}>
                Keywords
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={keywords}
                onChange={e => setKeywords(e.target.value)}
                placeholder="e.g. machine learning, drug discovery, proteomics"
                helperText="Comma-separated keywords describing the proposal's topics"
              />


            </Box>
          </Paper>
        )}

        {/* Sections Tab */}
        {activeTab === 1 && (
          <Box mr={1}>
            {/* <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ ml:2 }}>Proposal Sections</Typography>
            </Box> */}

            {editingSection ? (() => {
              const editorHeader = (
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {sectionEditorFullScreen && (
                      <Tooltip title="Back">
                        <IconButton onClick={() => setSectionEditorFullScreen(false)}>
                          <ArrowBackIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Typography variant="h4">
                      {editingSection.section_key && (
                        <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                          {editingSection.section_key}.
                        </Box>
                      )}
                      {editingSection.title}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PresenceAvatars others={presenceOthers} sectionId={editingSection.id} />
                    {!sectionEditorFullScreen && (
                      <Tooltip title="Full screen">
                        <IconButton onClick={() => setSectionEditorFullScreen(true)}>
                          <FullscreenIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton onClick={cancelEditing}>
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              );

              const toolsHidden = sectionEditorFullScreen && fsToolsHidden;
              const editorBody = (
                <>
                  <Collapse in={!toolsHidden} timeout={200}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="body2" color={editingSection.word_limit && getWordCount(content) > editingSection.word_limit ? 'error' : 'text.secondary'}>
                      Word count: {getWordCount(content)}
                      {editingSection.word_limit ? ` / ${editingSection.word_limit}` : ''}
                    </Typography>
                    {autosaveStatus && (
                      <Typography variant="caption" color={autosaveStatus.paused ? 'warning.main' : 'text.secondary'} sx={{ ml: 2, mr: 'auto' }}>
                        {autosaveStatus.paused
                          ? 'Autosave paused: a co-author changed the same lines. Save to compare.'
                          : `Autosaved at ${autosaveStatus.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · co-authors see it; Save to notify them`}
                      </Typography>
                    )}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="caption" color="text.secondary">Word limit:</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={wordLimitInput}
                        onChange={e => setWordLimitInput(e.target.value)}
                        onBlur={() => handleWordLimitBlur(
                          editingSection.id,
                          wordLimitInput,
                          (newLimit) => setEditingSection(prev => ({ ...prev, word_limit: newLimit }))
                        )}
                        placeholder="none"
                        slotProps={{ htmlInput: { min: 1, style: { width: 80, textAlign: 'right' } } }}
                        sx={WORD_LIMIT_SX}
                      />
                    </Stack>
                  </Stack>

                  {editingSection.word_limit && (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((getWordCount(content) / editingSection.word_limit) * 100, 100)}
                      color={getWordCount(content) > editingSection.word_limit ? 'error' : 'primary'}
                      sx={{ mb: 2 }}
                    />
                  )}

                  {aiEnabled && (
                    <Box sx={{ mb: 2 }}>
                      <AISectionWriter
                        section={editingSection}
                        proposal={selectedProposal}
                        onUseContent={(text) => {
                          setContent(text);
                          setSectionEditorResetKey(k => k + 1);
                        }}
                      />
                    </Box>
                  )}
                  </Collapse>

                  <Box sx={{ mb: 2 }}>
                    <SlateEditor
                      key={`${editingSection.id}-${sectionEditorResetKey}`}
                      richText
                      value={content}
                      onChange={setContent}
                      placeholder="Start writing..."
                      height={sectionEditorFullScreen ? window.innerHeight - (toolsHidden ? 130 : 300) : 400}
                      stickyToolbar={sectionEditorFullScreen}
                      allowFigures
                      figures={editorFigures}
                      trackChanges={trackChangesProp}
                      citations={editingSection.title.trim().toLowerCase() !== 'references' ? citationsProp : undefined}
                      {...commentProps(editingSection.id, sectionComments, setSectionComments)}
                    />
                  </Box>

                  <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<HistoryIcon />}
                      onClick={() => handleShowVersions(editingSection)}
                    >
                      View History
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={handleSaveContent}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </Stack>
                </>
              );

              // Full screen: the section title stays pinned in a bar above the scrolling content
              const overLimit = editingSection.word_limit && getWordCount(content) > editingSection.word_limit;
              const fullScreenBar = (
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
                    borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0,
                  }}
                >
                  <Tooltip title="Exit full screen">
                    <IconButton onClick={() => setSectionEditorFullScreen(false)} aria-label="Exit full screen">
                      <ArrowBackIcon />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="h5" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {editingSection.section_key && (
                      <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>{editingSection.section_key}.</Box>
                    )}
                    {editingSection.title}
                  </Typography>
                  <PresenceAvatars others={presenceOthers} sectionId={editingSection.id} />
                  <Chip
                    size="small"
                    variant="outlined"
                    color={overLimit ? 'error' : 'default'}
                    label={`${getWordCount(content)}${editingSection.word_limit ? ` / ${editingSection.word_limit}` : ''} words`}
                  />
                  <Button
                    variant="contained"
                    startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveContent}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Tooltip title="Close editor">
                    <IconButton onClick={cancelEditing} aria-label="Close editor">
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Hide the header, word count and tools: more room to write">
                    <IconButton onClick={toggleFsTools} aria-expanded aria-label="Hide the header, word count and tools">
                      <ExpandLessIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              );

              return sectionEditorFullScreen ? (
                <Dialog
                  fullScreen
                  open
                  onClose={() => setSectionEditorFullScreen(false)}
                  PaperProps={{ sx: { display: 'flex', flexDirection: 'column', position: 'relative' } }}
                >
                  <Collapse in={!fsToolsHidden} timeout={200} sx={{ flexShrink: 0 }}>{fullScreenBar}</Collapse>
                  {fsToolsHidden && (
                    // Everything above the editor is folded away: one quiet arrow brings it back
                    <Tooltip title="Show the header, word count and tools" placement="left">
                      <IconButton onClick={toggleFsTools} aria-expanded={false} aria-label="Show the header, word count and tools"
                        sx={{ position: 'absolute', top: 31, right: 36, zIndex: 20, width: 32, height: 32,
                          border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'action.hover' } }}>
                        <ExpandMoreIcon sx={{ fontSize: 22 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* No top padding on the scroll area itself, so the editor toolbar pins flush under the bar */}
                  <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
                    <Box sx={{ pt: 3 }}>{editorBody}</Box>
                  </DialogContent>
                </Dialog>
              ) : (
                <Paper sx={{ p: 3 }}>
                  {editorHeader}
                  {editorBody}
                </Paper>
              );
            })() : (
              <>
                {(() => {
                  const displayedSections = (selectedProposal.sections || []).filter(s => s.section_key !== 'abstract' && s.section_key !== 'appendix');
                  const wps = selectedProposal.work_packages || [];
                  return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '8fr 3fr' }, gap: 2, alignItems: 'start' }}>
                  <Stack spacing={2}>
                  {displayedSections.length === 0 && (
                    <Paper sx={{ p: 8, textAlign: 'center' }}>
                      <DescriptionIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                      <Typography color="text.secondary" paragraph>No sections available for this proposal</Typography>
                      {canManageSections && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddSection}>
                          Add First Section
                        </Button>
                      )}
                    </Paper>
                  )}
                  {displayedSections.map(section => (
                    <Card
                      key={section.id}
                      variant={section.is_work_plan ? 'outlined' : 'elevation'}
                      sx={section.is_work_plan ? theme => ({
                        borderColor: theme.palette.secondary.main,
                        borderLeftWidth: 4,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.06),
                      }) : {}}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box
                            sx={{
                              flex: 1,
                              cursor: lockingEnabled && section.is_locked && section.locked_by_id !== String(currentUser?.id) ? 'not-allowed' : 'pointer',
                              '&:hover .section-title': { color: 'primary.main' },
                            }}
                            onClick={() => startEditing(section)}
                          >


                       < Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h5" className="section-title">{section.section_key}. {section.title}</Typography>
                            {section.is_work_plan && (
                              <FolderSpecialIcon fontSize="small" color="secondary" titleAccess="Work Plan section" />
                            )}
                            
<Stack direction="row" spacing={0.5} alignItems="center">
  <PeopleIcon fontSize="small" color="action" />

  {section?.assignee_names?.length ? (
    section.assignee_names.map((assignee, index) => (
      <Typography
        key={index}
        variant="caption"
        color="text.secondary"
      >
        {assignee.name}
      </Typography>
    ))
  ) : (
    <Typography variant="caption" color="text.secondary">
      Unassigned
    </Typography>
  )}
</Stack>


<Stack direction="row" alignItems="center" spacing={1}>
  <Typography variant="caption" color="text.secondary">
    Due date:
  </Typography>

  <Typography variant="caption" fontWeight={500}>
    {section.due_date || '—'}
  </Typography>
</Stack>




   

                        </Box>
                            
                            {section.current_content ? (
                              <>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  sx={{ 
                                    mb: 1,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                   {/* Readable text: comment / tracked-change / figure markers removed, citations styled */}
                                   {markdownToPlainText(styledBody(section)).replace(/\s*\n+\s*/g, ' ')}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Words: {getWordCount(section.current_content.body)}
                                    {section.word_limit && ` / ${section.word_limit}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">•</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Last edited by {section.current_content.author_name}
                                  </Typography>
                                </Stack>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                No content yet
                              </Typography>
                            )}
                          </Box>
                        






  <Stack direction="row" spacing={0.5} sx={{ ml: 2 }}>
    <IconButton
      size="small"
      onClick={() => handleShowVersions(section)}
      title="View history"
    >
      <HistoryIcon fontSize="small" />
    </IconButton>




    {canManageSections && (<>

      <IconButton onClick={() => handleAddSubsection(section)} size="small" title="Add subsection">
        <AddIcon fontSize="small" />
      </IconButton>

      <IconButton onClick={() => handleEditSection(section)} size="small" title="Edit section">
        <SettingsIcon fontSize="small" />
      </IconButton>

      </>)}


      {canManageSections && (
    <IconButton
      size="small"
      color="error"
      onClick={() => handleDeleteSection(section.id)}
      title="Delete section"
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
    )}






  </Stack>
</Stack>

{lockingEnabled && section.is_locked && (
  <Chip
    size="small"
    icon={section.locked_by_id === String(currentUser?.id) ? <LockOpenIcon /> : <LockIcon />}
    label={
      section.locked_by_id === String(currentUser?.id)
        ? 'You are editing'
        : `Editing: ${section.locked_by_name}`
    }
    color={section.locked_by_id === String(currentUser?.id) ? 'primary' : 'warning'}
    variant="outlined"
    sx={{ mt: 1 }}
  />
)}

{/* Status row */}
<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
  {/* <Chip
    size="small"
    label={{
      draft: 'Draft',
      in_progress: 'In Progress',
      ready_for_review: 'Ready for Review',
      approved: 'Approved',
    }[section.status] || section.status}
    color={{
      draft: 'default',
      in_progress: 'info',
      ready_for_review: 'warning',
      approved: 'success',
    }[section.status] || 'default'}
    variant="filled"
  /> */}

  {/* Writer: submit for review — only when locking/approval workflow is enabled */}
  {lockingEnabled && section.status === 'in_progress' && section.current_content && (
    <Tooltip title="Submit this section for coordinator review">
      <Button
        size="small"
        variant="outlined"
        color="warning"
        startIcon={<RateReviewIcon />}
        onClick={() => handleSectionStatusUpdate(section.id, 'ready_for_review')}
      >
        Submit for Review
      </Button>
    </Tooltip>
  )}

  {/* Coordinator: approve or push back — only when locking/approval workflow is enabled */}
  {lockingEnabled && section.status === 'ready_for_review' && isCoordinator && (
    <>
      <Tooltip title="Approve this section">
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={() => handleSectionStatusUpdate(section.id, 'approved')}
        >
          Approve
        </Button>
      </Tooltip>
      <Tooltip title="Push back for revision">
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<ReplayIcon />}
          onClick={() => handleSectionStatusUpdate(section.id, 'in_progress')}
        >
          Push Back
        </Button>
      </Tooltip>
    </>
  )}
</Stack>




                        {section.subsections && section.subsections.length > 0 && (
                          <Box sx={{ mt: 1.5, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                            <Stack spacing={0.5}>
                              {section.subsections.map(subsection => (
                                <Box
                                  key={subsection.id}
                                  sx={subsection.is_work_plan ? theme => ({
                                    borderLeft: `3px solid ${theme.palette.secondary.main}`,
                                    pl: 1,
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.06),
                                    borderRadius: '0 4px 4px 0',
                                  }) : {}}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center"
                                    sx={{ py: 0.25, '&:hover .sub-actions': { opacity: 1 } }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: 'primary.main' } }}
                                      onClick={() => startEditing(subsection)}
                                    >
                                      {subsection.section_key}. {subsection.title}
                                      {subsection.is_work_plan && (
                                        <FolderSpecialIcon sx={{ fontSize: 14, color: 'secondary.main', flexShrink: 0 }} />
                                      )}
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} className="sub-actions"
                                      sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                                      {canManageSections && (<>
                                      <IconButton size="small" onClick={() => handleAddSubsection(subsection)} title="Add subsection">
                                        <AddIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleEditSection(subsection)} title="Edit section">
                                        <SettingsIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                      </>)}
                                      {canManageSections && (
                                      <IconButton size="small" color="error" onClick={() => handleDeleteSection(subsection.id)} title="Delete">
                                        <DeleteIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                      )}
                                    </Stack>
                                  </Stack>

                                  {/* Third level */}
                                  {subsection.subsections && subsection.subsections.length > 0 && (
                                    <Box sx={{ pl: 2, borderLeft: 1, borderColor: 'divider', ml: 1, mb: 0.5 }}>
                                      <Stack spacing={0.25}>
                                        {subsection.subsections.map(sub2 => (
                                          <Stack key={sub2.id} direction="row" justifyContent="space-between" alignItems="center"
                                            sx={sub2.is_work_plan ? theme => ({
                                              py: 0.25,
                                              borderLeft: `3px solid ${theme.palette.secondary.main}`,
                                              pl: 1,
                                              backgroundColor: alpha(theme.palette.secondary.main, 0.06),
                                              borderRadius: '0 4px 4px 0',
                                              '&:hover .sub2-actions': { opacity: 1 },
                                            }) : { py: 0.25, '&:hover .sub2-actions': { opacity: 1 } }}>
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ flex: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: 'primary.main' } }}
                                              onClick={() => startEditing(sub2)}
                                            >
                                              {sub2.section_key}. {sub2.title}
                                              {sub2.is_work_plan && (
                                                <FolderSpecialIcon sx={{ fontSize: 12, color: 'secondary.main', flexShrink: 0 }} />
                                              )}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} className="sub2-actions"
                                              sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                                              {canManageSections && (<>
                                              <IconButton size="small" onClick={() => handleEditSection(sub2)} title="Edit section">
                                                <SettingsIcon sx={{ fontSize: 12 }} />
                                              </IconButton>
                                              </>)}
                                              {canManageSections && (
                                              <IconButton size="small" color="error" onClick={() => handleDeleteSection(sub2.id)} title="Delete">
                                                <DeleteIcon sx={{ fontSize: 12 }} />
                                              </IconButton>
                                              )}
                                            </Stack>
                                          </Stack>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {/* Appendix sections group */}
                  {(() => {
                    const appendixSect = (selectedProposal.sections || []).find(s => s.section_key === 'appendix');
                    if (!appendixSect || isPublication) return null;
                    const appendixSubs = (appendixSect.subsections || []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
                    return (
                      <>
                        <Divider sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ px: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Appendix
                          </Typography>
                        </Divider>
                        {appendixSubs.length === 0 ? (
                          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 1.5, fontStyle: 'italic' }}>
                            No content yet.
                          </Typography>
                        ) : appendixSubs.map((sub, idx) => (
                          <Card key={sub.id} sx={{ opacity: 0.85 }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box
                                  sx={{ flex: 1, cursor: 'pointer', '&:hover .app-title': { color: 'primary.main' } }}
                                  onClick={() => startEditing(sub)}
                                >
                                  <Typography variant="subtitle1" className="app-title" color="text.secondary">
                                    A.{idx + 1}. {sub.title}
                                  </Typography>
                                </Box>
                                <Stack direction="row" spacing={0.5}>
                                  {canManageSections && (<>
                                    <IconButton size="small" onClick={() => handleEditSection(sub)} title="Edit section">
                                      <SettingsIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </>)}
                                  {canManageSections && (
                                    <IconButton size="small" color="error" onClick={() => handleDeleteSection(sub.id)} title="Delete">
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  )}
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </>
                    );
                  })()}
                  {isCoordinator && (
                    <Box sx={{ pt: 1, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        size="small"
                        startIcon={<BookmarkAddIcon />}
                        onClick={() => { setSaveTemplateName(''); setSaveTemplateDesc(''); setSaveTemplateOpen(true); }}
                        variant="outlined"
                        color="secondary"
                      >
                        Save as Template
                      </Button>
                    </Box>
                  )}
                  </Stack>

<Stack spacing={3} sx={{ position: 'sticky', top: 80, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>

                <ProposalTodoPanel
                    proposalId={selectedProposal.id}
                    sections={displayedSections}
                    consortiumMembers={selectedProposal.consortium_users || []}
                    refreshKey={boardsOpen}
                  />

                  <QuickReferenceCard
                    isPublication={isPublication}
                    sections={displayedSections}
                    workPackages={wps}
                    tasks={selectedProposal.tasks || []}
                    proposalId={selectedProposal.id}
                    memberResources={memberResources ? {
                      publications: (memberResources.publications || []).filter(r => memberResourcesSelected.publications.has(String(r.id))),
                      patents:      (memberResources.patents      || []).filter(r => memberResourcesSelected.patents.has(String(r.id))),
                      projects:     (memberResources.projects     || []).filter(r => memberResourcesSelected.projects.has(String(r.id))),
                      networks:     (memberResources.networks     || []).filter(r => memberResourcesSelected.networks.has(String(r.id))),
                    } : null}
                    advisoryBoard={advisoryBoard}
                    consortiumMembers={selectedProposal.consortium_members || []}
                  />

        
        
        </Stack>
                  </Box>
                  );
                })()}
              </>
            )}
          </Box>
        )}

        {/* Work Packages Tab - includes Tasks */}
        {activeTab === 2 && (
          <Box>
            {/* <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, ml:2 }}>
              <Typography variant="h6">Work Packages & Tasks</Typography>
  
            </Box> */}
         
            <Stack spacing={3} mr={1}>
              {selectedProposal.work_packages && selectedProposal.work_packages.length > 0 ? (
                selectedProposal.work_packages.map(wp => {
                  const wpTasks = (selectedProposal.tasks?.filter(t => t.work_package === wp.id) || []).sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
                  const wpBudget = wp.total_budget || 0;
                  const wpPms = wp.total_person_months || 0;
                  // console.log(  '###### Rendering work package organizations:', wp.consortium_members);
                  return (
                    <Card key={wp.id}>
                      <CardContent>
                        <Stack spacing={2}>
                          {/* WP Header */}
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                              <Typography variant="h4" gutterBottom>{wp.full_title}</Typography>
                            
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                mt={2}
                              >
                                {/* Left side (first two) */}
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                  <Chip
                                    icon={<PeopleIcon />}
                                    label={
                                      leadersById[wp.wp_leader]
                                        ? (leadersById[wp.wp_leader]?.organization?.name || leadersById[wp.wp_leader]?.name || 'Unknown')
                                        : "Loading..."
                                    }
                                    size="small"
                                    variant="outlined"
                                  />

                                  {wp.start_date && wp.end_date && (
                                    <Chip
                                      icon={<CalendarIcon />}
                                      label={`${new Date(wp.start_date).toLocaleDateString()} - ${new Date(
                                        wp.end_date
                                      ).toLocaleDateString()}`}
                                      size="small"
                                    />
                                  )}
                                </Stack>

                                {/* Right side (second two) */}
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                  <Chip
                                    icon={<AttachMoneyIcon />}
                                    label={`Total Budget: €${parseFloat(wpBudget).toLocaleString()}`}
                                    size="small"
                                    color="info"
                                  />

                                  <Chip
                                    icon={<PeopleIcon />}
                                    label={`Total PMs: ${parseFloat(wpPms).toLocaleString()}`}
                                    size="small"
                                    color="secondary"
                                    sx={{ color:'#fff'}}
                                  />
                                </Stack>
                              </Stack>




                            </Box>
                            {isCoordinator && (
                            <Stack direction="row" spacing={1}>
                              <IconButton size="small" onClick={() => handleEditWorkPackage(wp)} title="Edit Work Package">
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteWorkPackage(wp.id)}
                                title="Delete Work Package"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                            )}
                          </Box>

                          <Divider />
 
                      </Stack>
                    



<Table size="small">
  <TableHead>
    <TableRow>
      <TableCell>Task</TableCell>
      <TableCell align="center">Task Leader</TableCell>
      <TableCell align="center">Objectives</TableCell>
      <TableCell align="center">Milestones</TableCell>
      <TableCell align="center">Deliverables</TableCell>
      <TableCell align="center">Risks</TableCell>
      <TableCell align="center">Budget Requests</TableCell>
      <TableCell align="center"> Budget</TableCell>
      <TableCell align="center">PM Requests</TableCell>
      <TableCell align="center"> PMs</TableCell>
      {isCoordinator && <TableCell padding="none" />}
    </TableRow>
  </TableHead>

  <TableBody>
    {wpTasks.map( (task) => {
                  // console.log('Rendering task:', task);
                  const budgetContributors = task.budget_items?.reduce((acc, item) => {
                                    if (item.requester_name && !acc.find(r => r.id === item.id)) {
                                      acc.push({ 
                                        id: item.id,
                                        name: item.requester_name,
                                        budgetItem: item
                                      });
                                    }
                                    return acc;
                                  }, []) || [];


                                  return (

       <TableRow key={task.id} hover onClick={() => handleEditTask(task)} sx={{ cursor: 'pointer' }}>

        {/* TASK */}
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <TaskIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {task.full_title}
            </Typography>
          </Stack>
        </TableCell>

        {/* TASK LEADER */}
        <TableCell align="center">
          {task.task_leader_name ? (
            <Tooltip title={task.task_leader_name}>
              <Chip
                label={task.task_leader_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ height: 20, fontSize: '0.9rem', cursor: 'default' }}
                onClick={e => e.stopPropagation()}
              />
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>

        {/* OBJECTIVES */}
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {task.objectives?.length > 0 ? (
              <Chip size="small" label={`${task.objectives.length}O`} />
            ) : "-"}
          </Stack>
        </TableCell>

        {/* MILESTONES */}
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {task.milestones?.length > 0 ? (
              <Chip size="small" label={`${task.milestones.length}M`} />
            ) : "-"}
          </Stack>
        </TableCell>

        {/* DELIVERABLES */}
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {task.deliverables?.length > 0 ? (
              <Chip size="small" label={`${task.deliverables.length}D`} />
            ) : "-"}
          </Stack>
        </TableCell>

        {/* RISKS */}
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {task.risks?.length > 0 ? (
              <Chip size="small" label={`${task.risks.length}R`} />
            ) : "-"}
          </Stack>
        </TableCell>

        {/* BUDGET ITEMS */}
        <TableCell align="center">
          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
    
  
      


      {/* Budget Contributors Badges */}
      {budgetContributors.map((contributor) => {
                                          const initials = contributor.name.split(' ').map(n => n[0]).join('').toUpperCase();
                                          return (
                                            <Chip
                                              key={contributor.id}
                                              label={initials}
                                              size="small"
                                              variant="filled"
                                              title={`Budget contributor: ${contributor.name}. Click to edit.`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setBudgetTaskId(task.id);
                                                setBudgetTask(task);
                                                setEditingBudget(contributor.budgetItem);
                                                setBudgetDialogOpen(true);
                                              }}
                                              sx={{ 
                                                height: 16, 
                                                fontSize: '1rem',
                                                bgcolor:"#4980a7",
                                                color: '#fff',
                                                cursor: 'pointer',
                                                '&:hover': { 
                                                  backgroundColor: 'info.main',
                                                }
                                              }}
                                            />
                                          );
                                        })}

       
          <IconButton
              size="small"
              sx={{ bgcolor: "info.main", color: "#fff", "&:hover": { bgcolor: "info.dark" } }}
              onClick={(e) => {
                e.stopPropagation();
                setBudgetTaskId(task.id);
                setBudgetTask(task);
                setEditingBudget(null);
                setBudgetDialogOpen(true);
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>

           </Stack>
        </TableCell>


<TableCell align="center">
  
          {task.total_budget > 0 && (
              <Typography variant="body2" fontWeight={600} color="info.dark">
                €{parseFloat(task.total_budget).toLocaleString()}
              </Typography>
            )}
  </TableCell >


        {/* Person Months */}
        <TableCell align="center">

<Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
  { 

  task?.person_months.length > 0 && (
    <>
      {/* Budget Contributors Badges */}
      
      { task?.person_months.map((pm) => {
        const initials = pm.user_name.split(' ').map(n => n[0]).join('').toUpperCase();
        // console.log('Rendering PM:', pm);
        return (
          <Chip
            key={pm.user_id}
            label={initials}
            size="small"
            color="secondary"
            variant="filled"
            title={`PM requester: ${pm.user_name}. Click to edit.`}
            onClick={(e) => {
                  e.stopPropagation();
                  setPersonMonthTask(task);
                  setEditingPersonMonth(pm);
                  setPersonMonthDialogOpen(true);
              // setBudgetTaskId(task.id);
              // setEditingBudget(contributor.budgetItem);
              // setBudgetDialogOpen(true);
            }}
            sx={{ 
              height: 16, 
              fontSize: '1rem',
              bgcolor:"#c099be",
              color: '#fff',
              cursor: 'pointer',
              '&:hover': { 
                backgroundColor: 'secondary.main',
              }
            }}
          />
        );
      })}
    </>
  )}
  
  <IconButton
    size="small"
    sx={{ bgcolor: 'secondary.main', color: '#fff', '&:hover': { bgcolor: 'secondary.dark' } }}
    onClick={(e) => {  
      e.stopPropagation();
      setPersonMonthTask(task);
      setPersonMonthDialogOpen(true);
    }}
  >
    <AddIcon fontSize="small"   />
  </IconButton>
</Stack>



        </TableCell>



<TableCell align="center">
          {/* {console.log('Total PMs for task', task.id, ':', task.total_pms)} */}
          {task?.total_pms > 0 ? (
              <Typography variant="body2" fontWeight={600} color="secondary.dark">
                {task.total_pms}
              </Typography>
            ) : "-"}
  </TableCell >

        {isCoordinator && (
          <TableCell align="center" padding="none">
            <IconButton
              size="small"
              color="error"
              title="Delete task"
              onClick={(e) => handleDeleteTask(e, task.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}

      </TableRow>
      
    )
  }
  )} 
  </TableBody>
</Table>

                                <Stack direction="row" spacing={1} mt={2} >
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                      setSelectedWorkPackageId(wp.id);
                                      handleAddTask();
                                    }}
                                  >
                                    Add Task
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditNoteIcon />}
                                    onClick={() => {
                                      setWpNotesTarget(wp);
                                      setWpNotesOpen(true);
                                    }}
                                  >
                                    Notes
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PreviewIcon />}
                                    onClick={() => {
                                      setWpPreviewTarget(wp);
                                      setWpPreviewOpen(true);
                                    }}
                                  >
                                    Preview WP
                                  </Button>
                                </Stack>
                 

                      </CardContent>
                   
                   </Card>
                  );
                })
              ) : (
                <Paper sx={{ p: 8, textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary" paragraph>No work packages available for this proposal</Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={handleAddWorkPackage}
                  >
                    Create Work Package
                  </Button>
                </Paper>
              )}
            </Stack>
          </Box>
        )}

        {/* Budget Tab - shows WP budgets readonly */}
        {activeTab === 3 && (

          <Paper sx={{ p: 3, pt:0, pb:8, mr:1 }}>
            {/* <Typography variant="h6" gutterBottom sx={{ ml:1 }}>Budget Overview by Work Package</Typography> */}
            {/* <Divider sx={{ my: 2 }} /> */}
            {selectedProposal.work_packages && selectedProposal.work_packages.length > 0 ? (
              <Stack spacing={3}>
                {selectedProposal.work_packages.map(wp => {
                  
                  return ( <></>
                    // <Card key={wp.id} variant="outlined">
                    //   <CardContent>
                    //     <Typography variant="h6" gutterBottom>{wp.title}</Typography>
                    //     <Divider sx={{ my: 1 }} />
                        
                    //     {/* Tasks Budget Breakdown */}
                    //     {wpTasks.length > 0 && (
                    //       <Stack spacing={1} sx={{ mb: 2 }}>
                    //         {wpTasks.map(task => (
                    //           task.total_budget > 0 && (
                    //             <Box key={task.id} display="flex" justifyContent="space-between" alignItems="center">
                    //               <Typography variant="body2">{task.title}</Typography>
                    //               <Typography variant="body2" fontWeight="medium">
                    //                 €{parseFloat(task.total_budget).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    //               </Typography>
                    //             </Box>
                    //           )
                    //         ))}
                    //       </Stack>
                    //     )}
                        
                    //     {/* WP Total */}
                    //     <Box 
                    //       display="flex" 
                    //       justifyContent="space-between" 
                    //       alignItems="center"
                    //       sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}
                    //     >
                    //       <Typography variant="subtitle1" fontWeight="bold">Work Package Total</Typography>
                    //       <Typography variant="h6" color="primary" fontWeight="bold">
                    //         €{parseFloat(wpBudget).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    //       </Typography>
                    //     </Box>
                    //   </CardContent>
                    // </Card>
                  );
                })}
                
                {/* Proposal Total */}
                {/* <Box sx={{ pt: 2 }}>
                  <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5">Total Proposal Budget</Typography>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          €{(selectedProposal.calculated_total_budget || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box> */}
              </Stack>
            ) : (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <AttachMoneyIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">No work packages available. Create work packages and tasks first.</Typography>
              </Box>
            )}

            {/* Budget by Organization Table */}
            {selectedProposal.tasks && selectedProposal.tasks.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>Budget Requests by Organization</Typography>
                <Divider sx={{ my: 2 }} />
                <Card variant="outlined">
                  <Box sx={{ overflowX: 'auto' }}>
                

<TableContainer component={Paper} variant="outlined" sx={{ pb: 2 }}>
  <Table size="large" >
    {/* ---------- HEADER ---------- */}
    <TableHead>
      <TableRow sx={{ bgcolor: "grey.100" }}>
        {[
          "Organization",
          "Requester",
          "Consumables",
          "Equipment",
          "Personnel",
          "Travel",
          "Subcontracting",
          "Overheads",
          "Total",
        ].map((header, i) => (
          <TableCell
            key={header}
            align={i >= 2 ? "right" : "left"}
            sx={{
              fontWeight: 600,
              bgcolor: header === "Total" ? "primary.50" : undefined,
            }}
          >
            {header}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>

    {/* ---------- BODY ---------- */}
    <TableBody>
      {Object.entries(byOrganization).map(([orgName, items]) => {
        const totals = {
          consumables: items.reduce((s, i) => s + +i.consumables || 0, 0),
          equipment: items.reduce((s, i) => s + +i.equipment || 0, 0),
          personnel: items.reduce((s, i) => s + +i.personnel || 0, 0),
          travel: items.reduce((s, i) => s + +i.travel || 0, 0),
          subcontracting: items.reduce(
            (s, i) => s + +i.subcontracting || 0,
            0
          ),
          overheads: items.reduce((s, i) => s + +i.overheads || 0, 0),
        };

        const total = Object.values(totals).reduce(
          (s, v) => s + v,
          0
        );

        return (
          <TableRow key={orgName} hover>
            <TableCell sx={{ fontWeight: 500 }}>
              {orgName}
            </TableCell>

            <TableCell sx={{ color: "text.secondary" }}>
              {[...new Set(items.map((i) => i.requester_name))].join(
                ", "
              )}
            </TableCell>

            <TableCell align="right">
              {currency(totals.consumables)}
            </TableCell>
            <TableCell align="right">
              {currency(totals.equipment)}
            </TableCell>
            <TableCell align="right">
              {currency(totals.personnel)}
            </TableCell>
            <TableCell align="right">
              {currency(totals.travel)}
            </TableCell>
            <TableCell align="right">
              {currency(totals.subcontracting)}
            </TableCell>
            <TableCell align="right">
              {currency(totals.overheads)}
            </TableCell>

            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                bgcolor: "primary.50",
              }}
            >
              {currency(total)}
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>

    {/* ---------- FOOTER ---------- */}
    <TableFooter>
      {(() => {
        const grand = {
          consumables: allBudgetItems.reduce(
            (s, i) => s + +i.consumables || 0,
            0
          ),
          equipment: allBudgetItems.reduce(
            (s, i) => s + +i.equipment || 0,
            0
          ),
          personnel: allBudgetItems.reduce(
            (s, i) => s + +i.personnel || 0,
            0
          ),
          travel: allBudgetItems.reduce(
            (s, i) => s + +i.travel || 0,
            0
          ),
          subcontracting: allBudgetItems.reduce(
            (s, i) => s + +i.subcontracting || 0,
            0
          ),
          overheads: allBudgetItems.reduce(
            (s, i) => s + +i.overheads || 0,
            0
          ),
        };

        const total = Object.values(grand).reduce(
          (s, v) => s + v,
          0
        );

        return (
          <TableRow sx={{ bgcolor: "grey.100" }}>
            <TableCell
              colSpan={2}
              sx={{ fontWeight: 600, fontSize: "1rem" }}
            >
              GRAND TOTAL
            </TableCell>

            {Object.values(grand).map((v, i) => (
              <TableCell
                key={i}
                align="right"
                sx={{ fontWeight: 600 }}
              >
                {currency(v)}
              </TableCell>
            ))}

            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              {currency(total)}
            </TableCell>
          </TableRow>
        );
      })()}
    </TableFooter>
  </Table>
</TableContainer>




                  </Box>
                </Card>
              </Box>
            )}
          </Paper>
        )}

        {/* Gender Equality Tab */}
        {/* {activeTab === 4 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Gender Equality Plan</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography color="text.secondary">
              Describe measures to ensure gender equality in the project.
            </Typography>
          </Paper>
        )} */}

        {/* Ethics Tab */}
        {/* {activeTab === 5 && (
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Typography variant="h6">Ethics Assessment</Typography>
              <Chip 
                label={selectedProposal.ethics_flag ? 'Ethics Review Required' : 'No Ethics Issues'} 
                color={selectedProposal.ethics_flag ? 'warning' : 'success'}
              />
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography color="text.secondary" paragraph>
              Identify and address any ethical issues related to the project.
            </Typography>
            <TextField
              multiline
              rows={10}
              fullWidth
              placeholder="Describe ethical considerations, if any..."
              sx={{ mb: 2 }}
            />
            <Button variant="contained" startIcon={<SaveIcon />}>
              Save Ethics Information
            </Button>
          </Paper>
        )} */}

        {/* Dissemination Tab */}
        {activeTab === 4 && selectedProposal && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 300px', lg: '1fr 340px', xl: '1fr 380px' },
            gap: 2,
            pr: 1,
            pb: 2,
            alignItems: 'start',
          }}>
            {/* Left / main column — full width on xs/sm */}
            <Box sx={{ minWidth: 0 }}>
              <DisseminationTab proposal={selectedProposal} />
              <Box sx={{ mb: 3 }} />
              <MemberResourcesCard
                resources={memberResources}
                loading={loadingMemberResources}
                currentUserId={String(currentUser?.id || '')}
                isCoordinator={isCoordinator}
                selected={memberResourcesSelected}
                onSelectedChange={setMemberResourcesSelected}
              />
            </Box>
            {/* Right sidebar — stacks below on xs/sm */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <AdvisoryBoardCard
                members={advisoryBoard}
                consortiumUsers={consortiumUsers}
                onAdd={(member) => setAdvisoryBoard(prev => [...prev, { ...member, id: Date.now() }])}
                onRemove={(id) => setAdvisoryBoard(prev => prev.filter(m => m.id !== id))}
              />
              <EventSuggestionCard callId={selectedProposal?.call} />
            </Box>
          </Box>
        )}

        {/* Authors Tab (publications) */}
        {activeTab === 7 && isPublication && (
          <AuthorsPanel
            pub={selectedProposal}
            members={consortiumUsers}
            canEdit={(consortiumUsers || []).some(u => String(u.id) === String(currentUser?.id))}
            onSave={savePublicationFields}
          />
        )}

        {/* Bibliography Tab (publications) */}
        {activeTab === 8 && isPublication && (
          <ReferencesPanel
            references={selectedProposal.references || []}
            canEdit={(consortiumUsers || []).some(u => String(u.id) === String(currentUser?.id))}
            onChange={saveReferences}
            onRebuildReferences={() => rebuildReferencesSection()}
            style={citationStyle}
            styleTitle={selectedProposal.citation_style_title}
            onStyleChange={(style) => applyCitationStyle(style)}
          />
        )}

        {/* Preview Tab */}
        {activeTab === 5 && (
          <Paper ref={previewRef} sx={{ p: 3, mr:1, position: 'relative', pl: isPublication ? 7 : 3 }}>
            {/* Publications are previewed with continuous line numbers, as journals require */}
            {isPublication && <LineNumberGutter containerRef={previewRef} />}

            
            <Box sx={{ py: 0 }} >
      



<Paper 
  elevation={0} 
  sx={{ 
    p: 3,
    pt: 1, 
    mb: 4, 
    borderBottom: '1px solid', 
    borderColor: 'divider',
    borderRadius: 1 
  }}
>


  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">

    <Box>
      <Typography variant="h5" fontWeight={600} paragraph>
        {selectedProposal.acronym && <><strong>{selectedProposal.acronym}</strong>{' — '}</>}{selectedProposal.title}
      </Typography>

      {isPublication && (selectedProposal.authors || []).length > 0 && (
        <Typography variant="body1" {...previewEditable({ part: 'authors' })} sx={{ mb: 1.5, ...previewEditSx }}>
          {selectedProposal.authors.map((a, i) => (
            <React.Fragment key={`${a.name}-${i}`}>
              {i > 0 && ', '}{a.name}{a.is_corresponding ? '*' : ''}
              {a.affiliation && <Typography component="span" variant="caption" color="text.secondary"> ({a.affiliation})</Typography>}
            </React.Fragment>
          ))}
        </Typography>
      )}

      {isPublication ? (
      <Stack direction="row" spacing={3}>
        <Box>
          <Typography variant="caption" color="text.secondary">Type</Typography>
          <Typography variant="body1">{publicationTypeLabel(selectedProposal.publication_type)}</Typography>
        </Box>
        {selectedProposal.target_journal && (
          <Box>
            <Typography variant="caption" color="text.secondary">Target journal</Typography>
            <Typography variant="body1">{selectedProposal.target_journal}</Typography>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary">Status</Typography>
          <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{selectedProposal.status.replace(/_/g, ' ')}</Typography>
        </Box>
      </Stack>
      ) : (
      <Stack direction="row" spacing={3}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Duration
          </Typography>
          <Typography variant="body1">
            {selectedProposal.duration_months} months
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Budget
          </Typography>
          <Typography variant="body1">
            {selectedProposal.total_budget ?
              `\u20ac${parseFloat(selectedProposal.total_budget).toLocaleString()}`
              : '\u2014'
            }
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Status
          </Typography>
          <Typography variant="body1">
            {selectedProposal.status.replace('_', ' ')}
          </Typography>
        </Box>

      </Stack>
      )}
    </Box>
  </Stack>
</Paper>




<Paper 
  elevation={0} 
  sx={{ 
    p: 3, 
    mb: 4, 
    border: '1px solid', 
    borderColor: 'divider',
    borderRadius: 1 
  }}
>

              {/* <Typography variant="h5" gutterBottom></Typography> */}
              
              {selectedProposal.abstract && (
                <Box {...previewEditable({ part: 'abstract' })} sx={previewEditSx}>
                  <Typography variant="h6" gutterBottom>ABSTRACT</Typography>
                  <Box sx={{ typography: 'body1' }}>
                    {renderMarkdownToReact(selectedProposal.abstract, { figureCounter: previewFigures, figures: figuresMap })}
                  </Box>
                </Box>
              )}
              
         
 </Paper>             




{/* Table: Consortium Members */}
{!isPublication && selectedProposal.consortium_members && selectedProposal.consortium_members.length > 0 && (
  <>
    <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2, ml:2 }}>
       Consortium Members
    </Typography>

    <Card variant="outlined" sx={{ mb: 4 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={th} align="center">Number</TableCell>
              <TableCell sx={th}>Organization</TableCell>
              <TableCell sx={th}>Short Name</TableCell>
              <TableCell sx={th}>Country</TableCell>
              <TableCell sx={th}>Organization Type</TableCell>
              <TableCell sx={th} align="right">Total Person-Months</TableCell>
              <TableCell sx={th} align="right">Total Budget (€)</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {selectedProposal.consortium_members
              .sort((a, b) => {
                // Sort by participant number (P01, P02, etc.)
                const numA = a.number;
                const numB = b.number;
                return numA - numB;
              })
              .map((member) => (
                <TableRow 
                  key={member.id} 
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {member.number || '—'}
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {member.name || '—'}
                      </Typography>
                      {member.email && (
                        <Typography variant="caption" color="text.secondary">
                          {member.email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    {member.short_name || '—'}
                  </TableCell>
                  
                  <TableCell>
                    {member.country ? (
                      <Chip
                        size="small"
                        label={ `${member.country}`}
                        variant="outlined"
                      />
                    ) : '—'}
                  </TableCell>
                  
                  <TableCell>
                    {member.org_type ? (
                      <Chip 
                        size="small" 
                        label={member.org_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        color={
                          member.org_type?.toLowerCase().includes('academic') ? 'primary' :
                          member.org_type?.toLowerCase().includes('industry') ? 'secondary' :
                          member.org_type?.toLowerCase().includes('sme') ? 'success' :
                          member.org_type?.toLowerCase().includes('ngo') ? 'warning' :
                          'default'
                        }
                        variant="outlined"
                      />
                    ) : '—'}
                  </TableCell>
                  
                  <TableCell align="right">
                    <Typography fontWeight="medium">
                      {member.total_person_months?.toLocaleString() || '0'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {member.total_budget ? 
                        `€${member.total_budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                        : '€0.00'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            
            {/* Totals row */}
            {selectedProposal.consortium_members.length > 0 && (
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                  Totals:
                </TableCell>
                
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {selectedProposal.consortium_members
                    .reduce((sum, member) => sum + (member.total_person_months || 0), 0)
                    .toLocaleString()}
                </TableCell>
                
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  €{selectedProposal.consortium_members
                    .reduce((sum, member) => sum + (member.total_budget || 0), 0)
                    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </>
)}


              {/* Work Packages */}
              {/* {selectedProposal.work_packages && selectedProposal.work_packages.length > 0 && ( 
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>Work Packages</Typography>
                  {selectedProposal.work_packages.map((wp) => (
                    <Card key={wp.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                          <Chip label={`WP${wp.number}`} color="primary" size="small" />
                          <Typography variant="h6">{wp.title}</Typography> 
                            {wp.description && (
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {wp.description}
                          </Typography>
                        )}
                        </Stack>
                      
                        <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Lead</Typography>
                            <Typography variant="body2">{leadersById[wp.wp_leader]?.organization?.name || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Start Month</Typography>
                            <Typography variant="body2">{wp.start_month || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">End Month</Typography>
                            <Typography variant="body2">{wp.end_month || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Duration</Typography>
                            <Typography variant="body2">{wp.duration_months || ''}</Typography>
                          </Box>
                        </Stack>
                        
                          <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                          <Box>
                            
                            <Typography variant="body2">{wp?.organizations.map(org => org.name).join(', ') || '—'}</Typography>
                          </Box>
                          {/* <Box>
                            
                            <Typography variant="body2">{wp.start_month || 'N/A'}</Typography>
                          </Box>
                        
                        </Stack>


                      </CardContent>
                    </Card>
                  ))}
                </>
                 )}  */}

{/* Section Contents */}

<Paper 
  elevation={0} 
  sx={{ 
    p: 3,
    pb: 1,
    pt: 1, 
    mb: 4, 
    border: '1px solid', 
    borderColor: 'divider',
    borderRadius: 1 
  }}
>
    {selectedProposal.sections && selectedProposal.sections.length > 0 && (
      <>
        {/* <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>BACKGROUND</Typography> */}
        {selectedProposal.sections.filter(s => s.section_key !== 'abstract' && s.section_key !== 'appendix').map((section) => (
          <Box key={section.id} {...previewEditable({ section })} sx={{ mb: 2, mt: 2, ...previewEditSx }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="h6" color="primary">
                {section.section_key}. {section.title.toUpperCase()}
              </Typography>
            </Stack>

            {section.current_content ? (
              <>
                <Box sx={{ typography: 'body1', mb: 2 }}>
                  {renderMarkdownToReact(previewBody(section), { figureCounter: previewFigures, figures: figuresMap, citations: previewCitations(section) })}
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Words: {getWordCount(section.current_content.body)}
                    {section.word_limit && ` / ${section.word_limit}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Author: {section.current_content.author_name}
                  </Typography>
                  {section.current_content.assignee_names && section.current_content.assignee_names.length > 0 && (
                    <>
                      <Typography variant="caption" color="text.secondary">•</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Assignees: {section.current_content.assignee_names.map(a => a.name).join(', ')}
                      </Typography>
                    </>
                  )}
                </Stack>
              </>
            ) : (
              !(section.subsections && section.subsections.length > 0) && (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 1 }}>
                  No content available for this section
                </Typography>
              )
            )}

            {/* Subsections */}
            {section.subsections && section.subsections.length > 0 && (
              <Box sx={{ ml: 3, mt: 1 }}>
                {section.subsections.map((subsection) => (
                  <Box key={subsection.id} {...previewEditable({ section: subsection })} sx={{ mb: 2, ...previewEditSx }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                        {subsection.section_key}. {subsection.title}
                      </Typography>
                    </Stack>

                    {subsection.current_content ? (
                      <>
                        <Box sx={{ typography: 'body1', mb: 1 }}>
                          {renderMarkdownToReact(previewBody(subsection), { figureCounter: previewFigures, figures: figuresMap, citations: previewCitations(subsection) })}
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Words: {getWordCount(subsection.current_content.body)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">•</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Author: {subsection.current_content.author_name}
                          </Typography>
                        </Stack>
                      </>
                    ) : (
                      !(subsection.subsections && subsection.subsections.length > 0) && (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 1 }}>
                          No content available
                        </Typography>
                      )
                    )}

                    {/* Level-3 subsections */}
                    {subsection.subsections && subsection.subsections.length > 0 && (
                      <Box sx={{ ml: 3, mt: 0.5 }}>
                        {subsection.subsections.map((sub2) => (
                          <Box key={sub2.id} {...previewEditable({ section: sub2 })} sx={{ mb: 1.5, ...previewEditSx }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              {sub2.section_key}. {sub2.title}
                            </Typography>
                            {sub2.current_content ? (
                              <Box sx={{ typography: 'body2', mb: 1 }}>
                                {renderMarkdownToReact(previewBody(sub2), { figureCounter: previewFigures, figures: figuresMap, citations: previewCitations(sub2) })}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                No content available
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            <Divider sx={{ mt: 1 }} />
          </Box>
        ))}
      </>
    )}
</Paper>

{/* AI Review Dialog — history list / single-history view / confirm / progress / results */}
{(() => {
  const reviewData = selectedHistoryReview?.result ?? aiReviewResult;
  const isShowingResult = !!reviewData;
  const isHistoryList = aiReviewHistoryMode && !selectedHistoryReview && !aiReviewResult;
  const isHistoryDetail = aiReviewHistoryMode && !!selectedHistoryReview;

  const closeDialog = () => {
    if (aiReviewLoading) return;
    setAiReviewDialogOpen(false);
    setAiReviewResult(null);
    setAiReviewError(null);
    setAiReviewHistoryMode(false);
    setSelectedHistoryReview(null);
  };

  const ReviewResultPanel = ({ data }) => (
    <Stack spacing={3}>
      {data.overall_summary && (
        <Typography variant="body1" color="text.secondary" fontStyle="italic">
          {data.overall_summary}
        </Typography>
      )}
      {(data.strengths?.length > 0 || data.weaknesses?.length > 0) && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {data.strengths?.length > 0 && (
            <Paper variant="outlined" sx={{ flex: 1, p: 2, borderColor: 'success.light' }}>
              <Typography variant="subtitle2" color="success.dark" fontWeight={700} gutterBottom>Strengths</Typography>
              {data.strengths.map((s, i) => <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {s}</Typography>)}
            </Paper>
          )}
          {data.weaknesses?.length > 0 && (
            <Paper variant="outlined" sx={{ flex: 1, p: 2, borderColor: 'error.light' }}>
              <Typography variant="subtitle2" color="error.dark" fontWeight={700} gutterBottom>Weaknesses</Typography>
              {data.weaknesses.map((w, i) => <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {w}</Typography>)}
            </Paper>
          )}
        </Stack>
      )}
      {data.sections?.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Section-by-Section Feedback</Typography>
          <Stack spacing={1.5}>
            {data.sections.map((sec, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2" fontWeight={600} flex={1}>{sec.title}</Typography>
                  <Chip size="small" label={`${sec.score ?? '—'}/10`}
                    color={(sec.score ?? 0) >= 7 ? 'success' : (sec.score ?? 0) >= 5 ? 'warning' : 'error'}
                    variant="outlined" />
                </Stack>
                {sec.feedback && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{sec.feedback}</Typography>}
                {sec.suggestions?.length > 0 && (
                  <Stack spacing={0.25}>
                    {sec.suggestions.map((sug, j) => <Typography key={j} variant="body2" color="secondary.main">→ {sug}</Typography>)}
                  </Stack>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
      {data.top_suggestions?.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Top Improvement Suggestions</Typography>
          <Stack spacing={0.75}>
            {data.top_suggestions.map((sug, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                <Chip size="small" label={i + 1} color="secondary"
                  sx={{ minWidth: 28, height: 22, fontWeight: 700, flexShrink: 0, mt: 0.1 }} />
                <Typography variant="body2">{sug}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );

  return (
    <Dialog
      open={aiReviewDialogOpen}
      onClose={closeDialog}
      maxWidth={isShowingResult || isHistoryList ? 'md' : 'sm'}
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 1, maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {isHistoryDetail && (
            <IconButton size="small" onClick={() => setSelectedHistoryReview(null)} title="Back to history list">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <RateReviewIcon color={isShowingResult ? 'secondary' : 'primary'} />
          <Typography variant="h6" fontWeight={700} flex={1}>
            {isHistoryList ? 'Previous AI Reviews' :
             isHistoryDetail ? `AI Review — ${new Date(selectedHistoryReview.created).toLocaleString()}` :
             isShowingResult ? 'AI Review Report' : 'AI Review'}
          </Typography>
          {reviewData?.overall_score != null && (
            <Chip
              label={`Score: ${reviewData.overall_score} / 10`}
              color={reviewData.overall_score >= 7 ? 'success' : reviewData.overall_score >= 5 ? 'warning' : 'error'}
              variant="filled" sx={{ fontWeight: 700 }}
            />
          )}
          {!aiReviewLoading && (
            <IconButton size="small" onClick={closeDialog}><CloseIcon fontSize="small" /></IconButton>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: isShowingResult || isHistoryList ? 3 : 2 }}>
        {/* ── Loading ── */}
        {aiReviewLoading && (
          <Stack alignItems="center" spacing={2.5} sx={{ py: 6 }}>
            <CircularProgress size={56} thickness={3} />
            <Typography variant="subtitle1" fontWeight={600}>Analysing proposal…</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={360}>
              The AI is reviewing your proposal against the funding call criteria.
              This may take up to a minute — please keep this window open.
            </Typography>
          </Stack>
        )}

        {/* ── History list ── */}
        {!aiReviewLoading && isHistoryList && (
          <Stack spacing={1.5}>
            {aiReviewHistory.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No previous reviews found.</Typography>
            ) : aiReviewHistory.map((rec) => (
              <Paper
                key={rec.id}
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                onClick={() => setSelectedHistoryReview(rec)}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box flex={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {new Date(rec.created).toLocaleString()}
                    </Typography>
                    {rec.reviewed_by && (
                      <Typography variant="caption" color="text.secondary">by {rec.reviewed_by}</Typography>
                    )}
                  </Box>
                  {rec.overall_score != null && (
                    <Chip
                      size="small"
                      label={`${rec.overall_score}/10`}
                      color={rec.overall_score >= 7 ? 'success' : rec.overall_score >= 5 ? 'warning' : 'error'}
                      variant="outlined"
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {rec.tokens_used ? `${rec.tokens_used} tokens` : ''}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {/* ── History detail or new result ── */}
        {!aiReviewLoading && isShowingResult && <ReviewResultPanel data={reviewData} />}

        {/* ── Error ── */}
        {!aiReviewLoading && aiReviewError && !aiReviewResult && !aiReviewHistoryMode && (
          <Stack spacing={2} sx={{ py: 1 }}>
            <Alert severity="error" onClose={() => setAiReviewError(null)}>{aiReviewError}</Alert>
            <Alert severity="info" icon={<InfoIcon fontSize="small" />}>
              Make sure your proposal sections contain content before starting.
            </Alert>
          </Stack>
        )}

        {/* ── Confirm ── */}
        {!aiReviewLoading && !aiReviewResult && !aiReviewError && !aiReviewHistoryMode && (
          <Stack spacing={2} sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              The AI will review your proposal sections, evaluate them against the
              funding call criteria, and provide a score with detailed feedback on
              strengths, weaknesses, and suggestions for improvement.
            </Typography>
            <Alert severity="info" icon={<InfoIcon fontSize="small" />}>
              Starting the review will activate AI features for this proposal.
              Make sure your proposal sections contain content before starting.
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {isHistoryDetail ? (
          <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedHistoryReview(null)}>
            Back to History
          </Button>
        ) : isHistoryList ? (
          <Button onClick={closeDialog}>Close</Button>
        ) : isShowingResult ? (
          <Button variant="contained" onClick={closeDialog}>Close</Button>
        ) : (
          <>
            <Button onClick={closeDialog} disabled={aiReviewLoading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmAIReview}
              disabled={aiReviewLoading}
              startIcon={aiReviewLoading ? <CircularProgress size={16} color="inherit" /> : <RateReviewIcon />}
            >
              {aiReviewLoading ? 'Reviewing…' : aiReviewError ? 'Retry' : 'Start Review'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
})()}

{/* Warnings Dialog */}
{warningsOpen && selectedProposal && (() => {
  const { items, grouped } = computeWarnings(selectedProposal);
  const errorCount   = items.filter(i => i.severity === 'error').length;
  const warningCount = items.filter(i => i.severity === 'warning').length;
  const infoCount    = items.filter(i => i.severity === 'info').length;
  const doneCount    = checkedWarnings.size;
  const pct          = items.length > 0 ? Math.round((doneCount / items.length) * 100) : 100;

  const SevIcon = ({ severity, sx }) => {
    if (severity === 'error')   return <ErrorOutlineIcon fontSize="small" color="error" sx={sx} />;
    if (severity === 'warning') return <WarningAmberIcon fontSize="small" color="warning" sx={sx} />;
    return <InfoOutlinedIcon fontSize="small" color="info" sx={sx} />;
  };

  const toggleCheck = (idx) => {
    setCheckedWarnings(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  let globalIdx = 0;

  return (
    <Dialog open={warningsOpen} onClose={() => setWarningsOpen(false)} maxWidth="md" fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 1 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <WarningAmberIcon color={errorCount > 0 ? 'error' : 'warning'} />
          <Box flex={1}>
            <Typography variant="h6" fontWeight={700}>Proposal Completeness</Typography>
            <Typography variant="caption" color="text.secondary">
              Check off items as you complete them
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setWarningsOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {/* Summary chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {errorCount > 0   && <Chip icon={<ErrorOutlineIcon />}   label={`${errorCount} Missing`}     color="error"   size="small" variant="outlined" />}
          {warningCount > 0 && <Chip icon={<WarningAmberIcon />}   label={`${warningCount} Incomplete`} color="warning" size="small" variant="outlined" />}
          {infoCount > 0    && <Chip icon={<InfoOutlinedIcon />}   label={`${infoCount} Suggested`}     color="info"    size="small" variant="outlined" />}
          {items.length === 0 && <Chip icon={<CheckBoxIcon />} label="All complete!" color="success" size="small" />}
        </Stack>

        {/* Progress */}
        {items.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {doneCount} of {items.length} resolved
              </Typography>
              <Typography variant="caption" fontWeight={600} color={pct === 100 ? 'success.main' : 'text.secondary'}>
                {pct}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={pct === 100 ? 'success' : pct >= 60 ? 'warning' : 'error'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckBoxIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600} color="success.main">
              No issues found!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This proposal appears complete.
            </Typography>
          </Box>
        ) : (
          Object.entries(grouped).map(([category, catItems]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, pb: 0.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ flex: 1 }}>
                  {category}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {catItems.length} item{catItems.length !== 1 ? 's' : ''}
                </Typography>
              </Stack>
              {catItems.map((item) => {
                const idx = globalIdx++;
                const done = checkedWarnings.has(idx);
                return (
                  <Stack key={idx} direction="row" alignItems="flex-start" spacing={1}
                    sx={{
                      py: 0.75, px: 1, borderRadius: 1, cursor: 'pointer',
                      opacity: done ? 0.45 : 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => toggleCheck(idx)}
                  >
                    {done
                      ? <CheckBoxIcon fontSize="small" color="success" sx={{ mt: 0.1, flexShrink: 0 }} />
                      : <CheckBoxBlankIcon fontSize="small" sx={{ mt: 0.1, flexShrink: 0, color: 'text.disabled' }} />
                    }
                    <SevIcon severity={item.severity} sx={{ mt: 0.1, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{ textDecoration: done ? 'line-through' : 'none', color: done ? 'text.disabled' : 'text.primary' }}
                    >
                      {item.message}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
          ))
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          Click items to mark as resolved
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadWarnings} variant="outlined">
            Download Checklist (.docx)
          </Button>
          <Button size="small" onClick={() => setWarningsOpen(false)}>Close</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
})()}

{/* AI review results are shown inside the AI Review dialog */}



{/* Implementation Section */}
{/* <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 3 }}>IMPLEMENTATION</Typography> */}

{!isPublication && (() => {
  const { tableLabels, allDeliverables, allMilestones, allRisks } = computeWorkPlanTableData(selectedProposal);

  return (
    <>
      {/* List of Work Packages */}
      {tableLabels.wpList && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>
            {tableLabels.wpList}
          </Typography>

          <Card variant="outlined" sx={{ mb: 4 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={th}>
                      Work Package No
                    </TableCell>
                    <TableCell sx={th}>
                      Work Package Title
                    </TableCell>
                    <TableCell sx={th}>
                      Lead Participant No
                    </TableCell>
                    <TableCell sx={th} align="right">
                      Person-Months
                    </TableCell>
                    <TableCell sx={th} align="right">
                      Start Month
                    </TableCell>
                    <TableCell sx={th} align="right">
                      End Month
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedProposal.work_packages.map((wp, index) => (
                    <TableRow
                      key={wp.id || index}
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {wp.number}
                      </TableCell>
                      <TableCell>
                        {wp.title}
                      </TableCell>
                      <TableCell>
                        {wp?.wp_leader_org_name || '—'} ({wp?.wp_leader_org_short_name || '—'})
                      </TableCell>
                      <TableCell align="right">
                        {wp.total_person_months || '—'}
                      </TableCell>
                      <TableCell align="right">
                        {wp.start_month || '—'}
                      </TableCell>
                      <TableCell align="right">
                        {wp.end_month || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Work Package Descriptions */}
      {tableLabels.wpDesc && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>
            {tableLabels.wpDesc}
          </Typography>

          {selectedProposal.work_packages.map((selectedWorkPackage) => (
            <React.Fragment key={selectedWorkPackage.id || selectedWorkPackage.wp_number}>
              <Card variant="outlined" sx={{ mb: 4 }}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: '30%',
                            backgroundColor: 'action.hover',
                          }}
                        >
                          Work package number
                        </TableCell>
                        <TableCell>
                          {selectedWorkPackage.number}
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            backgroundColor: 'action.hover',
                          }}
                        >
                          Work package title
                        </TableCell>
                        <TableCell>
                          {selectedWorkPackage.title}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            verticalAlign: 'top',
                            backgroundColor: 'action.hover',
                          }}
                        >
                          Description of work
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography> {selectedWorkPackage.description || "—"}</Typography>
                                {selectedWorkPackage.tasks && selectedWorkPackage.tasks.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'none' }}>
                                    {selectedWorkPackage.tasks.map((task, index) => {
                                      const mEnd = task.start_month && task.duration_months
                                        ? task.start_month + task.duration_months - 1
                                        : null;
                                      const months = task.start_month
                                        ? ` (M${task.start_month}${mEnd ? ` - M${mEnd}` : ''})`
                                        : '';
                                      return (
                                        <li key={index}>
                                          <strong>Task {selectedWorkPackage.number}.{task.number} - {task.title}{months}:</strong>{' '}{task.description}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  "—"
                                )}
                          </Stack>
                        </TableCell>
                      </TableRow>

                      <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            verticalAlign: 'top',
                            backgroundColor: 'action.hover',
                          }}
                        >
                          Objectives
                        </TableCell>
                        <TableCell>
                          {selectedWorkPackage?.objectives && selectedWorkPackage.objectives.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'none' }}>
                              {selectedWorkPackage.objectives.map((obj, index) => (
                                <li key={index}>
                                  <strong>O{selectedWorkPackage.number}.{index + 1}</strong>{' - '}{obj.description}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </React.Fragment>
          ))}
        </>
      )}

      {/* List of Deliverables */}
      {tableLabels.deliverables && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>
            {tableLabels.deliverables}
          </Typography>

          <Card variant="outlined" sx={{ mb: 4 }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={th}>Number</TableCell>
                    <TableCell sx={th}>Deliverable name</TableCell>
                    <TableCell sx={th}>Short description</TableCell>
                    <TableCell align="center" sx={th}>WP No</TableCell>
                    <TableCell sx={th}>Lead participant</TableCell>
                    <TableCell align="center" sx={th}>Type</TableCell>
                    <TableCell align="center" sx={th}>Dissemination level</TableCell>
                    <TableCell align="center" sx={th}>Delivery month</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {allDeliverables.map((deliv, index) => (
                    <TableRow
                      key={deliv.id || index}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell align="center">
                        {deliv.displayCode ?? deliv.code ?? '—'}
                      </TableCell>
                      <TableCell>
                        {deliv.title || '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        {deliv.description || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {deliv.wp_number || '—'}
                      </TableCell>
                      <TableCell>
                        {deliv.assignee?.organization?.name || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {deliv.type}
                      </TableCell>
                      <TableCell align="center">
                        {deliv.dissemination_level}
                      </TableCell>
                      <TableCell align="center">
                        {deliv.delivery_month ? `M${deliv.delivery_month}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* List of Milestones */}
      {tableLabels.milestones && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>
            {tableLabels.milestones}
          </Typography>

          <Card variant="outlined" sx={{ mb: 4 }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={th}>
                      Milestone number
                    </TableCell>
                    <TableCell sx={th}>
                      Milestone name
                    </TableCell>
                    <TableCell sx={th}>
                      Related work package(s)
                    </TableCell>
                    <TableCell align="center" sx={th}>
                      Due date (month)
                    </TableCell>
                    <TableCell sx={th}>
                      Means of verification
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {allMilestones.map((ms, index) => (
                    <TableRow
                      key={ms.id || index}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell align="center">
                        {ms?.displayCode ?? ms?.code ?? '—'}
                      </TableCell>

                      <TableCell>
                        {ms.description || '—'}
                      </TableCell>

                      <TableCell>
                        {  ms?.wp_number }
                      </TableCell>

                      <TableCell align="center">
                        {ms?.due_month ? `M${ms.due_month}` : '—'}
                      </TableCell>

                      <TableCell sx={{ maxWidth: 350 }}>
                        {ms.verification_means || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Critical Risks for Implementation */}
      {tableLabels.risks && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>
            {tableLabels.risks}
          </Typography>

          <Card variant="outlined" sx={{ mb: 4 }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ ...th, width: 80 }}>Number</TableCell>
                    <TableCell sx={th}>
                      Description of risk (indicate level of (i) likelihood, and (ii) severity: Low/Medium/High)
                    </TableCell>
                    <TableCell sx={th}>
                      Work package(s) involved
                    </TableCell>
                    <TableCell sx={th}>
                      Proposed risk-mitigation measures
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {allRisks.map((risk, index) => (
                    <TableRow
                      key={risk.id || index}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell align="center" sx={{ width: 80, whiteSpace: 'nowrap' }}>
                        <strong>{risk.displayCode ?? '—'}</strong>
                      </TableCell>
                      <TableCell>
                        <div>
                          {risk.description && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {risk.description}
                            </Typography>
                          )}
                          <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                            <Chip
                              size="small"
                              label={`Likelihood: ${risk.likelihood || 'Not specified'}`}
                              color={risk.likelihood === 'High' ? 'error' : risk.likelihood === 'Medium' ? 'warning' : 'default'}
                              variant="outlined"
                            />
                            <Chip
                              size="small"
                              label={`Severity: ${risk.impact || 'Not specified'}`}
                              color={risk.impact === 'High' ? 'error' : risk.impact === 'Medium' ? 'warning' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        </div>
                      </TableCell>

                      <TableCell>
                        { risk?.wp_number }
                      </TableCell>

                      <TableCell sx={{ maxWidth: 400 }}>
                        {risk.mitigation_plan || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}

      {/* Summary of Staff Effort */}
      {tableLabels.staffEffort && (() => {
        // Calculate totals from actual per-WP data
        const wpTotals = {};
        const participantTotals = {};

        selectedProposal.work_packages.forEach(wp => {
          wpTotals[wp.number] = 0;
        });

        selectedProposal.consortium_members.forEach(member => {
          let participantTotal = 0;

          selectedProposal.work_packages.forEach(wp => {
            const wpEffort = member.effort_by_wp?.[wp.number] || 0;
            participantTotal += wpEffort;
            wpTotals[wp.number] = (wpTotals[wp.number] || 0) + wpEffort;
          });

          participantTotals[member.id] = participantTotal;
        });

        const grandTotal = Object.values(participantTotals).reduce((sum, total) => sum + total, 0);

        return (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>
              {tableLabels.staffEffort}
            </Typography>

            <Card variant="outlined" sx={{ mb: 4, overflowX: 'auto' }}>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...th, minWidth: 200 }}>
                        Participant Number/Short Name
                      </TableCell>

                      {selectedProposal.work_packages.map((wp) => {
                        if (!wp || !wp.number) return null;
                        return (
                          <TableCell
                            key={`wp-header-${wp.number}-${wp.id || 'no-id'}`}
                            align="center"
                            sx={th}
                          >
                            {`WP${wp.number}`}
                          </TableCell>
                        );
                      }).filter(Boolean)}

                      <TableCell align="center" sx={th}>
                        Total Person-Months per Participant
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {selectedProposal.consortium_members.map((member) => (
                      <TableRow
                        key={member.id}
                        hover
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ fontWeight: 'medium' }}>
                          {`${member.number} / ${member.name}`}
                        </TableCell>

                        {selectedProposal.work_packages.map((wp) => (
                          <TableCell
                            key={`effort-${member.id}-wp${wp.number}`}
                            align="center"
                          >
                            {member.effort_by_wp?.[wp.number] || 0}
                          </TableCell>
                        ))}

                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {participantTotals[member.id] || 0}
                        </TableCell>
                      </TableRow>
                    ))}

                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Total Person Months
                      </TableCell>

                      {selectedProposal.work_packages.map((wp) => (
                        <TableCell
                          key={`total-wp${wp.number}`}
                          align="center"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {wpTotals[wp.number] || 0}
                        </TableCell>
                      ))}

                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {grandTotal}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        );
      })()}

      {/* Purchase Costs (Equipment) */}
      {tableLabels.equipment && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>{tableLabels.equipment}</Typography>
          <Card variant="outlined" sx={{ mb: 4 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600, minWidth: '200px' }}>
                      Participant
                    </th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #ddd', fontWeight: 600, minWidth: '120px' }}>
                      Cost (€)
                    </th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600, minWidth: '300px' }}>
                      Justification
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const equipmentByOrg = {};

                    selectedProposal.tasks.forEach(task => {
                      task.budget_items?.forEach(item => {
                        if (parseFloat(item.equipment || 0) > 0) {
                          const orgName = item.organization_name || 'Unknown';
                          if (!equipmentByOrg[orgName]) {
                            equipmentByOrg[orgName] = {
                              cost: 0,
                              justifications: []
                            };
                          }
                          equipmentByOrg[orgName].cost += parseFloat(item.equipment);
                          if (item.equipment_justification) {
                            equipmentByOrg[orgName].justifications.push(item.equipment_justification);
                          }
                        }
                      });
                    });

                    return Object.entries(equipmentByOrg).map(([orgName, data]) => (
                      <tr key={orgName} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: 500 }}>{orgName}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          €{data.cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {data.justifications.length > 0 ? data.justifications.join('; ') : '—'}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </Box>
          </Card>
        </>
      )}
    </>
  );
})()}



{/* Gantt Chart */}
{!isPublication && (() => {
  const allTasks = (selectedProposal.tasks || [])
    .filter(t => t.start_month != null && t.duration_months != null)
    .map(t => ({ ...t, end_month: t.start_month + t.duration_months - 1 }));
  if (allTasks.length === 0) return null;

  const maxMonth = selectedProposal.duration_months ||
    Math.max(...allTasks.map(t => t.end_month), 12);
  const monthWidth = 100 / maxMonth;

  const WP_PALETTE = [
    { dark: '#1565c0', light: '#90caf9' },
    { dark: '#2e7d32', light: '#a5d6a7' },
    { dark: '#c84b00', light: '#ffcc80' },
    { dark: '#6a1b9a', light: '#ce93d8' },
    { dark: '#b71c1c', light: '#ef9a9a' },
    { dark: '#00695c', light: '#80cbc4' },
    { dark: '#283593', light: '#9fa8da' },
    { dark: '#4e342e', light: '#bcaaa4' },
  ];

  const wps = (selectedProposal.work_packages || [])
    .slice().sort((a, b) => (a.number || 0) - (b.number || 0));
  const tasksByWP = {};
  allTasks.forEach(t => {
    const key = t.work_package || '__none__';
    if (!tasksByWP[key]) tasksByWP[key] = [];
    tasksByWP[key].push(t);
  });

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml: 2 }}>Project Timeline</Typography>
      <Card>
        <CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            {/* Month header — numbers only */}
            <Box sx={{ display: 'flex', mb: 0.5, ml: '200px', borderBottom: '2px solid', borderColor: 'divider', pb: 0.5, minWidth: `${maxMonth * 18}px` }}>
              {Array.from({ length: maxMonth }, (_, i) => (
                <Box key={i} sx={{ flex: `0 0 ${monthWidth}%`, textAlign: 'center', fontSize: '0.65rem', color: 'text.secondary' }}>
                  {i + 1}
                </Box>
              ))}
            </Box>

            {/* WP groups */}
            {wps.map((wp, wpIdx) => {
              const wpTasks = (tasksByWP[wp.id] || []).slice().sort((a, b) => a.start_month - b.start_month);
              if (wpTasks.length === 0) return null;
              const palette = WP_PALETTE[wpIdx % WP_PALETTE.length];
              const wpStart = Math.min(...wpTasks.map(t => t.start_month));
              const wpEnd   = Math.max(...wpTasks.map(t => t.end_month));
              const wpStartPct = ((wpStart - 1) / maxMonth) * 100;
              const wpWidthPct = ((wpEnd - wpStart + 1) / maxMonth) * 100;

              return (
                <Box key={wp.id} sx={{ mb: 1.5, minWidth: `${maxMonth * 18 + 200}px` }}>
                  {/* WP header row — dark bar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ width: '200px', pr: 1, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: palette.dark }}>
                        WP{wp.number}: {wp.title}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, position: 'relative', height: '26px' }}>
                      <Box sx={{
                        position: 'absolute',
                        left: `${wpStartPct}%`,
                        width: `${wpWidthPct}%`,
                        height: '26px',
                        backgroundColor: palette.dark,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        overflow: 'hidden',
                      }}>
                        <Typography sx={{ color: 'white', fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          WP{wp.number}: {wp.title}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Task bars — lighter shade */}
                  {wpTasks.map(task => {
                    const startPct = ((task.start_month - 1) / maxMonth) * 100;
                    const widthPct = (task.duration_months / maxMonth) * 100;
                    return (
                      <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, pl: 1 }}>
                        <Box sx={{ width: '192px', pr: 1, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{task.full_title}</Typography>
                        </Box>
                        <Box sx={{ flex: 1, position: 'relative', height: '20px' }}>
                          <Box
                            title={`${task.full_title} (${task.start_month}–${task.end_month})`}
                            sx={{
                              position: 'absolute',
                              left: `${startPct}%`,
                              width: `${widthPct}%`,
                              height: '20px',
                              backgroundColor: palette.light,
                              borderRadius: '3px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              color: palette.dark,
                              transition: 'opacity 0.15s',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            {task.duration_months > 2 && `${task.start_month}–${task.end_month}`}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}

            {/* Unassigned tasks */}
            {(tasksByWP['__none__'] || []).map(task => {
              const startPct = ((task.start_month - 1) / maxMonth) * 100;
              const widthPct = (task.duration_months / maxMonth) * 100;
              return (
                <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Box sx={{ width: '200px', pr: 1, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{task.full_title}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, position: 'relative', height: '20px' }}>
                    <Box sx={{ position: 'absolute', left: `${startPct}%`, width: `${widthPct}%`, height: '20px', backgroundColor: '#bdbdbd', borderRadius: '3px' }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    </>
  );
})()}




              {/* Budget Summary */}
{!isPublication && selectedProposal.tasks && selectedProposal.tasks.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, ml:2 }}>Budget Summary</Typography>
                  <Card>
                    <CardContent>
                      {(() => {
                        const allBudgetItems = selectedProposal?.tasks
                          ?.flatMap(task => task.budget_items || [])
                          .filter(item => item.organization_name);

                        if (allBudgetItems.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No budget items defined
                            </Typography>
                          );
                        }

                        const grandTotal = {
                          consumables: allBudgetItems.reduce((sum, item) => sum + parseFloat(item.consumables || 0), 0),
                          equipment: allBudgetItems.reduce((sum, item) => sum + parseFloat(item.equipment || 0), 0),
                          personnel: allBudgetItems.reduce((sum, item) => sum + parseFloat(item.personnel || 0), 0),
                          travel: allBudgetItems.reduce((sum, item) => sum + parseFloat(item.travel || 0), 0),
                          subcontracting: allBudgetItems.reduce((sum, item) => sum + parseFloat(item.subcontracting || 0), 0),
                          overheads: allBudgetItems.reduce((sum, item) => sum + parseFloat(item.overheads || 0), 0),
                        };
                        const total = Object.values(grandTotal).reduce((sum, val) => sum + val, 0);

                        return (
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={4} md={2}>
                              <Typography variant="caption" color="text.secondary">Consumables</Typography>
                              <Typography variant="h6">€{grandTotal.consumables.toLocaleString('en-US', { minimumFractionDigits: 0 })}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Typography variant="caption" color="text.secondary">Equipment</Typography>
                              <Typography variant="h6">€{grandTotal.equipment.toLocaleString('en-US', { minimumFractionDigits: 0 })}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Typography variant="caption" color="text.secondary">Personnel</Typography>
                              <Typography variant="h6">€{grandTotal.personnel.toLocaleString('en-US', { minimumFractionDigits: 0 })}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Typography variant="caption" color="text.secondary">Travel</Typography>
                              <Typography variant="h6">€{grandTotal.travel.toLocaleString('en-US', { minimumFractionDigits: 0 })}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Typography variant="caption" color="text.secondary">Subcontracting</Typography>
                              <Typography variant="h6">€{grandTotal.subcontracting.toLocaleString('en-US', { minimumFractionDigits: 0 })}</Typography>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Typography variant="caption" color="text.secondary">Overheads</Typography>
                              <Typography variant="h6">€{grandTotal.overheads.toLocaleString('en-US', { minimumFractionDigits: 0 })}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="caption" color="text.secondary">Total Budget</Typography>
                              <Typography variant="h4" color="primary">
                                €{total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </Typography>
                            </Grid>
                          </Grid>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </>
              )}




<Divider sx={{ my: 2 }} />


{/* ── Appendix ── */}
{!isPublication && (() => {
  const appendixSection = (selectedProposal.sections || []).find(s => s.section_key === 'appendix');
  const appendixSubsections = (appendixSection?.subsections || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const resTypes = [
    { key: 'publications', label: 'Publications', getTitle: r => r.title, getSub: r => [r.journal, r.year].filter(Boolean).join(', ') },
    { key: 'patents',      label: 'Patents',      getTitle: r => r.title, getSub: r => [r.patent_number, r.status].filter(Boolean).join(', ') },
    { key: 'projects',     label: 'Projects',     getTitle: r => r.title, getSub: r => [r.role, r.funding_agency].filter(Boolean).join(', ') },
    { key: 'networks',     label: 'Networks',     getTitle: r => r.name,  getSub: r => [r.domain, r.role].filter(Boolean).join(', ') },
  ];
  const hasResources = resTypes.some(t => (memberResources?.[t.key] || []).filter(r => memberResourcesSelected[t.key]?.has(String(r.id))).length > 0);
  const hasAppendixContent = appendixSubsections.length > 0 || hasResources;
  if (!hasAppendixContent) return null;

  let itemCounter = 0;
  return (
    <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 3, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}>
        APPENDIX
      </Typography>

      {appendixSubsections.map((sub, idx) => {
        itemCounter++;
        return (
          <Box key={sub.id} {...previewEditable({ section: sub })} sx={{ mb: 3, ...previewEditSx }}>
            <Typography variant="h6" color="primary" gutterBottom>
              A.{itemCounter}. {sub.title}
            </Typography>
            {sub.current_content?.body ? (
              <Box sx={{ typography: 'body1' }}>
                {renderMarkdownToReact(previewBody(sub), { figureCounter: previewFigures, figures: figuresMap, citations: previewCitations(sub) })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">No content.</Typography>
            )}
            {sub.subsections?.length > 0 && sub.subsections.map((s2, i2) => (
              <Box key={s2.id} {...previewEditable({ section: s2 })} sx={{ ml: 3, mt: 1, ...previewEditSx }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  A.{itemCounter}.{i2 + 1}. {s2.title}
                </Typography>
                {s2.current_content?.body ? (
                  <Box sx={{ typography: 'body1', mb: 1 }}>
                    {renderMarkdownToReact(previewBody(s2), { figureCounter: previewFigures, figures: figuresMap, citations: previewCitations(s2) })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">No content.</Typography>
                )}
              </Box>
            ))}
          </Box>
        );
      })}

      {hasResources && resTypes.map(({ key, label, getTitle, getSub }) => {
        const items = (memberResources?.[key] || []).filter(r => memberResourcesSelected[key]?.has(String(r.id)));
        if (!items.length) return null;
        itemCounter++;
        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              A.{itemCounter}. {label}
            </Typography>
            <Stack spacing={0.5} sx={{ pl: 1 }}>
              {items.map((r, i) => (
                <Box key={r.id || i}>
                  <Typography variant="body2" fontWeight={500} component="span">
                    [{i + 1}] {getTitle(r)}
                  </Typography>
                  {getSub(r) && (
                    <Typography variant="body2" color="text.secondary" component="span"> — {getSub(r)}</Typography>
                  )}
                  {r._member_name && (
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ pl: 1 }}>
                      {r._member_name}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Paper>
  );
})()}


    

     
            </Box>
          </Paper>
        )}
      </Container>

      {/* Dialogs */}
      <SectionDialog
        open={sectionDialogOpen}
        onClose={() => { setSectionDialogOpen(false); setSubsectionParentId(null); }}
        proposalId={selectedProposal?.id}
        section={editingDialogSection}
        sections={selectedProposal?.sections}
        defaultParentId={subsectionParentId}
        consortiumMembers={consortiumUsers}
        onSuccess={handleSectionDialogSuccess}
      />

      <WorkPackageDialog
        open={workPackageDialogOpen}
        onClose={() => setWorkPackageDialogOpen(false)}
        proposalId={selectedProposal?.id}
        workPackage={editingWorkPackage}
        consortiumMembers={consortiumUsers}
        onSuccess={handleWorkPackageDialogSuccess}
      />

      <TaskDialog
        open={taskDialogOpen}
        onClose={() => {
          setTaskDialogOpen(false);
          setSelectedWorkPackageId(null);
        }}
        proposalId={selectedProposal?.id}
        workPackages={selectedProposal?.work_packages}
        sections={selectedProposal?.sections}
        task={editingTask}
        defaultWorkPackageId={selectedWorkPackageId}
        onSuccess={handleTaskDialogSuccess}
        consortiumMembers={consortiumUsers}
        canEditAssignees={canEditAssignees(editingTask)}
        currentUser={currentUser}
      />

      <WpPreviewDialog
        open={wpPreviewOpen}
        onClose={() => setWpPreviewOpen(false)}
        wp={wpPreviewTarget}
      />

      <WpNotesDialog
        open={wpNotesOpen}
        onClose={() => setWpNotesOpen(false)}
        wp={wpNotesTarget}
        onSaved={(updatedWp) => {
          if (selectedProposal?.work_packages) {
            selectedProposal.work_packages = selectedProposal.work_packages.map(w =>
              w.id === updatedWp.id ? { ...w, notes: updatedWp.notes, updated: updatedWp.updated } : w
            );
          }
        }}
      />

      <BudgetDialog
        open={budgetDialogOpen}
        onClose={() => setBudgetDialogOpen(false)}
        taskId={budgetTaskId}
        budgetItem={editingBudget}
        consortiumMembers={getTaskAssignees(budgetTask)}
        currentUser={currentUser}
        onSuccess={() => {
          setBudgetDialogOpen(false);
          loadProposalDetails(selectedProposal.id);
        }}
      />

      <PersonMonthDialog
        open={personMonthDialogOpen}
        onClose={() => {
          setPersonMonthDialogOpen(false);
          setPersonMonthTask(null);
          setEditingPersonMonth(null);
        }}
        personMonthItem={editingPersonMonth}
        task={personMonthTask}
        consortiumMembers={getTaskAssignees(personMonthTask)}
        onSuccess={() => {
          setPersonMonthDialogOpen(false);
          setPersonMonthTask(null);
          loadProposalDetails(selectedProposal.id);
        }}
      />

      <SaveConflictDialog
        conflict={saveConflict}
        onClose={() => setSaveConflict(null)}
        onShowHistory={() => { handleShowVersions(saveConflict.section); setSaveConflict(null); }}
        onUseMine={async () => {
          const res = await restoreContentVersion(saveConflict.mine.id);
          if (res.success) {
            showSnackbar(`Your version is now current. Theirs stays in the version history.`, 'success');
            await loadProposalDetails(selectedProposal.id);
          } else {
            showSnackbar('Could not switch to your version.', 'error');
          }
          setSaveConflict(null);
        }}
      />
      <Menu open={Boolean(previewMenu)} onClose={() => setPreviewMenu(null)} anchorReference="anchorPosition"
            anchorPosition={previewMenu ? { top: previewMenu.y, left: previewMenu.x } : undefined}>
        {previewMenu?.section && [
          <MenuItem key="edit" onClick={() => editFromPreview(previewMenu.section)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Edit the text"
              secondary={`${previewMenu.section.section_key ? `${previewMenu.section.section_key}. ` : ''}${previewMenu.section.title}`} />
          </MenuItem>,
          <MenuItem key="full" onClick={() => editFromPreview(previewMenu.section, true)}>
            <ListItemIcon><FullscreenIcon fontSize="small" /></ListItemIcon><ListItemText>Edit full screen</ListItemText>
          </MenuItem>,
          <MenuItem key="settings" onClick={() => { const sec = previewMenu.section; setPreviewMenu(null); handleEditSection(sec); }}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon><ListItemText>Section settings</ListItemText>
          </MenuItem>,
          <MenuItem key="history" onClick={() => { const sec = previewMenu.section; setPreviewMenu(null); handleShowVersions(sec); }}>
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon><ListItemText>View history</ListItemText>
          </MenuItem>,
        ]}
        {previewMenu?.part && (
          <MenuItem onClick={() => { const part = previewMenu.part; setPreviewMenu(null); setActiveTab(part === 'abstract' ? 0 : 7); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{previewMenu.part === 'abstract' ? 'Edit the abstract' : 'Edit the authors'}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <ContentVersionsDialog
        open={versionsDialogOpen}
        onClose={() => setVersionsDialogOpen(false)}
        sectionId={versionsSectionId}
        sectionTitle={versionsSectionTitle}
        onRestore={handleVersionRestore}
      />

      <EditProposalDialog
        open={editProposalDialogOpen}
        onClose={() => setEditProposalDialogOpen(false)}
        proposal={selectedProposal}
        onSuccess={(updatedProposal) => {
          const styleChanged = isPublication && updatedProposal.citation_style
            && updatedProposal.citation_style !== selectedProposal.citation_style;
          setSelectedProposal(prev => ({ ...prev, ...updatedProposal }));
          if (styleChanged && (selectedProposal.references || []).some((r) => r.cited)) {
            // Picking a journal in the edit dialog switches the reference style
            rebuildReferencesSection(selectedProposal.references, false, updatedProposal.citation_style);
          } else {
            reloadProposalSilently(updatedProposal.id);
          }
        }}
      />

      <ReviewsDialog
        open={reviewsOpen}
        onClose={() => setReviewsOpen(false)}
        proposal={selectedProposal}
        isPublication={isPublication}
      />

      <ProposalSettingsDialog
        open={proposalSettingsOpen}
        initialTab={settingsTab}
        onClose={() => setProposalSettingsOpen(false)}
        consortium={consortium}
        proposal={selectedProposal}
        onSuccess={(result) => {
          if (result === 'left') {
            // User left the proposal — navigate back to consortium; they have no access
            navigate(`/consortium/${consortiumId}/`);
            return;
          }
          // A journal picked in the dialog switches the reference style: rebuild References
          const styleChanged = isPublication && result?.citation_style
            && result.citation_style !== selectedProposal.citation_style;
          if (styleChanged && (selectedProposal.references || []).some((r) => r.cited)) {
            rebuildReferencesSection(selectedProposal.references, false, result.citation_style);
          } else {
            // Reload proposal so acronym/title/status reflect any changes made in the dialog
            reloadProposalSilently(selectedProposal.id);
          }
          // Refresh consortium to pick up updated permission fields
          axiosService.get(`/consortium/consortiums/${consortiumId}/`).then(res => setConsortium(res.data));
        }}
      />

      {/* Save as Template dialog */}
      <Dialog open={saveTemplateOpen} onClose={() => !saveTemplateSaving && setSaveTemplateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkAddIcon color="secondary" />
          Save as Template
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              fullWidth
              autoFocus
              value={saveTemplateName}
              onChange={e => setSaveTemplateName(e.target.value)}
              placeholder="e.g. Horizon Europe 2025"
              disabled={saveTemplateSaving}
              required
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={saveTemplateDesc}
              onChange={e => setSaveTemplateDesc(e.target.value)}
              placeholder="Brief note about this template"
              disabled={saveTemplateSaving}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateOpen(false)} disabled={saveTemplateSaving}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveAsTemplate}
            disabled={!saveTemplateName.trim() || saveTemplateSaving}
            startIcon={saveTemplateSaving ? <CircularProgress size={16} /> : <BookmarkAddIcon />}
          >
            {saveTemplateSaving ? 'Saving…' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedProposal && isPublication && (
        <FormatReferencesDialog
          open={formatDialogOpen}
          value={citationStyle}
          valueTitle={selectedProposal.citation_style_title}
          references={selectedProposal.references || []}
          onClose={() => setFormatDialogOpen(false)}
          onApply={applyCitationStyle}
        />
      )}

      {selectedProposal && (
        <ProposalTemplateDialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          proposalId={selectedProposal.id}
          documentType={selectedProposal.document_type}
          recommendedTemplate={journalInfo?.recommended_template}
          existingSectionCount={(selectedProposal.sections || []).filter(s => s.section_key !== 'abstract' && s.section_key !== 'appendix').length}
          onSuccess={() => {
            setTemplateDialogOpen(false);
            loadProposalDetails(selectedProposal.id);
          }}
        />
      )}

      {selectedProposal && (
        <RestructureHeadingsDialog
          open={restructureDialogOpen}
          onClose={() => setRestructureDialogOpen(false)}
          sections={(selectedProposal.sections || []).filter(s => s.section_key !== 'abstract' && s.section_key !== 'appendix')}
          onSaved={() => { loadProposalDetails(selectedProposal.id); refreshFigures(); }}
        />
      )}

      {selectedProposal && (
        <Dialog open={versionDialogOpen} onClose={() => !versionSaving && setVersionDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Proposal Version</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Version"
              placeholder="e.g. 1.2.0"
              value={versionInput}
              onChange={(e) => setVersionInput(e.target.value)}
              disabled={!isCoordinator || versionSaving}
              error={versionInput.trim() !== '' && !isValidSemver(versionInput)}
              helperText={
                versionInput.trim() !== '' && !isValidSemver(versionInput)
                  ? 'Use the format x.y.z (e.g. 1.2.0)'
                  : isCoordinator
                    ? 'Saving snapshots the full proposal text under this version'
                    : 'Only the coordinator can save or switch versions'
              }
              onKeyDown={(e) => { if (e.key === 'Enter' && isValidSemver(versionInput)) handleVersionSave(); }}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Saved Versions</Typography>
            {versionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : versionsList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No saved versions yet — save one above to start tracking history.
              </Typography>
            ) : (
              <Stack spacing={0} divider={<Divider />} sx={{ maxHeight: 240, overflowY: 'auto' }}>
                {versionsList.map(v => (
                  <Stack key={v.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1 }}>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={600}>v{v.version}</Typography>
                        {v.version === selectedProposal.current_version && (
                          <Chip label="Current" size="small" color="primary" variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {v.created_by_name || 'Unknown'} • {new Date(v.created).toLocaleString()}
                      </Typography>
                    </Box>
                    {isCoordinator && v.version !== selectedProposal.current_version && (
                      <Button
                        size="small"
                        onClick={() => handleSwitchVersion(v.version)}
                        disabled={switchingVersion !== null}
                      >
                        {switchingVersion === v.version ? <CircularProgress size={16} /> : 'Switch to this version'}
                      </Button>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVersionDialogOpen(false)} disabled={versionSaving}>
              {isCoordinator ? 'Cancel' : 'Close'}
            </Button>
            {isCoordinator && (
              <Button
                variant="contained"
                onClick={handleVersionSave}
                disabled={versionSaving || !isValidSemver(versionInput)}
              >
                {versionSaving ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {selectedProposal && (
        <AIActivationDialog
          open={aiActivationDialogOpen}
          onClose={() => setAiActivationDialogOpen(false)}
          proposalId={selectedProposal.id}
          onActivated={() => setAiEnabled(true)}
        />
      )}

      {selectedProposal?.id && (
        <ImportPublicationDialog open={importDocxOpen} onClose={() => setImportDocxOpen(false)}
          kind={isPublication ? 'publication' : 'proposal'}
          proposalId={selectedProposal.id} onImported={finishDocxImport} />
      )}

      {/* About Proposal Writer Dialog */}
      <Dialog open={aboutDialogOpen} onClose={() => setAboutDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" fontSize="small" />
          About {isPublication ? 'Publication' : 'Proposal'} Writer
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Chip label="BETA" color="warning" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem', px: 1 }} />
          </Box>
          <DialogContentText align="center" sx={{ mb: 1.5 }}>
            <strong>Version 0.9.4</strong>
          </DialogContentText>
          <DialogContentText>
            {isPublication ? 'Publication' : 'Proposal'} Writer is currently in active development. Features may change, and some functionality may be incomplete or subject to revision.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            We appreciate your feedback as we continue to improve the tool. If you encounter issues or have suggestions, please report them to the platform team.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>




  );
};

/* ===== Advisory Board ===== */

const EMPTY_AB_FORM = {
  first_name: '', last_name: '', domain: '', email: '',
  organization: '', address: '', description: '',
};

function AddABMemberDialog({ open, onClose, onAdd, consortiumUsers, existing }) {
  const [mode, setMode] = useState('select'); // 'select' | 'manual'
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(EMPTY_AB_FORM);

  useEffect(() => {
    if (open) { setMode('select'); setSelectedUser(null); setForm(EMPTY_AB_FORM); }
  }, [open]);

  const set = (f) => (e) => setForm(v => ({ ...v, [f]: e.target.value }));

  const existingIds = new Set(existing.filter(m => m._userId).map(m => m._userId));
  const available = consortiumUsers.filter(u => !existingIds.has(u.id));

  const handleAdd = () => {
    if (mode === 'select' && selectedUser) {
      const nameParts = (selectedUser.name || '').trim().split(/\s+/);
      const first_name = nameParts.slice(0, -1).join(' ') || nameParts[0] || '';
      const last_name = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      onAdd({
        first_name,
        last_name,
        email: selectedUser.email || '',
        domain: selectedUser.research_areas?.[0]?.name || '',
        organization: selectedUser.org_short_name || '',
        address: '',
        description: '',
        _userId: selectedUser.id,
        _avatar: selectedUser.avatar,
      });
    } else if (mode === 'manual') {
      onAdd({ ...form });
    }
    onClose();
  };

  const manualValid = form.first_name.trim() && form.last_name.trim() && form.domain.trim() && form.email.trim();
  const canAdd = mode === 'select' ? Boolean(selectedUser) : manualValid;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Advisory Board Member</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {/* Mode toggle */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small" variant={mode === 'select' ? 'contained' : 'outlined'}
              onClick={() => setMode('select')}
            >
              Select Otable Member
            </Button>
            <Button
              size="small" variant={mode === 'manual' ? 'contained' : 'outlined'}
              onClick={() => setMode('manual')}
            >
              Manual Entry
            </Button>
          </Box>

          {mode === 'select' && (
            <Autocomplete
              options={available}
              getOptionLabel={(u) => `${u.name || u.email || u.id}${u.org_short_name ? ` — ${u.org_short_name}` : ''}`}
              value={selectedUser}
              onChange={(_, v) => setSelectedUser(v)}
              renderInput={(params) => <TextField {...params} label="Search consortium members" size="small" />}
              noOptionsText={available.length === 0 ? 'All consortium members already added' : 'No matches'}
            />
          )}

          {mode === 'manual' && (
            <>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField label="First Name *" value={form.first_name} onChange={set('first_name')} size="small" fullWidth />
                <TextField label="Last Name *" value={form.last_name} onChange={set('last_name')} size="small" fullWidth />
              </Box>
              <TextField label="Research Domain *" value={form.domain} onChange={set('domain')} size="small" fullWidth
                helperText="e.g. Computational Biology, Climate Science" />
              <TextField label="Email *" value={form.email} onChange={set('email')} size="small" fullWidth type="email" />
              <TextField label="Organization" value={form.organization} onChange={set('organization')} size="small" fullWidth />
              <TextField label="Address" value={form.address} onChange={set('address')} size="small" fullWidth />
              <TextField label="Description / Notes" value={form.description} onChange={set('description')}
                size="small" fullWidth multiline rows={2} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canAdd} onClick={handleAdd}>Add</Button>
      </DialogActions>
    </Dialog>
  );
}

function AdvisoryBoardCard({ members, consortiumUsers, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{
        px: 2, py: 1.5, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: members.length > 0 ? '1px solid' : 'none',
        borderColor: 'divider',
      }}>
        <Typography variant="subtitle2" fontWeight={600}>Advisory Board</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Add AB Member
        </Button>
      </Box>

      {members.length > 0 && (
        <Stack divider={<Divider />}>
          {members.map((m) => (
            <Box key={m.id} sx={{ px: 2, py: 1, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {m.full_name}
                </Typography>
                {m.domain && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>{m.domain}</Typography>
                )}
                {m.organization && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>{m.organization}</Typography>
                )}
                {m.email && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>{m.email}</Typography>
                )}
              </Box>
              <IconButton size="small" onClick={() => onRemove(m.id)} sx={{ mt: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {members.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.5 }}>
          No advisory board members added yet.
        </Typography>
      )}

      <AddABMemberDialog
        open={open}
        onClose={() => setOpen(false)}
        onAdd={onAdd}
        consortiumUsers={consortiumUsers}
        existing={members}
      />
    </Paper>
  );
}

/* ===== Member Resources Card ===== */

const RESOURCE_TABS = [
  { key: 'publications', label: 'Publications' },
  { key: 'patents',      label: 'Patents' },
  { key: 'projects',     label: 'Projects' },
  { key: 'networks',     label: 'Networks' },
];

const MAX_PER_USER_PER_TYPE = 5;

function MemberResourcesCard({ resources, loading, currentUserId, isCoordinator, selected, onSelectedChange }) {
  const [tab, setTab] = useState(0);

  // Auto-select first 5 per user per type when resources load
  useEffect(() => {
    if (!resources) return;
    const autoSelected = {};
    for (const { key } of RESOURCE_TABS) {
      const perUser = {};
      const ids = new Set();
      for (const item of (resources[key] || [])) {
        const uid = item._member_user_id || '__unknown__';
        if (!perUser[uid]) perUser[uid] = 0;
        if (perUser[uid] < MAX_PER_USER_PER_TYPE) {
          ids.add(String(item.id));
          perUser[uid]++;
        }
      }
      autoSelected[key] = ids;
    }
    onSelectedChange(autoSelected);
  }, [resources]); // eslint-disable-line react-hooks/exhaustive-deps

  const empty = !resources || RESOURCE_TABS.every(t => !(resources[t.key]?.length));

  // Count how many items of a given type are selected for a given user
  const selectedCountForUser = (typeKey, userId) => {
    const items = resources?.[typeKey] || [];
    return items.filter(item => selected[typeKey].has(String(item.id)) && item._member_user_id === userId).length;
  };

  const handleToggle = (typeKey, item) => {
    const id = String(item.id);
    const userId = item._member_user_id;
    const isOwn = userId === currentUserId;
    if (!isOwn && !isCoordinator) return;

    const next = new Set(selected[typeKey]);
    if (next.has(id)) {
      next.delete(id);
    } else {
      const userCount = selectedCountForUser(typeKey, userId);
      if (userCount >= MAX_PER_USER_PER_TYPE) return;
      next.add(id);
    }
    onSelectedChange({ ...selected, [typeKey]: next });
  };

  const isChecked = (typeKey, id) => selected[typeKey].has(String(id));

  const isDisabled = (typeKey, item) => {
    const id = String(item.id);
    const userId = item._member_user_id;
    const isOwn = userId === currentUserId;
    if (!isOwn && !isCoordinator) return true; // can't touch other's items
    if (selected[typeKey].has(id)) return false; // can always uncheck
    return selectedCountForUser(typeKey, userId) >= MAX_PER_USER_PER_TYPE;
  };

  // Group items by member name for display
  const groupByMember = (items) => {
    const groups = {};
    for (const item of items) {
      const name = item._member_name || 'Unknown';
      if (!groups[name]) groups[name] = [];
      groups[name].push(item);
    }
    return groups;
  };

  const renderItemContent = (typeKey, item) => {
    if (typeKey === 'publications') return (
      <>
        <Typography variant="body2" fontWeight={500} display="block" sx={{ lineHeight: 1.3 }}>
          {item.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {[item.journal, item.year].filter(Boolean).join(' · ')}
          {item.citation_count > 0 && ` · ${item.citation_count} citations`}
        </Typography>
      </>
    );
    if (typeKey === 'patents') return (
      <>
        <Typography variant="body2" fontWeight={500} display="block" sx={{ lineHeight: 1.3 }}>
          {item.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {[item.patent_number, item.status].filter(Boolean).join(' · ')}
        </Typography>
      </>
    );
    if (typeKey === 'projects') return (
      <>
        <Typography variant="body2" fontWeight={500} display="block" sx={{ lineHeight: 1.3 }}>
          {item.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {[item.role, item.funding_agency].filter(Boolean).join(' · ')}
        </Typography>
      </>
    );
    if (typeKey === 'networks') return (
      <>
        <Typography variant="body2" fontWeight={500} display="block" sx={{ lineHeight: 1.3 }}>
          {item.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {[item.domain, item.role].filter(Boolean).join(' · ')}
        </Typography>
      </>
    );
    return null;
  };

  const selectedTotal = RESOURCE_TABS.reduce((sum, t) => sum + selected[t.key].size, 0);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', pt:1, pb:3 }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>Member Resources</Typography>
          <Typography variant="caption" color="text.secondary">
            Select up to 5 items per type. {isCoordinator ? 'As coordinator, you can select for any member.' : 'You can only select your own resources.'}
          </Typography>
        </Box>
        {selectedTotal > 0 && (
          <Chip label={`${selectedTotal} selected`} size="small" color="primary" variant="outlined" sx={{ mt: 0.25 }} />
        )}
      </Box>

      {loading && (
        <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        </Box>
      )}

      {!loading && empty && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.5 }}>
          No resources found for consortium members.
        </Typography>
      )}

      {!loading && !empty && (
        <>
          <Tabs
            value={tab} onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 36 }}
            TabIndicatorProps={{ style: { height: 2 } }}
          >
            {RESOURCE_TABS.map((t) => {
              const count = resources[t.key]?.length || 0;
              const sel = selected[t.key].size;
              return (
                <Tab
                  key={t.key}
                  label={`${t.label} (${sel}/${count})`}
                  sx={{ fontSize: '1.1rem', minHeight: 36, py: 0, px: 0.5 }}
                />
              );
            })}
          </Tabs>

          {RESOURCE_TABS.map((t, i) => tab === i && (
            <Box key={t.key} sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {(resources[t.key] || []).length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>
                  None found.
                </Typography>
              ) : (
                Object.entries(groupByMember(resources[t.key] || [])).map(([memberName, items]) => (
                  <Box key={memberName}>
                    <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.8rem' }}>
                        {memberName}
                      </Typography>
                      {items[0]?._member_user_id && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.9rem' }}>
                          {selectedCountForUser(t.key, items[0]._member_user_id)}/{MAX_PER_USER_PER_TYPE}
                        </Typography>
                      )}
                    </Box>
                    <Stack sx={{pt:1}} divider={<Divider />}>
                      {items.map((item, idx) => {
                        const checked = isChecked(t.key, item.id);
                        const disabled = isDisabled(t.key, item);
                        return (
                          <Box
                            key={item.id || idx}
                            onClick={() => !disabled && handleToggle(t.key, item)}
                            sx={{
                              px: 2, py: 0.75,
                              display: 'flex', alignItems: 'flex-start', gap: 1,
                              cursor: disabled ? 'default' : 'pointer',
                              bgcolor: checked ? 'action.selected' : 'transparent',
                              opacity: disabled && !checked ? 0.45 : 1,
                              '&:hover': !disabled ? { bgcolor: checked ? 'action.selected' : 'action.hover' } : {},
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <Checkbox
                              checked={checked}
                              disabled={disabled}
                              size="small"
                              sx={{ p: 0, mt: 0.1, flexShrink: 0 }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => !disabled && handleToggle(t.key, item)}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {renderItemContent(t.key, item)}
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ))
              )}
            </Box>
          ))}
        </>
      )}
    </Paper>
  );
}

/* ===== Quick Reference Card ===== */

const _wc = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length;

function WpTooltipContent({ wp, tasks }) {
  const wpTasks = tasks.filter(t => t.work_package === wp.id);
  return (
    <Box sx={{ maxWidth: 300, p: 0.5 }}>
      <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
        WP{wp.number}: {wp.title}
      </Typography>
      {wp.description && (
        <Typography variant="caption" display="block" sx={{ opacity: 0.85, mb: 0.75 }}>
          {wp.description.length > 240 ? wp.description.slice(0, 240) + '…' : wp.description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: wpTasks.length ? 0.75 : 0 }}>
        {wp.total_person_months != null && (
          <Typography variant="caption" sx={{ opacity: 0.65 }}>{wp.total_person_months} PM</Typography>
        )}
        {wp.total_budget != null && (
          <Typography variant="caption" sx={{ opacity: 0.65 }}>€{Number(wp.total_budget).toLocaleString()}</Typography>
        )}
        {wpTasks.length > 0 && (
          <Typography variant="caption" sx={{ opacity: 0.65 }}>{wpTasks.length} tasks</Typography>
        )}
      </Box>
      {wpTasks.slice(0, 6).map(t => (
        <Typography key={t.id} variant="caption" display="block" sx={{ opacity: 0.75, pl: 1 }}>
          · T{wp.number}.{t.number} {t.title}
        </Typography>
      ))}
      {wpTasks.length > 6 && (
        <Typography variant="caption" display="block" sx={{ opacity: 0.45, pl: 1 }}>+{wpTasks.length - 6} more</Typography>
      )}
    </Box>
  );
}

function SectionTooltipContent({ section }) {
  const body = section.current_content?.body || '';
  return (
    <Box sx={{ maxWidth: 300, p: 0.5 }}>
      <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
        {section.section_key}. {section.title}
      </Typography>
      {body ? (
        <>
          <Typography variant="caption" display="block" sx={{ opacity: 0.85, mb: 0.75 }}>
            {body.length > 300 ? body.slice(0, 300) + '…' : body}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.55 }}>
            {_wc(body)} words{section.word_limit ? ` / ${section.word_limit}` : ''}
            {section.status ? ` · ${section.status.replace(/_/g, ' ')}` : ''}
          </Typography>
        </>
      ) : (
        <Typography variant="caption" sx={{ opacity: 0.5 }}>No content yet</Typography>
      )}
      {section.subsections?.length > 0 && (
        <Box sx={{ mt: 0.75, pl: 1, borderLeft: '2px solid rgba(255,255,255,0.2)' }}>
          {section.subsections.slice(0, 5).map(sub => (
            <Typography key={sub.id} variant="caption" display="block" sx={{ opacity: 0.7 }}>
              {sub.section_key}. {sub.title}
            </Typography>
          ))}
          {section.subsections.length > 5 && (
            <Typography variant="caption" sx={{ opacity: 0.4 }}>+{section.subsections.length - 5} more</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

const TOOLTIP_SX = {
  tooltip: { sx: { maxWidth: 'none', bgcolor: 'background.paper', color: 'text.primary', boxShadow: 6, borderRadius: 2, p: 1.5, border: '1px solid', borderColor: 'divider' } },
  arrow: { sx: { color: 'background.paper' } },
};

function QuickReferenceCard({ sections, workPackages, tasks, proposalId, memberResources, advisoryBoard, consortiumMembers, isPublication = false }) {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);

  // Dissemination — lazy fetch on tab open
  const [dissemActivities, setDissemActivities] = useState(null);
  const [dissemLoading, setDissemLoading] = useState(false);

  useEffect(() => {
    if (tab === 2 && dissemActivities === null && !dissemLoading && proposalId) {
      setDissemLoading(true);
      axiosService.get('/proposal/dissemination/', { params: { proposal: proposalId, page_size: 200 } })
        .then(res => setDissemActivities(res.data?.results || res.data || []))
        .catch(() => setDissemActivities([]))
        .finally(() => setDissemLoading(false));
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = search.toLowerCase();

  const filteredSections = sections.filter(s =>
    s.title?.toLowerCase().includes(q) || String(s.section_key).includes(q)
  );
  const filteredWps = workPackages.filter(wp =>
    wp.title?.toLowerCase().includes(q) || `wp${wp.number}`.includes(q)
  );
  const filteredDissem = (dissemActivities || []).filter(a =>
    a.title?.toLowerCase().includes(q) ||
    a.category_display?.toLowerCase().includes(q) ||
    a.description?.toLowerCase().includes(q)
  );

  const allResources = [
    ...(memberResources?.publications || []).map(r => ({ ...r, _type: 'Publication', _label: r.title })),
    ...(memberResources?.patents || []).map(r => ({ ...r, _type: 'Patent', _label: r.title })),
    ...(memberResources?.projects || []).map(r => ({ ...r, _type: 'Project', _label: r.title })),
    ...(memberResources?.networks || []).map(r => ({ ...r, _type: 'Network', _label: r.name })),
  ];
  const filteredResources = allResources.filter(r =>
    r._label?.toLowerCase().includes(q) ||
    r._member_name?.toLowerCase().includes(q) ||
    r._type.toLowerCase().includes(q)
  );

  const filteredAB = (advisoryBoard || []).filter(m =>
    `${m.first_name} ${m.last_name}`.toLowerCase().includes(q) ||
    m.domain?.toLowerCase().includes(q) ||
    m.organization?.toLowerCase().includes(q)
  );
  const filteredBudget = consortiumMembers.filter(m =>
    m.name?.toLowerCase().includes(q) ||
    m.short_name?.toLowerCase().includes(q) ||
    m.country?.toLowerCase().includes(q)
  ).sort((a, b) => (a.number || 0) - (b.number || 0));

  const TABS = [
    { label: 'Sections', count: sections.length },
    { label: 'WPs', count: workPackages.length, hide: isPublication },
    { label: 'Dissem.', count: dissemActivities?.length ?? '…', hide: isPublication },
    { label: 'Resources', count: memberResources ? allResources.length : '…', hide: isPublication },
    { label: 'Adv. Board', count: (advisoryBoard || []).length, hide: isPublication },
    { label: 'Budget', count: consortiumMembers.length, hide: isPublication },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', 
                                    position: 'sticky', top: 16, pt:1}}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={600}>Quick Reference</Typography>
        <Typography variant="caption" color="text.secondary">Hover to preview while writing</Typography>
      </Box>

      <Tabs
        value={tab} onChange={(_, v) => { setTab(v); setSearch(''); }}
        variant="scrollable" scrollButtons="auto"
        sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 32 }}
        TabIndicatorProps={{ style: { height: 2 } }}
      >
        {TABS.map((t, i) => !t.hide && (
          <Tab key={t.label} value={i} label={`${t.label} (${t.count})`}
            sx={{ fontSize: '0.9rem', minHeight: 32, py: 0, px: 0.75, whiteSpace: 'nowrap' }} />
        ))}
      </Tabs>

      <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small" placeholder="Search…" value={search}
          onChange={e => setSearch(e.target.value)} fullWidth
          sx={{ '& .MuiInputBase-root': { height: 26 }, '& input': { py: '3px', px: '8px', fontSize: '0.9rem' } }}
        />
      </Box>

      <Stack divider={<Divider />} sx={{ maxHeight: 480, overflowY: 'auto'}}>

        {/* ── Sections ── */}
        {tab === 0 && (filteredSections.length === 0
          ? <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>No sections found.</Typography>
          : filteredSections.map(section => (
            <Tooltip key={section.id} placement="left" arrow componentsProps={TOOLTIP_SX}
              title={<SectionTooltipContent section={section} />}>
              <Box sx={{
                px: 2, py: 0.75, cursor: 'default', '&:hover': { bgcolor: 'action.hover' },
                ...(section.is_work_plan && { borderLeft: '2px solid', borderColor: 'secondary.main' }),
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ flexShrink: 0 }}>
                    {section.section_key}.
                  </Typography>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {section.title} 
                  </Typography>
                  {section.is_work_plan && (
                    // <FolderSpecialIcon sx={{ fontSize: 14, color: 'secondary.main', flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>Work Plan YES BEE </Typography>
                  )}
                </Box>
                {section.current_content?.body && (
                  <Typography variant="body2" color="text.disabled" noWrap display="block" sx={{ fontSize: '0.9rem' }}>
                    {markdownToPlainText(section.current_content.body).replace(/\s*\n+\s*/g, ' ').slice(0, 70)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))
        )}

        {/* ── Work Packages ── */}
        {tab === 1 && (filteredWps.length === 0
          ? <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>No work packages found.</Typography>
          : filteredWps.map(wp => (
            <Tooltip key={wp.id} placement="left" arrow componentsProps={TOOLTIP_SX}
              title={<WpTooltipContent wp={wp} tasks={tasks} />}>
              <Box sx={{ px: 2, py: 0.75, cursor: 'default', '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Chip label={`WP${wp.number}`} size="small" variant="outlined"
                    sx={{ height: 18, fontSize: '0.9rem', flexShrink: 0 }} />
                  <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {wp.title}
                  </Typography>
                </Box>
                {(tasks.filter(t => t.work_package === wp.id).length > 0 || wp.total_person_months != null) && (
                  <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: '0.9rem' }}>
                    {[
                      tasks.filter(t => t.work_package === wp.id).length && `${tasks.filter(t => t.work_package === wp.id).length} tasks`,
                      wp.total_person_months != null && `${wp.total_person_months} PM`,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))
        )}

        {/* ── Dissemination ── */}
        {tab === 2 && dissemLoading && (
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={13} />
            <Typography variant="caption" color="text.secondary">Loading…</Typography>
          </Box>
        )}
        {tab === 2 && !dissemLoading && (filteredDissem.length === 0
          ? <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>No activities found.</Typography>
          : filteredDissem.map(act => (
            <Tooltip key={act.id} placement="left" arrow componentsProps={TOOLTIP_SX}
              title={
                <Box sx={{ maxWidth: 300, p: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                    {act.title || act.category_display}
                  </Typography>
                  {act.category_display && act.title && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.65, mb: 0.5 }}>{act.category_display}</Typography>
                  )}
                  {act.description && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.85, mb: 0.5 }}>
                      {act.description.length > 240 ? act.description.slice(0, 240) + '…' : act.description}
                    </Typography>
                  )}
                  {(act.target_audience || act.event_title) && (
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      {[act.target_audience_display || act.target_audience, act.event_title].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              }>
              <Box sx={{ px: 2, py: 0.75, cursor: 'default', '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Chip label={act.category_display || 'Activity'} size="small" variant="outlined"
                    sx={{ height: 18, fontSize: '0.9rem', flexShrink: 0, maxWidth: 90 }} />
                  <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {act.title || act.category_display}
                  </Typography>
                </Box>
                {act.description && (
                  <Typography variant="caption" color="text.disabled" noWrap display="block" sx={{ fontSize: '0.9rem' }}>
                    {act.description.slice(0, 70)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))
        )}

        {/* ── Member Resources ── */}
        {tab === 3 && (filteredResources.length === 0
          ? <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>
              {memberResources === null ? 'Open the Dissemination tab first to load resources.' : 'No selected resources found.'}
            </Typography>
          : filteredResources.map((r, idx) => (
            <Tooltip key={r.id || idx} placement="left" arrow componentsProps={TOOLTIP_SX}
              title={
                <Box sx={{ maxWidth: 300, p: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.25 }}>
                    {r._label}
                  </Typography>
                  {r._type === 'Publication' && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.75, mb: 0.25 }}>
                      {[r.journal, r.year].filter(Boolean).join(' · ')}
                      {r.citation_count > 0 && ` · ${r.citation_count} citations`}
                    </Typography>
                  )}
                  {r._type === 'Patent' && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.75, mb: 0.25 }}>
                      {[r.patent_number, r.status].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  {r._type === 'Project' && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.75, mb: 0.25 }}>
                      {[r.role, r.funding_agency].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  {r._type === 'Network' && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.75, mb: 0.25 }}>
                      {[r.domain, r.role].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  {r._member_name && (
                    <Typography variant="caption" sx={{ opacity: 0.55 }}>{r._member_name}</Typography>
                  )}
                </Box>
              }>
              <Box sx={{ px: 2, py: 0.75, cursor: 'default', '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Chip label={r._type} size="small" variant="outlined"
                    sx={{ height: 18, fontSize: '0.9rem', flexShrink: 0 }} />
                  <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {r._label}
                  </Typography>
                </Box>
                {r._member_name && (
                  <Typography variant="caption" color="primary.main" noWrap display="block" sx={{ fontSize: '0.9rem' }}>
                    {r._member_name}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))
        )}

        {/* ── Advisory Board ── */}
        {tab === 4 && (filteredAB.length === 0
          ? <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>No advisory board members.</Typography>
          : filteredAB.map((m, idx) => (
            <Tooltip key={m.id || idx} placement="left" arrow componentsProps={TOOLTIP_SX}
              title={
                <Box sx={{ maxWidth: 280, p: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.25 }}>
                    {m.first_name} {m.last_name}
                  </Typography>
                  {m.domain && <Typography variant="caption" display="block" sx={{ opacity: 0.75 }}>{m.domain}</Typography>}
                  {m.email && <Typography variant="caption" display="block" sx={{ opacity: 0.65 }}>{m.email}</Typography>}
                  {m.organization && <Typography variant="caption" display="block" sx={{ opacity: 0.65 }}>{m.organization}</Typography>}
                  {m.description && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.5 }}>
                      {m.description.length > 200 ? m.description.slice(0, 200) + '…' : m.description}
                    </Typography>
                  )}
                </Box>
              }>
              <Box sx={{ px: 2, py: 0.75, cursor: 'default', '&:hover': { bgcolor: 'action.hover' } }}>
                <Typography variant="caption" fontWeight={600} display="block" noWrap>
                  {m.first_name} {m.last_name}
                </Typography>
                <Typography variant="caption" color="text.disabled" noWrap display="block" sx={{ fontSize: '0.9rem' }}>
                  {[m.domain, m.organization].filter(Boolean).join(' · ') || m.email || ''}
                </Typography>
              </Box>
            </Tooltip>
          ))
        )}

        {/* ── Budget per Organisation ── */}
        {tab === 5 && (filteredBudget.length === 0
          ? <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1.5, display: 'block' }}>No budget data.</Typography>
          : filteredBudget.map(member => (
            <Tooltip key={member.id} placement="left" arrow componentsProps={TOOLTIP_SX}
              title={
                <Box sx={{ maxWidth: 300, p: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                    {member.name || member.short_name}
                  </Typography>
                  {member.country && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.65, mb: 0.25 }}>{member.country}</Typography>
                  )}
                  {member.org_type && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.65, mb: 0.5 }}>{member.org_type}</Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" display="block" sx={{ opacity: 0.5 }}>Person-Months</Typography>
                      <Typography variant="caption" fontWeight={600}>{member.total_person_months ?? '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block" sx={{ opacity: 0.5 }}>Budget</Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {member.total_budget != null ? `€${Number(member.total_budget).toLocaleString()}` : '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }>
              <Box sx={{ px: 2, py: 0.75, cursor: 'default', '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {member.number != null && (
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ flexShrink: 0 }}>
                      P{String(member.number).padStart(2, '0')}
                    </Typography>
                  )}
                  <Typography variant="caption" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {member.short_name || member.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {member.total_person_months != null && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.9rem' }}>
                      {member.total_person_months} PM
                    </Typography>
                  )}
                  {member.total_budget != null && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.9rem' }}>
                      €{Number(member.total_budget).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Tooltip>
          ))
        )}

      </Stack>
    </Paper>
  );
}

/* ===== Restructure Headings Card ===== */

function cloneSectionTree(sections) {
  return (sections || []).map(s => ({
    id: s.id,
    title: s.title,
    section_key: s.section_key,
    subsections: cloneSectionTree(s.subsections),
  }));
}

function findTreeNode(tree, id, parent = null) {
  for (let i = 0; i < tree.length; i++) {
    if (tree[i].id === id) return { node: tree[i], parent, siblings: tree, index: i };
    const found = findTreeNode(tree[i].subsections, id, tree[i]);
    if (found) return found;
  }
  return null;
}

function nodeDepth(tree, id, depth = 0) {
  for (const n of tree) {
    if (n.id === id) return depth;
    const d = nodeDepth(n.subsections, id, depth + 1);
    if (d !== null) return d;
  }
  return null;
}

function maxSubtreeDepth(node) {
  if (!node.subsections || node.subsections.length === 0) return 0;
  return 1 + Math.max(...node.subsections.map(maxSubtreeDepth));
}

// Sections render at most 3 levels deep (top, sub, sub2) elsewhere in this
// page, so re-nesting is capped at the same depth.
const MAX_SECTION_DEPTH = 2;

function applyMove(tree, id, dir) {
  const next = cloneSectionTree(tree);
  const found = findTreeNode(next, id);
  if (!found) return next;
  const { siblings, index } = found;
  const swapIndex = dir === 'up' ? index - 1 : index + 1;
  if (swapIndex < 0 || swapIndex >= siblings.length) return next;
  [siblings[index], siblings[swapIndex]] = [siblings[swapIndex], siblings[index]];
  return next;
}

function applyIndent(tree, id) {
  const next = cloneSectionTree(tree);
  const found = findTreeNode(next, id);
  if (!found || found.index === 0) return next;
  const currentDepth = nodeDepth(next, id);
  if (currentDepth + 1 + maxSubtreeDepth(found.node) > MAX_SECTION_DEPTH) return next;
  const prev = found.siblings[found.index - 1];
  found.siblings.splice(found.index, 1);
  prev.subsections = prev.subsections || [];
  prev.subsections.push(found.node);
  return next;
}

function applyOutdent(tree, id) {
  const next = cloneSectionTree(tree);
  const found = findTreeNode(next, id);
  if (!found || !found.parent) return next;
  const parentId = found.parent.id;
  found.siblings.splice(found.index, 1);
  const parentCtx = findTreeNode(next, parentId);
  if (parentCtx) parentCtx.siblings.splice(parentCtx.index + 1, 0, found.node);
  return next;
}

// Drag-and-drop reorder/re-nest. Validated against the untouched tree first
// so an invalid drop (dropping onto a descendant, or nesting past the max
// depth) can bail out by simply returning the original tree, never a
// partially-mutated one.
function applyDrop(tree, dragId, targetId, position) {
  if (dragId === targetId) return tree;
  const dragCtx = findTreeNode(tree, dragId);
  if (!dragCtx) return tree;
  if (findTreeNode([dragCtx.node], targetId)) return tree;
  if (position === 'child') {
    const targetDepth = nodeDepth(tree, targetId);
    if (targetDepth === null || targetDepth + 1 + maxSubtreeDepth(dragCtx.node) > MAX_SECTION_DEPTH) return tree;
  }

  const next = cloneSectionTree(tree);
  const { node: dragNode, siblings: dragSiblings, index: dragIndex } = findTreeNode(next, dragId);
  dragSiblings.splice(dragIndex, 1);
  const targetCtx = findTreeNode(next, targetId);
  if (!targetCtx) return tree;
  const { node: targetNode, siblings: targetSiblings, index: targetIndex } = targetCtx;
  if (position === 'before') {
    targetSiblings.splice(targetIndex, 0, dragNode);
  } else if (position === 'after') {
    targetSiblings.splice(targetIndex + 1, 0, dragNode);
  } else {
    targetNode.subsections = targetNode.subsections || [];
    targetNode.subsections.push(dragNode);
  }
  return next;
}

// Recompute position + section_key ("1", "1.1", "1.2", "2", ...) for every
// node from its place in the tree, so headings stay numbered in reading order
// after a reorder/re-nest — this is also what the Preview tab's table
// numbering keys off of.
function computeTreeUpdates(tree, parentId = null, parentKey = '') {
  const updates = [];
  tree.forEach((node, idx) => {
    const position = idx + 1;
    const section_key = parentKey ? `${parentKey}.${position}` : String(position);
    updates.push({ id: node.id, parent: parentId, position, section_key });
    updates.push(...computeTreeUpdates(node.subsections, node.id, section_key));
  });
  return updates;
}

function RestructureNode({
  node, depth, isFirst, isLast, onMove, onIndent, onOutdent, numbersById,
  dropIndicator, onDragStart, onDragOver, onDrop, onDragEnd,
  selectedIds, onToggle,
}) {
  const canIndent = !isFirst && (depth + 1 + maxSubtreeDepth(node) <= MAX_SECTION_DEPTH);
  const rowRef = useRef(null);
  const dropPos = dropIndicator?.id === node.id ? dropIndicator.position : null;
  const isSelected = selectedIds?.has(node.id) ?? false;

  const computePosition = (e) => {
    if (!rowRef.current) return 'after';
    const { top, height } = rowRef.current.getBoundingClientRect();
    const y = e.clientY - top;
    if (y < height * 0.3) return 'before';
    if (y > height * 0.7) return 'after';
    return 'child';
  };

  const handleDragStart = (e) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    onDragStart(node.id);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    onDragOver(node.id, computePosition(e));
  };
  const handleDropEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(node.id);
  };

  return (
    <Box>
      <Stack
        ref={rowRef}
        direction="row"
        alignItems="center"
        spacing={0.25}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDropEvent}
        onDragEnd={onDragEnd}
        sx={{
          pl: depth * 2, py: 0.15, borderRadius: 0.5, mr: 3,
          ...(isSelected && { bgcolor: 'error.50', '& .MuiTypography-root': { color: 'error.main' } }),
          ...(dropPos === 'before' && { borderTop: '2px solid', borderTopColor: 'primary.main' }),
          ...(dropPos === 'after' && { borderBottom: '2px solid', borderBottomColor: 'primary.main' }),
          ...(dropPos === 'child' && { bgcolor: 'action.hover', outline: '1px solid', outlineColor: 'primary.main' }),
        }}
      >
        <Checkbox
          size="small"
          checked={isSelected}
          onChange={() => onToggle?.(node.id)}
          onClick={e => e.stopPropagation()}
          color="error"
          sx={{ p: 0.25, mr: 0.25, flexShrink: 0 }}
        />
        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'grab', flexShrink: 0 }} />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.75 }}>
            {numbersById.get(node.id)}
          </Typography>
          {node.title}
        </Typography>
        <Tooltip title="Outdent">
          <span>
            <IconButton size="small" disabled={depth === 0} onClick={() => onOutdent(node.id)}>
              <KeyboardArrowLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Indent (nest under previous heading)">
          <span>
            <IconButton size="small" disabled={!canIndent} onClick={() => onIndent(node.id)}>
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move up">
          <span>
            <IconButton size="small" disabled={isFirst} onClick={() => onMove(node.id, 'up')}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton size="small" disabled={isLast} onClick={() => onMove(node.id, 'down')}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {node.subsections?.length > 0 && (
        <Box>
          {node.subsections.map((child, idx) => (
            <RestructureNode
              key={child.id}
              node={child}
              depth={depth + 1}
              isFirst={idx === 0}
              isLast={idx === node.subsections.length - 1}
              onMove={onMove}
              onIndent={onIndent}
              onOutdent={onOutdent}
              numbersById={numbersById}
              dropIndicator={dropIndicator}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              selectedIds={selectedIds}
              onToggle={onToggle}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// Collect IDs of all descendants of a node (not the node itself)
function collectDescendantIds(node) {
  const ids = [];
  (node.subsections || []).forEach(child => {
    ids.push(child.id);
    ids.push(...collectDescendantIds(child));
  });
  return ids;
}

function RestructureHeadingsDialog({ open, onClose, sections, onSaved }) {
  const [tree, setTree] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Drag-and-drop state — refs avoid stale closures in the drag event handlers
  const dragIdRef = useRef(null);
  const dropIndicatorRef = useRef(null);
  const [dropIndicator, setDropIndicator] = useState(null);

  useEffect(() => {
    if (open) {
      setTree(cloneSectionTree(sections));
      setError(null);
      setSelectedIds(new Set());
      dragIdRef.current = null;
      dropIndicatorRef.current = null;
      setDropIndicator(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    // Determine which selected IDs are root-level (not a descendant of another selected node)
    // so we don't double-call the API when a parent cascades its children.
    const allDescendantsOfSelected = new Set();
    selectedIds.forEach(id => {
      const ctx = findTreeNode(tree, id);
      if (ctx) collectDescendantIds(ctx.node).forEach(did => allDescendantsOfSelected.add(did));
    });
    const rootSelected = [...selectedIds].filter(id => !allDescendantsOfSelected.has(id));
    const totalCount = selectedIds.size;

    if (!window.confirm(
      `Delete ${totalCount} section${totalCount > 1 ? 's' : ''} and all their content? This cannot be undone.`
    )) return;

    setDeleting(true);
    setError(null);
    try {
      await Promise.all(rootSelected.map(id => deleteSection(id)));
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete some sections.');
    } finally {
      setDeleting(false);
    }
  };

  const move = (id, dir) => setTree(prev => applyMove(prev, id, dir));
  const indent = (id) => setTree(prev => applyIndent(prev, id));
  const outdent = (id) => setTree(prev => applyOutdent(prev, id));

  const handleDragStart = useCallback((id) => {
    dragIdRef.current = id;
  }, []);

  const handleDragOver = useCallback((id, position) => {
    if (id === dragIdRef.current) return;
    const curr = dropIndicatorRef.current;
    if (curr?.id === id && curr?.position === position) return;
    const next = { id, position };
    dropIndicatorRef.current = next;
    setDropIndicator(next);
  }, []);

  const handleDrop = useCallback((targetId) => {
    const dragId = dragIdRef.current;
    const indicator = dropIndicatorRef.current;
    if (dragId && indicator && dragId !== targetId) {
      setTree(prev => applyDrop(prev, dragId, targetId, indicator.position));
    }
    dragIdRef.current = null;
    dropIndicatorRef.current = null;
    setDropIndicator(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    dragIdRef.current = null;
    dropIndicatorRef.current = null;
    setDropIndicator(null);
  }, []);

  const handleSave = async () => {
    const updates = computeTreeUpdates(tree);
    setSaving(true);
    setError(null);
    try {
      await axiosService.post('/proposal/sections/reorder/', { updates });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to save the new order.');
    } finally {
      setSaving(false);
    }
  };

  const numbersById = new Map(computeTreeUpdates(tree).map(u => [u.id, u.section_key]));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth sx={{ zIndex: 1600 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        <AccountTreeIcon fontSize="small" color="action" />
        Edit Section Order 
      </DialogTitle>
      <DialogContent dividers sx={{ py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Drag rows or use the arrows to reorder or nest section headings. Tick checkboxes to select sections for deletion. Saving renumbers headings (e.g. 2, 2.1, 2.2) to match the new order.
        </Typography>

        {/* Delete-selected toolbar — only visible when at least one section is checked */}
        {selectedIds.size > 0 && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{
            mb: 1.5, px: 1.5, py: 0.75,
            bgcolor: 'error.50', borderRadius: 1,
            border: '1px solid', borderColor: 'error.light',
          }}>
            <Typography variant="body2" color="error.main" sx={{ flex: 1 }}>
              {selectedIds.size} section{selectedIds.size > 1 ? 's' : ''} selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
              disabled={deleting || saving}
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </Button>
            <Button
              size="small"
              color="error"
              disabled={deleting}
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {tree.length === 0 ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            No sections yet.
          </Typography>
        ) : (
          <Stack spacing={0} divider={<Divider />} sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {tree.map((node, idx) => (
              <RestructureNode
                key={node.id}
                node={node}
                depth={0}
                isFirst={idx === 0}
                isLast={idx === tree.length - 1}
                onMove={move}
                onIndent={indent}
                onOutdent={outdent}
                numbersById={numbersById}
                dropIndicator={dropIndicator}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                selectedIds={selectedIds}
                onToggle={toggleSelect}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving || deleting}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || deleting || tree.length === 0}>
          {saving ? <CircularProgress size={16} /> : 'Save Order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


export default ProposalWriter;